// Source language of the codebase: English.
// Each user sees the UI in their own preferred_lang (he / ur / tl / en).
// Translations are additive overlays on top of the English source strings.
//
// Usage: t('Home') → 'Home' (en) | 'הבית' (he) | 'گھر' (ur) | 'Bahay' (tl)
//
// Platform-neutral port of apps/web/src/i18n.ts (ADR-0001 §12). Storage and
// the RTL side effect are injected, because a phone has no localStorage and
// no <html dir> to flip (RN wires RTL through I18nManager instead).
// Since 2026-08-06 this is the ONLY translation table: apps/web imports this
// package through a thin adapter (apps/web/src/i18n.ts) and keeps no copy.

import { OVERLAYS } from './langs/index';

export type Lang =
  | 'en'
  | 'he'
  | 'ur'
  | 'tl'
  | 'ar'
  | 'hi'
  | 'ml'
  | 'bn'
  | 'ta'
  | 'ne'
  | 'si'
  | 'pa'
  | 'fa'
  | 'ru';

/** Every code the app can run in locally.
 *
 *  The note that used to sit here — that the server accepts only en/he/ur/tl
 *  for `preferred_lang` — is no longer true. Migration 0053 widened the
 *  `lang_code` enum to the 53 codes in packages/schemas/src/i18n-supported.json
 *  and `LangCode` validates every one of them, so the server will both accept
 *  and return codes that are not in this list at all.
 *
 *  What still constrains the app is READY_LANGS below: a code with no
 *  dictionary renders as English whatever the server says about it. Pickers
 *  draw from there, never from here. */
export const LANG_CODES: readonly Lang[] = [
  'en',
  'he',
  'ur',
  'tl',
  'ar',
  'hi',
  'ml',
  'bn',
  'ta',
  'ne',
  'si',
  'pa',
  'fa',
  'ru',
];

/**
 * The languages a user can actually LIVE in today: full dictionary coverage,
 * offered by both pickers, accepted by the server's preferred_lang enum.
 * The ten overlay files under ./langs are still empty scaffolding — a code
 * in LANG_CODES but not here renders 100% English, so pickers and boot-time
 * language selection must draw from this list, never from LANG_CODES.
 */
export const READY_LANGS = ['en', 'he', 'ur', 'tl'] as const satisfies readonly Lang[];
export type ReadyLang = (typeof READY_LANGS)[number];

export function isReadyLang(code: string): code is ReadyLang {
  return (READY_LANGS as readonly string[]).includes(code);
}

/**
 * The safe way to adopt a language code that arrived from OUTSIDE the picker
 * — the server's preferred_lang, a deep link, storage written by a newer
 * build. An unknown or not-yet-ready code becomes English instead of being
 * installed verbatim and silently rejected on the next boot.
 */
export function coerceLang(code: string | null | undefined): Lang {
  return code && isReadyLang(code) ? code : 'en';
}

/**
 * The storage the language preference lives in. Async-capable because on
 * React Native this is AsyncStorage; the web passes localStorage. Hot reads
 * (`getLang`, and therefore every `t()` call at render time) are synchronous
 * against an in-memory cache — see `initI18n`.
 */
export interface I18nStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

export interface I18nConfig {
  storage: I18nStorage;
  /**
   * Applied whenever the active language changes (and once from
   * `bootstrapLang`). The web version wrote `document.documentElement.lang/
   * dir`; here each platform wires its own RTL — on RN this is
   * `I18nManager.forceRTL(isRtl(lang))` plus a reload (ADR-0001 §12).
   */
  onLangChange?(lang: Lang, dir: 'rtl' | 'ltr'): void;
}

const LANG_KEY = 'fm.lang';

// The in-memory cache every synchronous getter reads. 'en' until initI18n
// hydrates the stored preference — which is also the correct answer before
// hydration, because English is the default anyway (see getLang).
let currentLang: Lang = 'en';
let config: I18nConfig | null = null;

/**
 * Boot sequence, before the first render: `await initI18n({ storage })`.
 * Loads the stored `fm.lang` into memory so `getLang()`/`t()` can stay
 * synchronous on every render afterwards. Without it everything reads as
 * English — the same default the web falls back to.
 */
export async function initI18n(c: I18nConfig): Promise<void> {
  config = c;
  const raw = await c.storage.getItem(LANG_KEY);
  if (raw && (LANG_CODES as readonly string[]).includes(raw)) currentLang = raw as Lang;
}

export function getLang(): Lang {
  // English is the source language. We only switch when the user (or their
  // server-side preferred_lang on signin) explicitly picks another lang.
  // No device-language sniffing — a Hebrew-locale phone still gets English
  // by default, matching the architectural intent.
  return currentLang;
}

export function setLang(l: Lang): void | Promise<void> {
  currentLang = l;
  // Fire-and-forget write-through; the returned promise is there for tests
  // and for callers that genuinely need to know the write landed. Everyone
  // else ignores it — the in-memory cache is already updated.
  const written = config?.storage.setItem(LANG_KEY, l);
  config?.onLangChange?.(l, isRtl(l) ? 'rtl' : 'ltr');
  // No event bus needed — callers are expected to re-render or refresh; in
  // practice the lang is set during signin which navigates immediately after.
  return written;
}

export function isRtl(l: Lang = getLang()): boolean {
  return l === 'he' || l === 'ur' || l === 'ar' || l === 'fa';
}

// Translations: source-string → per-lang override.
// English source strings are the keys. Missing translations fall back to the
// source string (English).
const T: Record<string, Partial<Record<Lang, string>>> = {
  // Vault and Storage screens (apps/web/src/owner/Vault.tsx,
  // Storage.tsx) — was English-only for a Hebrew/Urdu/Tagalog reader
  // (docs/workstreams/WS-0-CONTRACTS/INTEGRATION.md's WS-5 gap).
  Documents: { he: 'מסמכים', ur: 'دستاویزات', tl: 'Mga dokumento' },
  'Task photos': { he: 'תמונות משימה', ur: 'ٹاسک کی تصاویر', tl: 'Larawan ng gawain' },
  'Proof photos': { he: 'תמונות אישור', ur: 'ثبوت کی تصاویر', tl: 'Larawan ng patunay' },
  'Voice notes': { he: 'הודעות קול', ur: 'صوتی نوٹس', tl: 'Mga voice note' },
  Largest: { he: 'הגדולים ביותר', ur: 'سب سے بڑا', tl: 'Pinakamalaki' },
  Oldest: { he: 'הישנים ביותר', ur: 'سب سے پرانا', tl: 'Pinakaluma' },
  'Not attached to anything': {
    he: 'לא מצורף לשום דבר',
    ur: 'کسی چیز سے منسلک نہیں',
    tl: 'Walang kalakip',
  },
  'Exact duplicates': { he: 'כפילויות מדויקות', ur: 'عین نقول', tl: 'Eksaktong duplicate' },
  'Older versions': { he: 'גרסאות ישנות', ur: 'پرانے ورژن', tl: 'Mas lumang bersyon' },
  Trash: { he: 'אשפה', ur: 'ٹریش', tl: 'Basura' },
  'Waiting for the file': { he: 'ממתין לקובץ', ur: 'فائل کا انتظار ہے', tl: 'Hinihintay ang file' },
  'This file did not pass our checks and was not kept.': {
    he: 'הקובץ הזה לא עבר את הבדיקות שלנו ולא נשמר.',
    ur: 'یہ فائل ہمارے چیکس میں ناکام رہی اور محفوظ نہیں کی گئی۔',
    tl: 'Hindi pumasa ang file na ito sa aming pagsusuri kaya hindi ito na-save.',
  },
  'The original is fine. A preview could not be made for it.': {
    he: 'המקור תקין. לא ניתן היה ליצור תצוגה מקדימה.',
    ur: 'اصل فائل ٹھیک ہے۔ اس کا پیش نظارہ نہیں بن سکا۔',
    tl: 'Maayos ang orihinal. Hindi magawa ang preview nito.',
  },
  'Being removed': { he: 'בתהליך הסרה', ur: 'ہٹایا جا رہا ہے', tl: 'Inaalis' },
  Preparing: { he: 'בהכנה', ur: 'تیار کیا جا رہا ہے', tl: 'Inihahanda' },
  'Being checked': { he: 'בבדיקה', ur: 'چیک کیا جا رہا ہے', tl: 'Sinusuri' },
  'Every file is checked before anyone in the household can open it.': {
    he: 'כל קובץ נבדק לפני שמישהו בבית יכול לפתוח אותו.',
    ur: 'ہر فائل کو گھر میں کوئی بھی کھولنے سے پہلے چیک کیا جاتا ہے۔',
    tl: 'Sinusuri ang bawat file bago ito mabuksan ng sinuman sa sambahayan.',
  },
  'Trash keeps using your space until you empty it.': {
    he: 'האשפה ממשיכה לתפוס מקום עד שתרוקן אותה.',
    ur: 'ٹریش خالی کرنے تک آپ کی جگہ استعمال کرتا رہتا ہے۔',
    tl: 'Patuloy na gumagamit ng espasyo ang basura hanggang sa i-empty mo ito.',
  },
  'Less than 5% of your space is left. This upload will still go through.': {
    he: 'נותרו פחות מ-5% מהמקום שלך. ההעלאה הזו עדיין תתבצע.',
    ur: 'آپ کی جگہ کا 5% سے کم باقی ہے۔ یہ اپ لوڈ پھر بھی مکمل ہوگا۔',
    tl: 'Mas mababa sa 5% ng espasyo mo ang natitira. Magpapatuloy pa rin ang pag-upload na ito.',
  },
  'We are preparing your export. You will get a notification when it is ready.': {
    he: 'אנחנו מכינים את הייצוא שלך. תקבל/י התראה כשהוא מוכן.',
    ur: 'ہم آپ کی ایکسپورٹ تیار کر رہے ہیں۔ تیار ہونے پر آپ کو اطلاع ملے گی۔',
    tl: 'Inihahanda namin ang export mo. Makakatanggap ka ng abiso kapag handa na ito.',
  },
  'Storage & Media': { he: 'אחסון ומדיה', ur: 'اسٹوریج اور میڈیا', tl: 'Storage at Media' },
  'Export everything': { he: 'ייצוא הכול', ur: 'سب کچھ ایکسپورٹ کریں', tl: 'I-export lahat' },
  '{{used}} of {{total}} used': {
    he: '{{used}} מתוך {{total}} בשימוש',
    ur: '{{used}} از {{total}} استعمال شدہ',
    tl: '{{used}} ng {{total}} ang nagamit',
  },
  'Uploaded. We are checking it before anyone can open it.': {
    he: 'הועלה. אנחנו בודקים אותו לפני שמישהו יכול לפתוח אותו.',
    ur: 'اپ لوڈ ہو گیا۔ کھلنے سے پہلے ہم اسے چیک کر رہے ہیں۔',
    tl: 'Na-upload na. Sinusuri namin ito bago ito mabuksan ninuman.',
  },
  'Trash is empty': { he: 'האשפה ריקה', ur: 'ٹریش خالی ہے', tl: 'Walang laman ang basura' },
  'Nothing here yet': {
    he: 'עדיין אין כאן כלום',
    ur: 'ابھی یہاں کچھ نہیں',
    tl: 'Wala pang laman dito',
  },
  'Anything you delete waits here before it is gone for good.': {
    he: 'כל דבר שתמחק/י ממתין כאן לפני שהוא נעלם לצמיתות.',
    ur: 'جو کچھ آپ ڈیلیٹ کریں وہ ہمیشہ کے لیے جانے سے پہلے یہاں انتظار کرتا ہے۔',
    tl: 'Anumang tinanggal mo ay naghihintay dito bago ito permanenteng mawala.',
  },
  'Add a document, and every photo, receipt and voice note joins it here.': {
    he: 'הוסיפו מסמך, וכל תמונה, קבלה והודעת קול יצטרפו אליו כאן.',
    ur: 'ایک دستاویز شامل کریں، اور ہر تصویر، رسید اور صوتی نوٹ یہاں اس کے ساتھ شامل ہو جائے گا۔',
    tl: 'Magdagdag ng dokumento, at sasali dito ang bawat larawan, resibo, at voice note.',
  },
  'Search by name': { he: 'חיפוש לפי שם', ur: 'نام سے تلاش کریں', tl: 'Maghanap ayon sa pangalan' },
  Everything: { he: 'הכול', ur: 'سب کچھ', tl: 'Lahat' },
  'Free up space': { he: 'פנו מקום', ur: 'جگہ خالی کریں', tl: 'Magpalaya ng espasyo' },
  'Added by': { he: 'נוסף על ידי', ur: 'شامل کیا از', tl: 'Idinagdag ni' },
  Anyone: { he: 'כל אחד', ur: 'کوئی بھی', tl: 'Kahit sino' },
  'Kept at the top': { he: 'נשמר למעלה', ur: 'اوپر رکھا گیا', tl: 'Nasa itaas' },
  '{{n}} versions': { he: '{{n}} גרסאות', ur: '{{n}} ورژن', tl: '{{n}} bersyon' },
  Shared: { he: 'משותף', ur: 'شیئر کیا گیا', tl: 'Ibinahagi' },
  'File removed, record kept': {
    he: 'הקובץ הוסר, הרשומה נשמרה',
    ur: 'فائل ہٹا دی گئی، ریکارڈ محفوظ ہے',
    tl: 'Tinanggal ang file, nanatili ang record',
  },
  'Gone for good on {{date}}': {
    he: 'ייעלם לצמיתות ב-{{date}}',
    ur: '{{date}} کو ہمیشہ کے لیے چلا جائے گا',
    tl: 'Permanenteng maglalaho sa {{date}}',
  },
  'Moved to Trash. It still uses your space until Trash is emptied.': {
    he: 'הועבר לאשפה. הוא עדיין תופס מקום עד שהאשפה תרוקן.',
    ur: 'ٹریش میں منتقل ہو گیا۔ ٹریش خالی ہونے تک یہ آپ کی جگہ استعمال کرتا رہے گا۔',
    tl: 'Inilipat sa basura. Ginagamit pa rin nito ang espasyo mo hanggang sa i-empty ang basura.',
  },
  Restored: { he: 'שוחזר', ur: 'بحال ہو گیا', tl: 'Naibalik' },
  'Removed. Your space comes back once the file is really gone.': {
    he: 'הוסר. המקום שלך יחזור ברגע שהקובץ ייעלם באמת.',
    ur: 'ہٹا دیا گیا۔ فائل واقعی جانے کے بعد آپ کی جگہ واپس آ جائے گی۔',
    tl: 'Tinanggal na. Babalik ang espasyo mo kapag talagang nawala na ang file.',
  },
  'Sharing stopped': {
    he: 'השיתוף הופסק',
    ur: 'شیئرنگ روک دی گئی',
    tl: 'Itinigil ang pagbabahagi',
  },
  'Attached to': { he: 'מצורף אל', ur: 'سے منسلک', tl: 'Nakalakip sa' },
  'File removed — the record is still there': {
    he: 'הקובץ הוסר — הרשומה עדיין קיימת',
    ur: 'فائل ہٹا دی گئی — ریکارڈ اب بھی موجود ہے',
    tl: 'Tinanggal ang file — nandiyan pa rin ang record',
  },
  'Live attachment': { he: 'צרופה פעילה', ur: 'فعال منسلکہ', tl: 'Aktibong kalakip' },
  'Version {{n}}': { he: 'גרסה {{n}}', ur: 'ورژن {{n}}', tl: 'Bersyon {{n}}' },
  'the original': { he: 'המקור', ur: 'اصل', tl: 'ang orihinal' },
  'Who can see this': {
    he: 'מי יכול לראות את זה',
    ur: 'یہ کون دیکھ سکتا ہے',
    tl: 'Sino ang makakakita nito',
  },
  'Stop sharing': { he: 'הפסקת שיתוף', ur: 'شیئرنگ بند کریں', tl: 'Itigil ang pagbabahagi' },
  Download: { he: 'הורדה', ur: 'ڈاؤن لوڈ', tl: 'I-download' },
  Restore: { he: 'שחזור', ur: 'بحال کریں', tl: 'Ibalik' },
  'Remove for good': { he: 'הסרה לצמיתות', ur: 'ہمیشہ کے لیے ہٹائیں', tl: 'Alisin nang tuluyan' },
  'Move to Trash': { he: 'העברה לאשפה', ur: 'ٹریش میں منتقل کریں', tl: 'Ilipat sa basura' },
  'Name and tags': { he: 'שם ותגיות', ur: 'نام اور ٹیگز', tl: 'Pangalan at tag' },
  'Somebody else changed this file while you were typing.': {
    he: 'מישהו אחר שינה את הקובץ הזה בזמן שהקלדת/ה.',
    ur: 'آپ کے ٹائپ کرتے وقت کسی اور نے اس فائل کو تبدیل کر دیا۔',
    tl: 'May ibang nagbago sa file na ito habang nagta-type ka.',
  },
  'It is now called "{{title}}". Nothing you typed has been lost.': {
    he: 'הוא נקרא כעת "{{title}}". שום דבר שהקלדת לא אבד.',
    ur: 'اب اس کا نام "{{title}}" ہے۔ آپ نے جو کچھ ٹائپ کیا وہ ضائع نہیں ہوا۔',
    tl: 'Ngayon ay tinatawag itong "{{title}}". Walang nawala sa na-type mo.',
  },
  'Keep my changes': {
    he: 'שמירת השינויים שלי',
    ur: 'میری تبدیلیاں رکھیں',
    tl: 'Panatilihin ang mga pagbabago ko',
  },
  'Use their version': {
    he: 'שימוש בגרסה שלהם',
    ur: 'ان کا ورژن استعمال کریں',
    tl: 'Gamitin ang bersyon nila',
  },
  Name: { he: 'שם', ur: 'نام', tl: 'Pangalan' },
  'A file needs a name, so the current one is kept.': {
    he: 'לקובץ צריך שם, אז השם הנוכחי נשמר.',
    ur: 'فائل کو نام کی ضرورت ہے، اس لیے موجودہ نام رکھا گیا ہے۔',
    tl: 'Kailangan ng pangalan ang file, kaya pinanatili ang kasalukuyang pangalan.',
  },
  Tags: { he: 'תגיות', ur: 'ٹیگز', tl: 'Mga tag' },
  'Separate tags with a comma': {
    he: 'הפרידו תגיות בפסיק',
    ur: 'ٹیگز کو کوما سے الگ کریں',
    tl: 'Paghiwalayin ang mga tag gamit ang kuwit',
  },
  'Keep this at the top': {
    he: 'שמירת זה למעלה',
    ur: 'اسے اوپر رکھیں',
    tl: 'Panatilihin ito sa itaas',
  },
  'Checking there is room…': {
    he: 'בודקים אם יש מקום…',
    ur: 'جگہ چیک کی جا رہی ہے…',
    tl: 'Sinusuri kung may espasyo…',
  },
  'Uploading…': { he: 'מעלה…', ur: 'اپ لوڈ ہو رہا ہے…', tl: 'Ina-upload…' },
  'The upload did not finish.': {
    he: 'ההעלאה לא הושלמה.',
    ur: 'اپ لوڈ مکمل نہیں ہوا۔',
    tl: 'Hindi natapos ang pag-upload.',
  },
  'Finishing…': { he: 'מסיים…', ur: 'مکمل ہو رہا ہے…', tl: 'Tinatapos…' },
  'Add a file': { he: 'הוספת קובץ', ur: 'فائل شامل کریں', tl: 'Magdagdag ng file' },
  'We check every file before anyone can open it.': {
    he: 'אנחנו בודקים כל קובץ לפני שמישהו יכול לפתוח אותו.',
    ur: 'کھلنے سے پہلے ہم ہر فائل کو چیک کرتے ہیں۔',
    tl: 'Sinusuri namin ang bawat file bago ito mabuksan ninuman.',
  },
  'You are offline. Uploading needs a connection.': {
    he: 'אין לך חיבור לאינטרנט. העלאה דורשת חיבור.',
    ur: 'آپ آف لائن ہیں۔ اپ لوڈ کرنے کے لیے کنکشن درکار ہے۔',
    tl: 'Offline ka. Kailangan ng koneksyon ang pag-upload.',
  },
  'Storage is full': { he: 'האחסון מלא', ur: 'اسٹوریج بھر گیا ہے', tl: 'Puno na ang storage' },
  'Almost out of space': {
    he: 'כמעט אזל המקום',
    ur: 'جگہ تقریباً ختم ہو گئی',
    tl: 'Halos naubusan na ng espasyo',
  },
  'Uploads still work. At 100% they pause until you free space or add capacity.': {
    he: 'העלאות עדיין עובדות. ב-100% הן יושהו עד שתפנו מקום או תוסיפו קיבולת.',
    ur: 'اپ لوڈز اب بھی کام کرتے ہیں۔ 100% پر وہ رک جاتے ہیں جب تک آپ جگہ خالی نہ کریں یا گنجائش نہ بڑھائیں۔',
    tl: 'Gumagana pa rin ang mga upload. Sa 100%, huminto ang mga ito hanggang sa magpalaya ka ng espasyo o magdagdag ng kapasidad.',
  },
  'Running low on space': {
    he: 'המקום הולך ואוזל',
    ur: 'جگہ کم ہوتی جا رہی ہے',
    tl: 'Paubos na ang espasyo',
  },
  'Clean up what you no longer need, or add capacity.': {
    he: 'נקו את מה שאתם כבר לא צריכים, או הוסיפו קיבולת.',
    ur: 'جو اب درکار نہیں اسے صاف کریں، یا گنجائش بڑھائیں۔',
    tl: 'Linisin ang hindi mo na kailangan, o magdagdag ng kapasidad.',
  },
  'Storage is filling up': {
    he: 'האחסון מתמלא',
    ur: 'اسٹوریج بھر رہا ہے',
    tl: 'Napupuno ang storage',
  },
  'See what is using space.': {
    he: 'ראו מה תופס מקום.',
    ur: 'دیکھیں کیا جگہ استعمال کر رہا ہے۔',
    tl: 'Tingnan kung ano ang gumagamit ng espasyo.',
  },
  'A Storage Box needs an active paid plan.': {
    he: 'קופסת אחסון דורשת תוכנית בתשלום פעילה.',
    ur: 'اسٹوریج باکس کے لیے فعال ادائیگی شدہ پلان درکار ہے۔',
    tl: 'Kailangan ng aktibong bayad na plano ang Storage Box.',
  },
  'You have reached the self-service limit. Talk to us about a larger household.': {
    he: 'הגעתם למגבלת השירות העצמי. דברו איתנו על בית גדול יותר.',
    ur: 'آپ سیلف سروس کی حد تک پہنچ گئے ہیں۔ بڑے گھرانے کے بارے میں ہم سے بات کریں۔',
    tl: 'Naabot mo na ang limitasyon ng self-service. Makipag-usap sa amin tungkol sa mas malaking sambahayan.',
  },
  'All boxes in a household share one billing cycle.': {
    he: 'כל הקופסאות בבית משתפות מחזור חיוב אחד.',
    ur: 'گھرانے کے تمام باکسز ایک بلنگ سائیکل شیئر کرتے ہیں۔',
    tl: 'Lahat ng box sa isang sambahayan ay may iisang billing cycle.',
  },
  'Only the household billing owner can buy storage.': {
    he: 'רק בעל/ת החיוב של הבית יכול/ה לרכוש אחסון.',
    ur: 'صرف گھرانے کا بلنگ مالک اسٹوریج خرید سکتا ہے۔',
    tl: 'Ang billing owner lang ng sambahayan ang puwedeng bumili ng storage.',
  },
  'Payment retrying': {
    he: 'ניסיון תשלום חוזר',
    ur: 'ادائیگی دوبارہ کوشش کی جا رہی ہے',
    tl: 'Sinusubukan ulit ang bayad',
  },
  'Recovery only': { he: 'שחזור בלבד', ur: 'صرف بحالی', tl: 'Recovery lang' },
  Ended: { he: 'הסתיים', ur: 'ختم ہو گیا', tl: 'Tapos na' },
  'Finish this purchase in the app on your phone.': {
    he: 'השלימו את הרכישה הזו באפליקציה בטלפון שלכם.',
    ur: 'یہ خریداری اپنے فون میں ایپ میں مکمل کریں۔',
    tl: 'Tapusin ang bilhing ito sa app sa telepono mo.',
  },
  'Open Storage & Media': {
    he: 'פתיחת אחסון ומדיה',
    ur: 'اسٹوریج اور میڈیا کھولیں',
    tl: 'Buksan ang Storage at Media',
  },
  'In Trash': { he: 'באשפה', ur: 'ٹریش میں', tl: 'Nasa basura' },
  'Held for uploads': {
    he: 'שמור להעלאות',
    ur: 'اپ لوڈز کے لیے روکا گیا',
    tl: 'Nakalaan para sa mga upload',
  },
  'Kept from before your plan': {
    he: 'נשמר מלפני התוכנית שלכם',
    ur: 'آپ کے پلان سے پہلے کا محفوظ شدہ',
    tl: 'Napanatili mula bago ang plano mo',
  },
  'In the Recovery Vault': { he: 'בכספת השחזור', ur: 'ریکوری والٹ میں', tl: 'Nasa Recovery Vault' },
  'System & Languages': { he: 'מערכת ושפות', ur: 'سسٹم اور زبانیں', tl: 'System at mga Wika' },
  'included, never billed': {
    he: 'כלול, לעולם לא בחיוב',
    ur: 'شامل ہے، کبھی بل نہیں کیا جاتا',
    tl: 'kasama, hindi kailanman sinisingil',
  },
  'Add capacity': { he: 'הוספת קיבולת', ur: 'گنجائش شامل کریں', tl: 'Magdagdag ng kapasidad' },
  'What you have': { he: 'מה יש לכם', ur: 'آپ کے پاس کیا ہے', tl: 'Ang mayroon ka' },
  'Your plan’s included storage appears here once your household is on a paid plan.': {
    he: 'האחסון הכלול בתוכנית שלכם יופיע כאן ברגע שהבית שלכם יעבור לתוכנית בתשלום.',
    ur: 'آپ کے پلان کی شامل اسٹوریج یہاں ظاہر ہوگی جب آپ کا گھرانہ ادائیگی شدہ پلان پر ہوگا۔',
    tl: 'Lalabas dito ang kasamang storage ng plano mo kapag naka-paid plan na ang sambahayan mo.',
  },
  'A Storage Box adds room. Nothing else.': {
    he: 'קופסת אחסון מוסיפה מקום. שום דבר אחר.',
    ur: 'اسٹوریج باکس صرف جگہ بڑھاتا ہے۔ اور کچھ نہیں۔',
    tl: 'Ang Storage Box ay nagdaragdag lang ng espasyo. Wala nang iba.',
  },
  'Buying storage is not open on this household yet.': {
    he: 'רכישת אחסון עדיין לא פתוחה עבור הבית הזה.',
    ur: 'اس گھرانے کے لیے اسٹوریج خریدنا ابھی کھلا نہیں ہے۔',
    tl: 'Hindi pa bukas ang pagbili ng storage para sa sambahayang ito.',
  },
  'Storage Box · {{size}}': {
    he: 'קופסת אחסון · {{size}}',
    ur: 'اسٹوریج باکس · {{size}}',
    tl: 'Storage Box · {{size}}',
  },
  'Included with your plan · {{size}}': {
    he: 'כלול בתוכנית שלכם · {{size}}',
    ur: 'آپ کے پلان میں شامل · {{size}}',
    tl: 'Kasama sa plano mo · {{size}}',
  },
  'Paid through {{date}}': {
    he: 'שולם עד {{date}}',
    ur: '{{date}} تک ادا شدہ',
    tl: 'Bayad hanggang {{date}}',
  },
  'Part of your plan': {
    he: 'חלק מהתוכנית שלכם',
    ur: 'آپ کے پلان کا حصہ',
    tl: 'Bahagi ng plano mo',
  },
  'Twelve months paid up front': {
    he: 'שנים עשר חודשים בתשלום מראש',
    ur: 'بارہ ماہ پیشگی ادا شدہ',
    tl: 'Labindalawang buwan na binayaran nang maaga',
  },
  'Files here are removed on {{date}}': {
    he: 'הקבצים כאן יוסרו ב-{{date}}',
    ur: 'یہاں کی فائلیں {{date}} کو ہٹا دی جائیں گی',
    tl: 'Aalisin ang mga file dito sa {{date}}',
  },
  '{{n}} days left': { he: 'נותרו {{n}} ימים', ur: '{{n}} دن باقی', tl: '{{n}} araw na lang' },
  'End this box': { he: 'סיום הקופסה הזו', ur: 'یہ باکس ختم کریں', tl: 'Tapusin ang box na ito' },
  'Billing cycle': { he: 'מחזור חיוב', ur: 'بلنگ سائیکل', tl: 'Billing cycle' },
  '12 months up front': { he: '12 חודשים מראש', ur: '12 ماہ پیشگی', tl: '12 buwan nang maaga' },
  'Your household is on one cycle for all boxes.': {
    he: 'הבית שלכם נמצא על מחזור אחד לכל הקופסאות.',
    ur: 'آپ کا گھرانہ تمام باکسز کے لیے ایک ہی سائیکل پر ہے۔',
    tl: 'Nasa iisang cycle ang sambahayan mo para sa lahat ng box.',
  },
  'Paying for twelve months up front is twelve months paid up front — not a discount.': {
    he: 'תשלום עבור שנים עשר חודשים מראש הוא שנים עשר חודשים בתשלום מראש — לא הנחה.',
    ur: 'بارہ ماہ پیشگی ادائیگی، بارہ ماہ پیشگی ادائیگی ہے — رعایت نہیں۔',
    tl: 'Ang pagbayad para sa labindalawang buwan nang maaga ay labindalawang buwan na maagang bayad — hindi diskwento.',
  },
  '+{{n}} GB': { he: '+{{n}} ג׳יגה', ur: '+{{n}} جی بی', tl: '+{{n}} GB' },
  'for 12 months, paid up front': {
    he: 'ל-12 חודשים, בתשלום מראש',
    ur: '12 ماہ کے لیے، پیشگی ادا شدہ',
    tl: 'para sa 12 buwan, binayaran nang maaga',
  },
  'Add this box': { he: 'הוספת הקופסה הזו', ur: 'یہ باکس شامل کریں', tl: 'Idagdag ang box na ito' },
  'This box will end at the end of the period you have paid for.': {
    he: 'הקופסה הזו תסתיים בסוף התקופה ששולמה.',
    ur: 'یہ باکس اس مدت کے اختتام پر ختم ہوگا جس کی آپ نے ادائیگی کی ہے۔',
    tl: 'Matatapos ang box na ito sa katapusan ng panahong binayaran mo.',
  },
  'End this Storage Box': {
    he: 'סיום קופסת האחסון הזו',
    ur: 'یہ اسٹوریج باکس ختم کریں',
    tl: 'Tapusin ang Storage Box na ito',
  },
  'Checking…': { he: 'בודק…', ur: 'چیک ہو رہا ہے…', tl: 'Sinusuri…' },
  'End at the paid-through date': {
    he: 'סיום בתאריך ששולם עד אליו',
    ur: 'ادا شدہ تاریخ پر ختم کریں',
    tl: 'Magtatapos sa petsa ng bayad',
  },
  'Your capacity stays until {{date}}.': {
    he: 'הקיבולת שלכם נשארת עד {{date}}.',
    ur: 'آپ کی گنجائش {{date}} تک برقرار رہے گی۔',
    tl: 'Mananatili ang kapasidad mo hanggang {{date}}.',
  },
  'These files are on this box': {
    he: 'הקבצים האלה נמצאים בקופסה הזו',
    ur: 'یہ فائلیں اس باکس پر ہیں',
    tl: 'Nasa box na ito ang mga file na ito',
  },
  'Everything you keep fits without this box.': {
    he: 'כל מה שאתם שומרים נכנס גם בלי הקופסה הזו.',
    ur: 'جو کچھ آپ رکھتے ہیں وہ اس باکس کے بغیر بھی سما جاتا ہے۔',
    tl: 'Kasya ang lahat ng pinapanatili mo kahit wala ang box na ito.',
  },
  'Recovery Vault': { he: 'כספת שחזור', ur: 'ریکوری والٹ', tl: 'Recovery Vault' },
  '{{size}} of files are waiting here': {
    he: '{{size}} של קבצים ממתינים כאן',
    ur: '{{size}} فائلیں یہاں انتظار کر رہی ہیں',
    tl: '{{size}} ng mga file ang naghihintay dito',
  },
  'They are removed on {{date}}.': {
    he: 'הם יוסרו ב-{{date}}.',
    ur: 'وہ {{date}} کو ہٹا دی جائیں گی۔',
    tl: 'Aalisin ang mga ito sa {{date}}.',
  },
  // Research consent — the participant-facing screen (web ResearchPanels.tsx,
  // mobile ResearchConsentCard.tsx). Was English-only until now: a consent a
  // worker cannot read is not consent (docs/legal/README.md).
  'Your choice': { he: 'הבחירה שלך', ur: 'آپ کا انتخاب', tl: 'Ang pasya mo' },
  'YOUR CHOICE': { he: 'הבחירה שלך', ur: 'آپ کا انتخاب', tl: 'PASYA MO' },
  'Optional product research': {
    he: 'מחקר מוצר — לבחירתך',
    ur: 'اختیاری پروڈکٹ ریسرچ',
    tl: 'Opsyonal na pananaliksik sa produkto',
  },
  'Help improve how work is supported': {
    he: 'עזרו לשפר איך העבודה נתמכת',
    ur: 'کام کی معاونت کو بہتر بنانے میں مدد کریں',
    tl: 'Tumulong pahusayin kung paano sinusuportahan ang trabaho',
  },
  'Famanage can record typed activity signals—such as when a task changes state or when you ask for help—to learn whether the app reduces effort.':
    {
      he: 'Famanage יכולה לתעד אותות פעילות מוקלדים — כמו שינוי מצב של משימה או בקשת עזרה — כדי ללמוד אם האפליקציה מפחיתה מאמץ.',
      ur: 'Famanage ٹائپ کی گئی سرگرمی کے اشارے ریکارڈ کر سکتا ہے — جیسے جب کسی ٹاسک کی حالت بدلے یا جب آپ مدد مانگیں — یہ جاننے کے لیے کہ آیا ایپ محنت کم کرتی ہے۔',
      tl: 'Maaaring itala ng Famanage ang mga senyales ng aktibidad na na-type — tulad ng pagbabago ng estado ng isang gawain o paghingi ng tulong — para malaman kung binabawasan ng app ang pagsisikap.',
    },
  'No message text, photos, voice, location or private household details.': {
    he: 'לא נכלל טקסט הודעות, תמונות, קול, מיקום או פרטי בית פרטיים.',
    ur: 'کوئی پیغام کی تحریر، تصاویر، آواز، مقام یا نجی گھریلو تفصیلات شامل نہیں۔',
    tl: 'Walang teksto ng mensahe, larawan, boses, lokasyon, o pribadong detalye ng sambahayan.',
  },
  'Your owner cannot choose for you or see private research feedback.': {
    he: 'המעסיק שלך לא יכול לבחור בשבילך או לראות משוב מחקר פרטי.',
    ur: 'آپ کا مالک آپ کے لیے فیصلہ نہیں کر سکتا اور نہ ہی نجی ریسرچ رائے دیکھ سکتا ہے۔',
    tl: 'Hindi maaaring pumili ang employer mo para sa iyo o makita ang pribadong puna sa pananaliksik.',
  },
  'Saying no or withdrawing will not change your work information.': {
    he: 'סירוב או ביטול ההסכמה לא ישנו את פרטי העבודה שלך.',
    ur: 'انکار کرنا یا واپس لینا آپ کی کام کی معلومات کو تبدیل نہیں کرے گا۔',
    tl: 'Ang pagtanggi o pag-withdraw ay hindi magbabago sa impormasyon ng iyong trabaho.',
  },
  'Research sharing is on': {
    he: 'שיתוף מחקר פעיל',
    ur: 'ریسرچ شیئرنگ فعال ہے',
    tl: 'Naka-on ang pagbabahagi para sa pananaliksik',
  },
  'You can stop it at any time.': {
    he: 'תוכל/י להפסיק בכל עת.',
    ur: 'آپ اسے کسی بھی وقت روک سکتے ہیں۔',
    tl: 'Maaari mo itong itigil anumang oras.',
  },
  'Stop research sharing': {
    he: 'הפסקת שיתוף מחקר',
    ur: 'ریسرچ شیئرنگ روکیں',
    tl: 'Itigil ang pagbabahagi para sa pananaliksik',
  },
  'I understand and agree': {
    he: 'הבנתי ואני מסכים/ה',
    ur: 'میں سمجھتا/سمجھتی ہوں اور اتفاق کرتا/کرتی ہوں',
    tl: 'Nauunawaan ko at sumasang-ayon ako',
  },
  // The plan picker (web Billing.tsx) — three purchasable plans, each with
  // its own checkout, replacing the single "Subscribe" button that only ever
  // sold Household.
  // Full sentence-shaped keys, not bare "Home"/"Household": those already
  // mean the nav's home screen and would silently steal this translation.
  'Home plan': { he: 'תוכנית Home', ur: 'ہوم پلان', tl: 'Home plan' },
  'Household plan': { he: 'תוכנית Household', ur: 'ہاؤس ہولڈ پلان', tl: 'Household plan' },
  'Private Household plan': {
    he: 'תוכנית Private Household',
    ur: 'پرائیویٹ ہاؤس ہولڈ پلان',
    tl: 'Private Household plan',
  },
  '{{workers}} workers · {{storage}} GB storage': {
    he: '{{workers}} עובדים · {{storage}} ג׳יגה אחסון',
    ur: '{{workers}} کارکن · {{storage}} جی بی اسٹوریج',
    tl: '{{workers}} manggagawa · {{storage}} GB na storage',
  },
  'Starting at': { he: 'החל מ־', ur: 'سے شروع', tl: 'Simula sa' },

  // Wave-3 worker-trust screens (waiting / authority / transition /
  // obligations / clarifications / context / chain). These are the screens
  // that tell someone what the system may do on her behalf; shipping them in
  // English to a Hebrew, Urdu or Tagalog reader is where trust is lost.
  'What the system may do': {
    he: 'מה המערכת רשאית לעשות',
    ur: 'سسٹم کیا کر سکتا ہے',
    tl: 'Ano ang maaaring gawin ng sistema',
  },
  'Emergency stop': { he: 'עצירת חירום', ur: 'ہنگامی روک', tl: 'Emergency stop' },
  'No active authority': {
    he: 'אין הרשאה פעילה',
    ur: 'کوئی فعال اختیار نہیں',
    tl: 'Walang aktibong awtoridad',
  },
  'Visible policies': {
    he: 'מדיניות גלויה',
    ur: 'نظر آنے والی پالیسیاں',
    tl: 'Nakikitang mga patakaran',
  },
  'The system cannot act for this role.': {
    he: 'המערכת לא יכולה לפעול עבור התפקיד הזה.',
    ur: 'سسٹم اس کردار کے لیے کام نہیں کر سکتا۔',
    tl: 'Hindi maaaring kumilos ang sistema para sa tungkuling ito.',
  },
  'Contact the owner. Revoking a policy blocks every later action immediately.': {
    he: 'פנה/י לבעל/ת הבית. ביטול מדיניות חוסם כל פעולה עתידית מיד.',
    ur: 'مالک سے رابطہ کریں۔ پالیسی منسوخ کرنے سے بعد کی ہر کارروائی فوراً رک جاتی ہے۔',
    tl: 'Kontakin ang may-ari. Ang pagbawi ng patakaran ay agad na humaharang sa lahat ng susunod na aksyon.',
  },
  'Nothing on this screen dispatches work.': {
    he: 'שום דבר במסך הזה לא שולח עבודה.',
    ur: 'اس اسکرین پر کوئی چیز کام تفویض نہیں کرتی۔',
    tl: 'Walang sa screen na ito ang nagpapadala ng trabaho.',
  },
  'Your first shift': { he: 'המשמרת הראשונה שלך', ur: 'آپ کی پہلی شفٹ', tl: 'Ang una mong shift' },
  'Your owner will open this guide when a transition starts.': {
    he: 'בעל/ת הבית יפתח/תפתח את המדריך הזה כשמתחיל מעבר.',
    ur: 'منتقلی شروع ہونے پر آپ کا مالک یہ گائیڈ کھولے گا۔',
    tl: 'Bubuksan ng may-ari mo ang gabay na ito kapag nagsimula ang paglipat.',
  },
  'The house rules and role details that matter today — already in one place.': {
    he: 'כללי הבית ופרטי התפקיד שרלוונטיים היום — כבר במקום אחד.',
    ur: 'گھر کے قواعد اور کردار کی تفصیلات جو آج اہم ہیں — پہلے سے ایک جگہ۔',
    tl: 'Ang mga panuntunan ng bahay at detalye ng tungkulin na mahalaga ngayon — nasa isang lugar na.',
  },
  'No private notes or scores from anyone who worked here before.': {
    he: 'אין הערות פרטיות או ציונים ממי שעבד/ה כאן קודם.',
    ur: 'یہاں پہلے کام کرنے والے کسی کے ذاتی نوٹ یا اسکور نہیں۔',
    tl: 'Walang pribadong tala o marka mula sa sinumang nagtrabaho dito noon.',
  },
  'Follow-through': { he: 'מעקב עד הסוף', ur: 'تکمیل', tl: 'Pagtupad' },
  'Durable promises': {
    he: 'התחייבויות שנשמרות',
    ur: 'پائیدار وعدے',
    tl: 'Matatag na mga pangako',
  },
  'Obligation queue': { he: 'תור התחייבויות', ur: 'ذمہ داریوں کی قطار', tl: 'Pila ng obligasyon' },
  'Checking durable state…': {
    he: 'בודק מצב שמור…',
    ur: 'پائیدار حالت کی جانچ…',
    tl: 'Sinusuri ang matatag na estado…',
  },
  "Queued is not completed. This view reports only the worker's acknowledged state.": {
    he: 'בתור אינו הושלם. המסך הזה מדווח רק על מה שהעובד/ת אישר/ה שקיבל/ה.',
    ur: 'قطار میں ہونا مکمل ہونا نہیں۔ یہ منظر صرف کارکن کی تسلیم شدہ حالت بتاتا ہے۔',
    tl: 'Ang nakapila ay hindi tapos. Ipinapakita lang dito ang kinilalang estado ng manggagawa.',
  },
  'Refreshes every 10 seconds and survives reconnect.': {
    he: 'מתרענן כל 10 שניות ושורד ניתוק.',
    ur: 'ہر 10 سیکنڈ بعد تازہ ہوتا ہے اور دوبارہ جڑنے پر برقرار رہتا ہے۔',
    tl: 'Nagre-refresh tuwing 10 segundo at nananatili kahit madiskonekta.',
  },
  'Needs your review': {
    he: 'צריך את הבדיקה שלך',
    ur: 'آپ کے جائزے کا منتظر',
    tl: 'Kailangan ang pagsusuri mo',
  },
  'What needs an answer—and why.': {
    he: 'מה דורש תשובה — ולמה.',
    ur: 'کس چیز کا جواب درکار ہے — اور کیوں۔',
    tl: 'Ano ang kailangang sagutin — at bakit.',
  },
  'Answer only what changes the outcome.': {
    he: 'ענה/י רק על מה שמשנה את התוצאה.',
    ur: 'صرف اس کا جواب دیں جو نتیجہ بدلے۔',
    tl: 'Sagutin lang ang nagbabago sa kinalabasan.',
  },
  'One detail': { he: 'פרט אחד', ur: 'ایک تفصیل', tl: 'Isang detalye' },
  'Does this work for you?': {
    he: 'זה מתאים לך?',
    ur: 'کیا یہ آپ کے لیے مناسب ہے؟',
    tl: 'Okay ba ito sa iyo?',
  },
  'Voice or text accepted · defaults to use once': {
    he: 'קול או טקסט · ברירת מחדל: שימוש חד-פעמי',
    ur: 'آواز یا متن · طے شدہ: ایک بار استعمال',
    tl: 'Boses o teksto · default: isang beses gamitin',
  },
  'Gap decisions': { he: 'החלטות על פערים', ur: 'خلا کے فیصلے', tl: 'Mga desisyon sa puwang' },
  'Gap policy': { he: 'מדיניות פערים', ur: 'خلا کی پالیسی', tl: 'Patakaran sa puwang' },
  'Decisions, not noise': {
    he: 'החלטות, לא רעש',
    ur: 'فیصلے، شور نہیں',
    tl: 'Mga desisyon, hindi ingay',
  },
  'The model proposes gaps. Deterministic policy owns risk, authority and blocking.': {
    he: 'המודל מציע פערים. המדיניות הקבועה מחליטה על סיכון, סמכות וחסימה.',
    ur: 'ماڈل خلا تجویز کرتا ہے۔ طے شدہ پالیسی خطرہ، اختیار اور روک تھام طے کرتی ہے۔',
    tl: 'Nagmumungkahi ng puwang ang modelo. Ang tiyak na patakaran ang may hawak ng panganib, awtoridad at paghadlang.',
  },
  'What happened, what it blocks and the smallest safe decision.': {
    he: 'מה קרה, מה זה חוסם, ומה ההחלטה הבטוחה הקטנה ביותר.',
    ur: 'کیا ہوا، کیا رکا ہے اور سب سے چھوٹا محفوظ فیصلہ۔',
    tl: 'Ano ang nangyari, ano ang hinaharangan nito at ang pinakamaliit na ligtas na desisyon.',
  },
  'Nothing to prepare yet': {
    he: 'אין עדיין מה להכין',
    ur: 'ابھی تیار کرنے کو کچھ نہیں',
    tl: 'Wala pang ihahanda',
  },
  'Context review': { he: 'סקירת הקשר', ur: 'سیاق و سباق کا جائزہ', tl: 'Pagsusuri ng konteksto' },
  'Context trace': { he: 'מסלול ההקשר', ur: 'سیاق کا سراغ', tl: 'Bakas ng konteksto' },
  'Why this context': { he: 'למה ההקשר הזה', ur: 'یہ سیاق کیوں', tl: 'Bakit ang kontekstong ito' },
  'Household knowledge only': {
    he: 'ידע של משק הבית בלבד',
    ur: 'صرف گھر کی معلومات',
    tl: 'Kaalaman ng sambahayan lamang',
  },
  'Keep useful context. Remove it in one tap.': {
    he: 'שמור/שמרי הקשר מועיל. אפשר להסיר בלחיצה אחת.',
    ur: 'مفید سیاق رکھیں۔ ایک ٹیپ میں ہٹائیں۔',
    tl: 'Panatilihin ang kapaki-pakinabang na konteksto. Alisin sa isang tap.',
  },
  'Future effect: candidate only': {
    he: 'השפעה עתידית: מועמד בלבד',
    ur: 'مستقبل کا اثر: صرف امیدوار',
    tl: 'Epekto sa hinaharap: kandidato lamang',
  },
  'Visibility was checked before ranking.': {
    he: 'הנראות נבדקה לפני הדירוג.',
    ur: 'درجہ بندی سے پہلے نمائش جانچی گئی۔',
    tl: 'Sinuri ang visibility bago ang ranggo.',
  },
  'What matters today': { he: 'מה חשוב היום', ur: 'آج کیا اہم ہے', tl: 'Ano ang mahalaga ngayon' },
  'Operational chain': { he: 'שרשרת תפעולית', ur: 'عملی سلسلہ', tl: 'Kadena ng operasyon' },
  'Proposed chain': { he: 'שרשרת מוצעת', ur: 'تجویز کردہ سلسلہ', tl: 'Iminungkahing kadena' },
  'Chain preview': {
    he: 'תצוגה מקדימה של השרשרת',
    ur: 'سلسلے کا پیش منظر',
    tl: 'Preview ng kadena',
  },
  'The original proposal stays immutable.': {
    he: 'ההצעה המקורית נשארת ללא שינוי.',
    ur: 'اصل تجویز ناقابل تبدیل رہتی ہے۔',
    tl: 'Nananatiling hindi mababago ang orihinal na panukala.',
  },
  'What must be true': {
    he: 'מה חייב להתקיים',
    ur: 'کیا سچ ہونا چاہیے',
    tl: 'Ano ang dapat totoo',
  },
  'Outcome preview': {
    he: 'תצוגה מקדימה של התוצאה',
    ur: 'نتیجے کا پیش منظر',
    tl: 'Preview ng kinalabasan',
  },
  'Outcome review has not started.': {
    he: 'סקירת התוצאה טרם התחילה.',
    ur: 'نتیجے کا جائزہ شروع نہیں ہوا۔',
    tl: 'Hindi pa nagsisimula ang pagsusuri ng kinalabasan.',
  },
  'Correct the outcome': {
    he: 'תקן/י את התוצאה',
    ur: 'نتیجہ درست کریں',
    tl: 'Itama ang kinalabasan',
  },
  'Evidence is not the verdict': {
    he: 'ראיה אינה פסק דין',
    ur: 'ثبوت فیصلہ نہیں ہے',
    tl: 'Ang ebidensiya ay hindi ang hatol',
  },
  'Next action': { he: 'הפעולה הבאה', ur: 'اگلا قدم', tl: 'Susunod na hakbang' },
  // Brand / tagline
  'family · manage': { he: 'משפחה · ניהול', ur: 'خاندان · انتظام', tl: 'pamilya · pangasiwaan' },
  'The operating system for your household.': {
    he: 'המערכת ההפעלה של הבית.',
    ur: 'آپ کے گھر کا آپریٹنگ سسٹم۔',
    tl: 'Ang operating system ng inyong tahanan.',
  },
  'Sign in as owner': {
    he: 'כניסה כבעלים',
    ur: 'مالک کے طور پر سائن ان',
    tl: 'Mag-sign in bilang may-ari',
  },
  'Start a new household': {
    he: 'פתח משק בית חדש',
    ur: 'نیا گھرانہ کھولیں',
    tl: 'Magbukas ng bagong sambahayan',
  },
  'Sign in as staff': {
    he: 'כניסה כעובד',
    ur: 'عملہ کے طور پر سائن ان',
    tl: 'Mag-sign in bilang tauhan',
  },
  'Start your household': {
    he: 'פתח את משק הבית',
    ur: 'اپنا گھرانہ کھولیں',
    tl: 'Buksan ang sambahayan',
  },
  'One app for the whole family — Hebrew, English, اردو, Tagalog': {
    he: 'אפליקציה אחת לכל המשפחה — עברית, אנגלית, اردو, Tagalog',
    ur: 'پورے خاندان کے لیے ایک ایپ — Hebrew, English, اردو, Tagalog',
    tl: 'Isang app para sa buong pamilya — Hebrew, English, اردو, Tagalog',
  },
  'Staff?': { he: 'עובד?', ur: 'عملہ؟', tl: 'Tauhan?' },
  'Sign in here': { he: 'כניסה כאן', ur: 'یہاں سائن ان کریں', tl: 'Mag-sign in dito' },
  'Same sign-in for owners and staff — we figure out who you are.': {
    he: 'אותה כניסה לבעלים ולעובדים — אנחנו מזהים מי אתה לפי השם.',
    ur: 'مالکان اور عملہ کے لیے ایک ہی سائن ان — ہم خود پہچان لیں گے۔',
    tl: 'Iisang sign-in para sa may-ari at tauhan — kami ang aalam kung sino ka.',
  },
  'Email or username': {
    he: 'אימייל או שם משתמש',
    ur: 'ای میل یا صارف نام',
    tl: 'Email o username',
  },
  'This username exists in more than one household. Add the household code to continue.': {
    he: 'שם המשתמש קיים ביותר ממשק בית אחד. הוסף קוד בית כדי להמשיך.',
    ur: 'یہ صارف نام ایک سے زائد گھرانوں میں موجود ہے۔ آگے بڑھنے کے لیے گھریلو کوڈ شامل کریں۔',
    tl: 'Umiiral ang username na ito sa higit sa isang sambahayan. Magdagdag ng household code.',
  },
  Show: { he: 'הצג', ur: 'دکھائیں', tl: 'Ipakita' },
  Hide: { he: 'הסתר', ur: 'چھپائیں', tl: 'Itago' },

  // Nav
  Home: { he: 'הבית', ur: 'گھر', tl: 'Bahay' },
  Tasks: { he: 'משימות', ur: 'کام', tl: 'Mga gawain' },
  'New task': { he: 'משימה חדשה', ur: 'نیا کام', tl: 'Bagong task' },
  'Quick command': { he: 'פקודה מהירה', ur: 'فوری حکم', tl: 'Mabilis na utos' },
  Staff: { he: 'עובדים', ur: 'عملہ', tl: 'Tauhan' },
  Money: { he: 'כסף', ur: 'پیسے', tl: 'Pera' },
  Expenses: { he: 'הוצאות', ur: 'اخراجات', tl: 'Mga gastos' },
  Memory: { he: 'זכרון הבית', ur: 'گھر کی یادداشت', tl: 'Alaala' },
  Today: { he: 'היום', ur: 'آج', tl: 'Ngayong araw' },
  Profile: { he: 'פרופיל', ur: 'پروفائل', tl: 'Profile' },
  'Sign out': { he: 'יציאה', ur: 'سائن آؤٹ', tl: 'Mag-sign out' },
  Primary: { he: 'ניווט ראשי', ur: 'بنیادی نیویگیشن', tl: 'Pangunahing nabigasyon' },
  Language: { he: 'שפה', ur: 'زبان', tl: 'Wika' },

  // Common actions
  Save: { he: 'שמור', ur: 'محفوظ کریں', tl: 'I-save' },
  Cancel: { he: 'ביטול', ur: 'منسوخ', tl: 'Kanselahin' },
  Continue: { he: 'המשך', ur: 'جاری رکھیں', tl: 'Magpatuloy' },
  Back: { he: 'חזרה', ur: 'واپس', tl: 'Bumalik' },
  Approve: { he: 'אשר', ur: 'منظور کریں', tl: 'Aprubahan' },
  Reject: { he: 'דחה', ur: 'رد کریں', tl: 'Tanggihan' },
  'Waiting on the owner': {
    he: 'ממתין לאישור הבעלים',
    ur: 'مالک کے فیصلے کا انتظار ہے',
    tl: 'Hinihintay ang pasya ng may-ari',
  },
  Delete: { he: 'מחק', ur: 'حذف کریں', tl: 'Burahin' },
  Edit: { he: 'ערוך', ur: 'ترمیم کریں', tl: 'I-edit' },
  Send: { he: 'שלח', ur: 'بھیجیں', tl: 'Ipadala' },
  Start: { he: 'התחל', ur: 'شروع کریں', tl: 'Simulan' },
  Done: { he: 'בוצע', ur: 'مکمل', tl: 'Tapos' },
  Archive: { he: 'ארכב', ur: 'محفوظ', tl: 'I-archive' },
  Sign: { he: 'כניסה', ur: 'سائن ان', tl: 'Mag-sign in' },
  Loading: { he: 'טוען…', ur: 'لوڈ ہو رہا ہے…', tl: 'Naglo-load…' },
  'Loading failed': { he: 'טעינה נכשלה', ur: 'لوڈ ناکام', tl: 'Bigo ang pag-load' },
  Failed: { he: 'נכשל', ur: 'ناکام', tl: 'Bigo' },
  'Something went wrong': { he: 'משהו השתבש', ur: 'کچھ غلط ہو گیا', tl: 'May nangyaring mali' },
  'Something went wrong. Please try again.': {
    he: 'משהו השתבש. נסה שוב.',
    ur: 'کچھ غلط ہو گیا۔ دوبارہ کوشش کریں۔',
    tl: 'May nangyaring mali. Subukan muli.',
  },
  'This screen hit a problem. The rest of the app is fine.': {
    he: 'למסך הזה הייתה תקלה. שאר האפליקציה תקינה.',
    ur: 'اس اسکرین میں مسئلہ ہوا۔ باقی ایپ ٹھیک ہے۔',
    tl: 'May problema ang screen na ito. Maayos ang ibang bahagi ng app.',
  },
  Close: { he: 'סגור', ur: 'بند کریں', tl: 'Isara' },
  'Approve and create': {
    he: 'אשר וצור',
    ur: 'منظور کریں اور بنائیں',
    tl: 'Aprubahan at gawin',
  },
  'Reject proposal': {
    he: 'דחה הצעה',
    ur: 'تجویز مسترد کریں',
    tl: 'Tanggihan ang panukala',
  },
  'Delete receipt': { he: 'מחק קבלה', ur: 'رسید حذف کریں', tl: 'Burahin ang resibo' },
  'Delete this receipt permanently?': {
    he: 'למחוק את הקבלה הזו לצמיתות?',
    ur: 'یہ رسید مستقل طور پر حذف کریں؟',
    tl: 'Permanenteng burahin ang resibong ito?',
  },
  'Receipt photo': { he: 'תצלום קבלה', ur: 'رسید کی تصویر', tl: 'Larawan ng resibo' },

  // Owner home
  'My household': { he: 'הבית שלי', ur: 'میرا گھر', tl: 'Aking sambahayan' },
  'Needs your attention': { he: 'דורש את תשומת לבך', ur: 'آپ کی توجہ درکار', tl: 'Kailangan mo' },
  'Awaiting approval': {
    he: 'ממתין לאישור',
    ur: 'منظوری کا منتظر',
    tl: 'Hinihintay ang pag-apruba',
  },
  'In progress': { he: 'בביצוע', ur: 'جاری', tl: 'Ginagawa' },
  'Stuck or overdue': { he: 'תקוע / חורג', ur: 'پھنسا / دیر', tl: 'Tigil o lampas' },
  'Active staff': { he: 'צוות פעיל', ur: 'فعال عملہ', tl: 'Aktibong tauhan' },
  'Smart suggestions': { he: 'הצעות חכמות', ur: 'سمارٹ تجاویز', tl: 'Mga mungkahi' },
  Suggestion: { he: 'הצעה', ur: 'تجویز', tl: 'Mungkahi' },
  "Who's working on what": {
    he: 'מי עובד על מה',
    ur: 'کون کیا کر رہا ہے',
    tl: 'Sino ang gumagawa ng ano',
  },
  'Everything under control.': {
    he: 'הכל תחת שליטה.',
    ur: 'سب کنٹرول میں ہے۔',
    tl: 'Maayos ang lahat.',
  },
  'Nothing scheduled today.': {
    he: 'היום ריק.',
    ur: 'آج کوئی پروگرام نہیں۔',
    tl: 'Walang naka-schedule ngayong araw.',
  },
  'No active staff.': {
    he: 'אין עובדים פעילים.',
    ur: 'کوئی فعال عملہ نہیں۔',
    tl: 'Walang aktibong tauhan.',
  },
  Now: { he: 'עכשיו', ur: 'ابھی', tl: 'Ngayon' },
  Waiting: { he: 'בהמתנה', ur: 'انتظار میں', tl: 'Naghihintay' },
  Overdue: { he: 'חורג', ur: 'دیر', tl: 'Lampas na' },
  'Outstanding owed': { he: 'חוב לעובדים', ur: 'بقایا واجبات', tl: 'Hindi pa nababayaran' },
  'Pending receipts': { he: 'קבלות ממתינות', ur: 'زیر التواء رسیدیں', tl: 'Mga rebong nakabinbin' },
  'Active loans': { he: 'הלוואות פעילות', ur: 'فعال قرضے', tl: 'Aktibong utang' },
  Deadline: { he: 'דדליין', ur: 'آخری تاریخ', tl: 'Deadline' },

  // Staff today
  'Right now': { he: 'עכשיו', ur: 'ابھی', tl: 'Ngayon mismo' },
  'Up next': { he: 'הבא', ur: 'اگلا', tl: 'Susunod' },
  Upcoming: { he: 'בקרוב', ur: 'آنے والے', tl: 'Paparating' },
  Tomorrow: { he: 'מחר', ur: 'کل', tl: 'Bukas' },
  'Waiting on me': { he: 'מחכה ממני', ur: 'مجھ سے انتظار', tl: 'Hinihintay sa akin' },
  'Done today': { he: 'בוצע היום', ur: 'آج مکمل', tl: 'Tapos ngayon' },
  'Nothing to do right now.': {
    he: 'אין מה לעשות עכשיו.',
    ur: 'ابھی کچھ نہیں کرنا۔',
    tl: 'Wala munang gagawin.',
  },
  "I'm starting": { he: 'מתחיל', ur: 'شروع کر رہا ہوں', tl: 'Sisimulan ko' },
  'Send proof': { he: 'שלח הוכחה', ur: 'ثبوت بھیجیں', tl: 'Magpadala ng patunay' },
  'Mark done': {
    he: 'סיימתי, שלח לאישור',
    ur: 'مکمل، منظوری بھیجیں',
    tl: 'Tapos, ipasa sa approval',
  },
  'Photo (camera)': { he: 'תמונה (מצלמה)', ur: 'تصویر (کیمرہ)', tl: 'Larawan (kamera)' },
  // Native camera (apps/mobile CameraModal / ProofSheet) — permission states
  // and the capture-preview actions. English-only in the web table so far.
  // ── Mobile: onboarding + auth + new screens (2026-08-04) ─────────────
  Skip: { he: 'דלג', ur: 'چھوڑیں', tl: 'Laktawan' },
  'For families with housekeepers, drivers, nannies and assistants': {
    he: 'למשפחות עם מנקות, נהגים, מטפלות ועוזרים אישיים',
    ur: 'گھریلو ملازمین، ڈرائیوروں، نانیوں اور اسسٹنٹس والے خاندانوں کے لیے',
    tl: 'Para sa mga pamilyang may katulong, drayber, yaya at assistant',
  },
  'Everything your household needs. In one place.': {
    he: 'כל מה שהבית צריך. במקום אחד.',
    ur: 'گھر کی ہر ضرورت۔ ایک ہی جگہ۔',
    tl: 'Lahat ng kailangan ng bahay. Sa isang lugar.',
  },
  'Every task has an owner': {
    he: 'לכל משימה יש אחראי',
    ur: 'ہر کام کا ایک ذمہ دار',
    tl: 'Bawat gawain ay may may-ari',
  },
  'Photo proof included': {
    he: 'כולל הוכחה בתמונה',
    ur: 'تصویری ثبوت شامل',
    tl: 'Kasama ang photo proof',
  },
  'Recurring routines': {
    he: 'שגרות חוזרות',
    ur: 'بار بار کی روٹینیں',
    tl: 'Mga paulit-ulit na routine',
  },
  'No more explaining things twice.': {
    he: 'לא עוד הסברים פעמיים.',
    ur: 'اب کوئی بھی بات دو بار نہیں کہنی۔',
    tl: 'Hindi na kailangang ipaliwanag nang dalawang beses.',
  },
  'Step-by-step instructions': {
    he: 'הוראות צעד-אחר-צעד',
    ur: 'قدم بہ قدم ہدایات',
    tl: 'Tagubiling hakbang-hakbang',
  },
  'Same-day reimbursements': {
    he: 'החזרים באותו היום',
    ur: 'اسی دن کی ادائیگی واپسی',
    tl: 'Reimbursement sa mismong araw',
  },
  'Available in 30+ languages': {
    he: 'זמין ב-30+ שפות',
    ur: '30+ زبانوں میں دستیاب',
    tl: 'Available sa 30+ wika',
  },
  '+27 more': { he: '+27 נוספות', ur: '+27 مزید', tl: '+27 pa' },
  'You get your mind back.': {
    he: 'אתם מקבלים את הראש שלכם בחזרה.',
    ur: 'آپ کا ذہن واپس ملتا ہے۔',
    tl: 'Mababalik ang kapayapaan ng isip mo.',
  },
  'Submit a receipt in seconds': {
    he: 'שולחים קבלה בשניות',
    ur: 'سیکنڈوں میں رسید جمع کریں',
    tl: 'Mag-submit ng resibo sa ilang segundo',
  },
  'Get reimbursed the same day': {
    he: 'מקבלים החזר באותו היום',
    ur: 'اسی دن رقم واپس پائیں',
    tl: 'Mabayaran sa mismong araw',
  },
  'Create my household': {
    he: 'צרו את משק הבית שלי',
    ur: 'میرا گھرانہ بنائیں',
    tl: 'Gawin ang aking sambahayan',
  },
  'Free for your staff, always.': {
    he: 'חינם לעובדים שלכם, תמיד.',
    ur: 'آپ کے عملے کے لیے ہمیشہ مفت۔',
    tl: 'Libre para sa iyong staff, palagi.',
  },
  'Username or phone number': {
    he: 'שם משתמש או מספר טלפון',
    ur: 'صارف نام یا فون نمبر',
    tl: 'Username o numero ng telepono',
  },
  'Household management for modern families.': {
    he: 'ניהול משק בית למשפחות מודרניות.',
    ur: 'جدید خاندانوں کے لیے گھریلو انتظام۔',
    tl: 'Pamamahala ng sambahayan para sa modernong pamilya.',
  },
  'Scan insight': { he: 'תובנת סריקה', ur: 'اسکین بصیرت', tl: 'Insight ng scan' },
  'Assigned to you': { he: 'מוקצה אליך', ur: 'آپ کو سونپا گیا', tl: 'Nakatalaga sa iyo' },
  'Add as task': { he: 'הוסף כמשימה', ur: 'کام کے طور پر شامل کریں', tl: 'Idagdag bilang gawain' },
  'Reading the receipt…': {
    he: 'קורא את הקבלה…',
    ur: 'رسید پڑھی جا رہی ہے…',
    tl: 'Binabasa ang resibo…',
  },
  'Add to my day': { he: 'הוסף ליום שלי', ur: 'میرے دن میں شامل کریں', tl: 'Idagdag sa araw ko' },
  'Your own — it lands on your day.': {
    he: 'שלך — נוחת על היום שלך.',
    ur: 'آپ کا اپنا — یہ آپ کے دن میں آ جائے گا۔',
    tl: 'Sa iyo — dadating ito sa araw mo.',
  },
  'What are you doing?': {
    he: 'מה את/ה עושה?',
    ur: 'آپ کیا کر رہے ہیں؟',
    tl: 'Anong ginagawa mo?',
  },
  'A note (optional)': { he: 'הערה (אופציונלי)', ur: 'نوٹ (اختیاری)', tl: 'Tala (opsyonal)' },
  // Mobile: owner composer + owner settings (2026-08-04) — the owner's +
  // assigns to a person he picks; his Settings screen mirrors the staff More.
  'It lands on their day.': {
    he: 'נוחת על היום שלהם.',
    ur: 'یہ ان کے دن میں آ جائے گا۔',
    tl: 'Dadating ito sa araw nila.',
  },
  'Assign to': { he: 'הקצאה ל', ur: 'تفویض کریں', tl: 'Italaga kay' },
  About: { he: 'אודות', ur: 'کے بارے میں', tl: 'Tungkol' },
  'App version': { he: 'גרסת האפליקציה', ur: 'ایپ کا ورژن', tl: 'Bersyon ng app' },
  'Pick a new password': {
    he: 'בחר סיסמה חדשה',
    ur: 'نیا پاس ورڈ منتخب کریں',
    tl: 'Pumili ng bagong password',
  },
  'Before you continue.': {
    he: 'לפני שממשיכים.',
    ur: 'آگے بڑھنے سے پہلے۔',
    tl: 'Bago ka magpatuloy.',
  },
  'Your house, at a glance': {
    he: 'הבית שלך, במבט אחד',
    ur: 'آپ کا گھر، ایک نظر میں',
    tl: 'Ang bahay mo, sa isang tingin',
  },
  'You subscribe through the App Store. Open Settings on your iPhone, tap your name, then Subscriptions, to change or cancel it.':
    {
      he: 'המנוי שלך דרך App Store. פתח הגדרות באייפון, הקש על השם שלך, ואז מנויים, כדי לשנות או לבטל.',
      ur: 'آپ کی سبسکرپشن App Store کے ذریعے ہے۔ آئی فون پر Settings کھولیں، اپنے نام پر ٹیپ کریں، پھر Subscriptions، تبدیلی یا منسوخی کے لیے۔',
      tl: 'Naka-subscribe ka sa pamamagitan ng App Store. Buksan ang Settings sa iPhone, i-tap ang pangalan mo, tapos Subscriptions, para baguhin o kanselahin.',
    },
  'You subscribe through Google Play. Open the Play Store app, tap your profile, then Payments and subscriptions, to change or cancel it.':
    {
      he: 'המנוי שלך דרך Google Play. פתח את אפליקציית Play Store, הקש על הפרופיל, ואז תשלומים ומנויים, כדי לשנות או לבטל.',
      ur: 'آپ کی سبسکرپشن Google Play کے ذریعے ہے۔ Play Store ایپ کھولیں، پروفائل پر ٹیپ کریں، پھر Payments and subscriptions، تبدیلی یا منسوخی کے لیے۔',
      tl: 'Naka-subscribe ka sa pamamagitan ng Google Play. Buksan ang Play Store app, i-tap ang profile mo, tapos Payments and subscriptions, para baguhin o kanselahin.',
    },
  'Email, phone or username': {
    he: 'אימייל, טלפון או שם משתמש',
    ur: 'ای میل، فون یا صارف نام',
    tl: 'Email, telepono o username',
  },
  'Already have a household? Sign in': {
    he: 'כבר יש לך משק בית? התחבר',
    ur: 'پہلے سے گھرانہ ہے؟ سائن ان کریں',
    tl: 'May sambahayan ka na? Mag-sign in',
  },
  'Your full name': { he: 'שמך המלא', ur: 'آپ کا پورا نام', tl: 'Ang buo mong pangalan' },
  'Household name (optional)': {
    he: 'שם משק הבית (אופציונלי)',
    ur: 'گھرانے کا نام (اختیاری)',
    tl: 'Pangalan ng sambahayan (opsyonal)',
  },
  'Owner? Sign in here': {
    he: 'בעלים? כניסה כאן',
    ur: 'مالک؟ یہاں سائن ان کریں',
    tl: 'May-ari? Mag-sign in dito',
  },
  'New here? Create your household': {
    he: 'חדש כאן? צור את משק הבית שלך',
    ur: 'نئے ہیں؟ اپنا گھرانہ بنائیں',
    tl: 'Bago ka? Gawin ang iyong sambahayan',
  },
  'Staff? Sign in here': {
    he: 'עובד? כניסה כאן',
    ur: 'عملہ؟ یہاں سائن ان کریں',
    tl: 'Staff? Mag-sign in dito',
  },
  'Search language': { he: 'חפש שפה', ur: 'زبان تلاش کریں', tl: 'Maghanap ng wika' },
  'No languages match your search': {
    he: 'אין שפות שתואמות את החיפוש',
    ur: 'تلاش سے کوئی زبان نہیں ملی',
    tl: 'Walang wikang tumugma sa hinahanap',
  },
  'Your people': { he: 'האנשים שלך', ur: 'آپ کے لوگ', tl: 'Ang mga tao mo' },
  'What each person may do beyond their own work.': {
    he: 'מה כל אדם רשאי לעשות מעבר לעבודתו.',
    ur: 'ہر شخص اپنے کام کے علاوہ کیا کر سکتا ہے۔',
    tl: 'Ano ang maaaring gawin ng bawat tao nang higit sa sariling trabaho.',
  },
  'Can create and hand out tasks': {
    he: 'יכול ליצור ולהקצות משימות',
    ur: 'کام بنانے اور تقسیم کر سکتے ہیں',
    tl: 'Maaaring lumikha at mag-assign ng mga gawain',
  },
  'Can manage the team': {
    he: 'יכול לנהל את הצוות',
    ur: 'ٹیم کو منظم کر سکتے ہیں',
    tl: 'Maaaring pamahalaan ang koponan',
  },
  'Can see the money screens': {
    he: 'יכול לראות את מסכי הכסף',
    ur: 'رقم کی اسکرینیں دیکھ سکتے ہیں',
    tl: 'Makikita ang mga screen ng pera',
  },
  // Owner people screen — the task-permission model as the server enforces
  // it: self-tasks always on (founder rule), then the three real switches.
  'Can write tasks for herself': {
    he: 'יכולה לכתוב משימות לעצמה',
    ur: 'اپنے لیے کام لکھ سکتی ہے',
    tl: 'Makakapagsulat ng mga gawain para sa sarili',
  },
  'Always on — every staff member can write tasks for herself.': {
    he: 'תמיד מופעל — כל עובדת יכולה לכתוב משימות לעצמה.',
    ur: 'ہمیشہ فعال — ہر ملازمہ اپنے لیے کام لکھ سکتی ہے۔',
    tl: 'Laging naka-on — bawat staff ay makakapagsulat ng mga gawain para sa sarili.',
  },
  'Assign work': { he: 'הקצאת עבודה', ur: 'کام سونپنا', tl: 'Mag-assign ng trabaho' },
  'Manage staff': { he: 'ניהול צוות', ur: 'عملے کی نگرانی', tl: 'Pamahalaan ang staff' },
  'See money': { he: 'צפייה בכסף', ur: 'پیسے دیکھنا', tl: 'Makita ang pera' },
  'The house': { he: 'הבית', ur: 'گھر', tl: 'Ang bahay' },
  'Your photo': { he: 'התמונה שלך', ur: 'آپ کی تصویر', tl: 'Ang litrato mo' },
  'Change photo': { he: 'שנה תמונה', ur: 'تصویر بدلیں', tl: 'Palitan ang larawan' },
  'Your name': { he: 'שמך', ur: 'آپ کا نام', tl: 'Ang pangalan mo' },
  'Out there': { he: 'בחוץ בעבודה', ur: 'باہر جاری', tl: 'Nasa labas' },
  'Payroll, expenses and loans live on the web for now — the numbers above are live.': {
    he: 'שכר, הוצאות והלוואות זמינים ב-web לעת עתה — המספרים למעלה חיים.',
    ur: 'پے رول، اخراجات اور قرضے فی الحال ویب پر ہیں — اوپر کے اعداد لائیو ہیں۔',
    tl: 'Ang payroll, expenses at loans ay nasa web muna — live ang mga numero sa taas.',
  },
  'Nothing waits on you. The house is moving.': {
    he: 'שום דבר לא מחכה לך. הבית זז.',
    ur: 'کوئی چیز آپ کا انتظار نہیں کر رہی۔ گھر چل رہا ہے۔',
    tl: 'Walang naghihintay sa iyo. Gumagalaw ang bahay.',
  },
  'Nothing on the board yet.': {
    he: 'אין כלום על הלוח עדיין.',
    ur: 'ابھی بورڈ خالی ہے۔',
    tl: 'Wala pa sa board.',
  },
  'No staff yet — invite your first person.': {
    he: 'אין עובדים עדיין — הזמן את האדם הראשון.',
    ur: 'ابھی کوئی عملہ نہیں — پہلے شخص کو مدعو کریں۔',
    tl: 'Wala pang staff — imbitahin ang unang tao.',
  },
  '1 thing needs you': {
    he: 'דבר אחד צריך אותך',
    ur: 'ایک چیز کو آپ کی ضرورت ہے',
    tl: 'Isang bagay ang kailangan ka',
  },

  'Camera access is off': {
    he: 'גישה למצלמה כבויה',
    ur: 'کیمرے تک رسائی بند ہے',
    tl: 'Naka-off ang access sa camera',
  },
  'Famanage needs the camera to photograph receipts and proof of work.': {
    he: 'Famanage צריכה את המצלמה כדי לצלם קבלות והוכחות לעבודה.',
    ur: 'Famanage کو رسیدوں اور کام کے ثبوت کی تصویر لینے کے لیے کیمرے کی ضرورت ہے۔',
    tl: 'Kailangan ng Famanage ang camera para kunan ng litrato ang mga resibo at patunay ng trabaho.',
  },
  'Allow camera access': {
    he: 'אפשר גישה למצלמה',
    ur: 'کیمرے کی اجازت دیں',
    tl: 'Payagan ang access sa camera',
  },
  'Open Settings': { he: 'פתח הגדרות', ur: 'ترتیبات کھولیں', tl: 'Buksan ang Settings' },
  'Use photo': { he: 'השתמש בתמונה', ur: 'تصویر استعمال کریں', tl: 'Gamitin ang litrato' },
  'Microphone access is off': {
    he: 'גישה למיקרופון כבויה',
    ur: 'مائیکروفون تک رسائی بند ہے',
    tl: 'Naka-off ang access sa microphone',
  },
  'Voice note': { he: 'הקלטה קולית', ur: 'وائس نوٹ', tl: 'Voice note' },
  'Stop recording': { he: 'עצור הקלטה', ur: 'ریکارڈنگ روکیں', tl: 'Itigil ang pag-record' },
  'Voice ready · tap to re-record': {
    he: 'הקלטה מוכנה · הקש להקלטה מחדש',
    ur: 'تیار · دوبارہ ریکارڈ',
    tl: 'Tapos · pindutin para ulitin',
  },
  'Short note (optional)': {
    he: 'תיאור קצר (לא חובה)',
    ur: 'مختصر نوٹ (اختیاری)',
    tl: 'Maikling tala (opsyonal)',
  },
  'Describe what was done': {
    he: 'תאר מה נעשה',
    ur: 'بیان کریں کیا کیا گیا',
    tl: 'Ilarawan kung ano ang nagawa',
  },
  'Routines repeat on their own — set one up once and it runs every week.': {
    he: 'שגרות חוזרות מעצמן — הגדר פעם אחת והן ירוצו כל שבוע.',
    ur: 'روٹینز خود بخود دہراتی ہیں — ایک بار سیٹ کریں اور وہ ہر ہفتے چلیں گی۔',
    tl: 'Awtomatikong umuulit ang mga rutina — i-set up minsan at tatakbo ito bawat linggo.',
  },
  'Everything handed out has been finished or approved.': {
    he: 'כל מה שחולק כבר הסתיים או אושר.',
    ur: 'جو کچھ سونپا گیا وہ سب مکمل یا منظور ہو چکا ہے۔',
    tl: 'Ang lahat ng naibigay na trabaho ay tapos na o naaprubahan na.',
  },

  // Operation detail
  History: { he: 'היסטוריה', ur: 'تاریخ', tl: 'Kasaysayan' },
  'Nothing happened yet.': {
    he: 'עוד לא קרה כלום.',
    ur: 'ابھی کچھ نہیں ہوا۔',
    tl: 'Wala pang nangyari.',
  },
  Add: { he: 'הוסף', ur: 'شامل کریں', tl: 'Magdagdag' },
  'Write a message tied to the task…': {
    he: 'כתוב הודעה צמודה למשימה…',
    ur: 'کام کے ساتھ پیغام لکھیں…',
    tl: 'Magsulat ng mensahe sa gawain…',
  },
  Note: { he: 'הערה', ur: 'نوٹ', tl: 'Tala' },
  Question: { he: 'שאלה', ur: 'سوال', tl: 'Tanong' },
  Answer: { he: 'תשובה', ur: 'جواب', tl: 'Sagot' },
  Issue: { he: 'בעיה', ur: 'مسئلہ', tl: 'Problema' },
  Reminder: { he: 'תזכורת', ur: 'یاد دہانی', tl: 'Paalala' },
  Instruction: { he: 'הוראה', ur: 'ہدایت', tl: 'Tagubilin' },
  Approval: { he: 'אישור', ur: 'منظوری', tl: 'Pag-apruba' },
  Rejection: { he: 'דחייה', ur: 'انکار', tl: 'Pagtanggi' },
  Proof: { he: 'הוכחה', ur: 'ثبوت', tl: 'Patunay' },
  Attachment: { he: 'קובץ מצורף', ur: 'منسلکہ', tl: 'Kalakip' },
  'Assigned to': { he: 'עובד אחראי', ur: 'تفویض', tl: 'Italaga kay' },
  Priority: { he: 'עדיפות', ur: 'ترجیح', tl: 'Priyoridad' },
  'Scheduled for': { he: 'מתוכנן ל', ur: 'وقت', tl: 'Naka-schedule sa' },
  Created: { he: 'נוצרה', ur: 'بنایا', tl: 'Ginawa' },
  'Not assigned': { he: 'לא משויך', ur: 'تفویض نہیں', tl: 'Hindi italaga' },
  'Cancel task': { he: 'בטל משימה', ur: 'کام منسوخ کریں', tl: 'Kanselahin' },
  Publish: { he: 'פרסם', ur: 'شائع کریں', tl: 'I-publish' },
  'Why reject?': { he: 'סיבת דחייה?', ur: 'انکار کی وجہ؟', tl: 'Bakit tanggihan?' },
  'Why cancel?': { he: 'סיבת ביטול?', ur: 'منسوخی کی وجہ؟', tl: 'Bakit kanselahin?' },

  // Owner parity surfaces (S6): compose advanced section, op transitions,
  // approvals queue, routines
  'More options': { he: 'אפשרויות נוספות', ur: 'مزید اختیارات', tl: 'Higit pang opsyon' },
  'In 1 hour': { he: 'בעוד שעה', ur: 'ایک گھنٹے میں', tl: 'Sa loob ng 1 oras' },
  'This evening': { he: 'הערב', ur: 'آج شام', tl: 'Ngayong gabi' },
  'Tomorrow morning': { he: 'מחר בבוקר', ur: 'کل صبح', tl: 'Bukas ng umaga' },
  'Add a step': { he: 'הוסף שלב', ur: 'ایک مرحلہ شامل کریں', tl: 'Magdagdag ng hakbang' },
  'Nudge sent': { he: 'התזכורת נשלחה', ur: 'یاد دہانی بھیج دی گئی', tl: 'Naipadala ang paalala' },
  'This cannot be undone.': {
    he: 'אי אפשר לבטל את הפעולה.',
    ur: 'یہ واپس نہیں ہو سکتا۔',
    tl: 'Hindi na ito mababawi.',
  },
  'Keep it': { he: 'להשאיר', ur: 'رہنے دیں', tl: 'Panatilihin' },
  'Keep current': {
    he: 'השאר את הנוכחי',
    ur: 'موجودہ رکھیں',
    tl: 'Panatilihin ang kasalukuyan',
  },
  'No date': { he: 'ללא תאריך', ur: 'کوئی تاریخ نہیں', tl: 'Walang petsa' },
  'Next run': { he: 'הרצה הבאה', ur: 'اگلی بار', tl: 'Susunod na takbo' },
  Approvals: { he: 'אישורים', ur: 'منظوریاں', tl: 'Mga pag-apruba' },
  'No receipts waiting for review.': {
    he: 'אין קבלות שממתינות לבדיקה.',
    ur: 'کوئی رسید جائزے کی منتظر نہیں۔',
    tl: 'Walang resibong naghihintay ng pagsusuri.',
  },
  'No reimbursements waiting.': {
    he: 'אין החזרים ממתינים.',
    ur: 'کوئی واپسی کی رقم زیر التوا نہیں۔',
    tl: 'Walang naghihintay na reimbursement.',
  },
  'Photo unavailable': {
    he: 'התמונה לא זמינה',
    ur: 'تصویر دستیاب نہیں',
    tl: 'Hindi available ang larawan',
  },
  'Reason (optional)': { he: 'סיבה (אופציונלי)', ur: 'وجہ (اختیاری)', tl: 'Dahilan (opsyonal)' },
  'Why is it rejected?': {
    he: 'למה זה נדחה?',
    ur: 'کیوں مسترد کیا گیا؟',
    tl: 'Bakit ito tinanggihan?',
  },

  // Priority labels (3-level: normal | important | emergency, matches schema)
  Normal: { he: 'רגיל', ur: 'عام', tl: 'Karaniwan' },
  Important: { he: 'חשוב', ur: 'اہم', tl: 'Mahalaga' },
  Emergency: { he: 'חירום', ur: 'ہنگامی', tl: 'Emerhensiya' },

  // State labels (full set)
  Draft: { he: 'טיוטה', ur: 'مسودہ', tl: 'Burador' },
  Scheduled: { he: 'מתוכנן', ur: 'شیڈولڈ', tl: 'Naka-schedule' },
  Sent: { he: 'נשלח', ur: 'بھیجا', tl: 'Naipadala' },
  Seen: { he: 'נראה', ur: 'دیکھا', tl: 'Nakita' },
  'Waiting for proof': { he: 'ממתין להוכחה', ur: 'ثبوت کا انتظار', tl: 'Hinihintay ang patunay' },
  Approved: { he: 'אושר', ur: 'منظور', tl: 'Aprubado' },
  Rejected: { he: 'נדחה', ur: 'مسترد', tl: 'Tinanggihan' },
  Cancelled: { he: 'בוטל', ur: 'منسوخ', tl: 'Kinansela' },
  'Needs proof': { he: 'דורש הוכחה', ur: 'ثبوت چاہئے', tl: 'Kailangan ng patunay' },

  // Auth screens
  'Sign in': { he: 'כניסה', ur: 'سائن ان', tl: 'Mag-sign in' },
  'Sign up': { he: 'הרשמה', ur: 'سائن اپ', tl: 'Mag-sign up' },
  Email: { he: 'אימייל', ur: 'ای میل', tl: 'Email' },
  Password: { he: 'סיסמה', ur: 'پاس ورڈ', tl: 'Password' },
  'Household code': { he: 'קוד הבית', ur: 'گھریلو کوڈ', tl: 'Code ng sambahayan' },
  'Only you should know it — not the person who set up your account.': {
    he: 'רק אתה צריך לדעת אותה — לא מי שפתח לך את החשבון.',
    ur: 'صرف آپ کو معلوم ہونی چاہیے — وہ نہیں جس نے آپ کا اکاؤنٹ بنایا۔',
    tl: 'Ikaw lang ang dapat nakakaalam — hindi ang nag-set up ng account mo.',
  },
  'Pick a new password before you continue.': {
    he: 'צריך לבחור סיסמה חדשה לפני שתמשיך.',
    ur: 'جاری رکھنے سے پہلے نیا پاس ورڈ منتخب کریں۔',
    tl: 'Pumili ng bagong password bago magpatuloca.',
  },
  'Sign-in required': {
    he: 'נדרשת התחברות מחדש',
    ur: 'دوبارہ سائن ان درکار',
    tl: 'Kailangan mag-sign in muli',
  },

  // Profile
  'Change name': { he: 'שינוי שם', ur: 'نام بدلیں', tl: 'Palitan ang pangalan' },
  'Change password': { he: 'שינוי סיסמה', ur: 'پاس ورڈ بدلیں', tl: 'Palitan ang password' },
  'Profile picture': { he: 'תמונת פרופיל', ur: 'پروفائل تصویر', tl: 'Larawan' },
  'Edit photo': { he: 'ערוך תמונה', ur: 'تصویر تبدیل کریں', tl: 'I-edit ang larawan' },
  Remove: { he: 'הסר', ur: 'ہٹا دیں', tl: 'Alisin' },
  'Good morning': { he: 'בוקר טוב', ur: 'صبح بخیر', tl: 'Magandang umaga' },
  'Good afternoon': { he: 'צהריים טובים', ur: 'دوپہر بخیر', tl: 'Magandang hapon' },
  'Good evening': { he: 'ערב טוב', ur: 'شام بخیر', tl: 'Magandang gabi' },
  'Display language': { he: 'שפת ממשק', ur: 'انٹرفیس زبان', tl: 'Wika' },
  'Current password': { he: 'סיסמה נוכחית', ur: 'موجودہ پاس ورڈ', tl: 'Kasalukuyang password' },
  'New?': { he: 'חדש?', ur: 'نیا؟', tl: 'Bago?' },
  'Full name': { he: 'שם מלא', ur: 'پورا نام', tl: 'Buong pangalan' },
  'Family name': { he: 'שם משפחה', ur: 'خاندانی نام', tl: 'Pangalan ng pamilya' },
  'Household name': { he: 'שם הבית', ur: 'گھر کا نام', tl: 'Pangalan ng sambahayan' },
  Country: { he: 'מדינה', ur: 'ملک', tl: 'Bansa' },
  Currency: { he: 'מטבע', ur: 'کرنسی', tl: 'Pera' },
  'Household settings': {
    he: 'הגדרות משק בית',
    ur: 'گھرانے کی ترتیبات',
    tl: 'Mga setting ng sambahayan',
  },
  'All amounts on the Expenses screen are shown in this currency.': {
    he: 'כל הסכומים במסך ההוצאות מוצגים במטבע הזה.',
    ur: 'اخراجات کی اسکرین پر تمام رقمیں اس کرنسی میں دکھائی جاتی ہیں۔',
    tl: 'Lahat ng halaga sa Expenses screen ay ipapakita sa currency na ito.',
  },
  Timezone: { he: 'אזור זמן', ur: 'ٹائم زون', tl: 'Time zone' },
  Hebrew: { he: 'עברית', ur: 'عبرانی', tl: 'Hebreo' },
  Urdu: { he: 'اردو', ur: 'اردو', tl: 'Urdu' },
  English: { he: 'אנגלית', ur: 'انگریزی', tl: 'Ingles' },
  Tagalog: { he: 'טגלוגית', ur: 'ٹیگالوگ', tl: 'Tagalog' },
  Routines: { he: 'שגרות', ur: 'روٹینز', tl: 'Mga rutina' },
  // The fourth tab on the staff phone, and what sits behind it.
  More: { he: 'עוד', ur: 'مزید', tl: 'Higit pa' },
  'How this house likes things done': {
    he: 'איך נהוג לעשות דברים בבית הזה',
    ur: 'اس گھر میں کام کیسے ہوتے ہیں',
    tl: 'Kung paano ginagawa ang mga bagay dito',
  },
  'Give work and follow it': {
    he: 'לתת עבודה ולעקוב אחריה',
    ur: 'کام دیں اور اس کی پیروی کریں',
    tl: 'Magbigay ng gawain at subaybayan',
  },
  'Who works here': {
    he: 'מי עובד כאן',
    ur: 'یہاں کون کام کرتا ہے',
    tl: 'Sino ang nagtatrabaho dito',
  },
  'What the house spent': {
    he: 'מה הבית הוציא',
    ur: 'گھر نے کیا خرچ کیا',
    tl: 'Ang ginastos ng bahay',
  },
  'Your name, language and phone': {
    he: 'השם, השפה והטלפון שלך',
    ur: 'آپ کا نام، زبان اور فون',
    tl: 'Pangalan, wika at telepono mo',
  },
  // The four places the bottom bar was reduced to, and the round button.
  People: { he: 'אנשים', ur: 'لوگ', tl: 'Mga tao' },
  'Give work': { he: 'לתת משימה', ur: 'کام دیں', tl: 'Magbigay ng gawain' },
  'Pick a person and a day': {
    he: 'בוחרים אדם ויום',
    ur: 'ایک شخص اور دن چنیں',
    tl: 'Pumili ng tao at araw',
  },
  'Say it in your own words': {
    he: 'אומרים במילים שלך',
    ur: 'اپنے الفاظ میں کہیں',
    tl: 'Sabihin sa sarili mong salita',
  },
  'Work that repeats on its own': {
    he: 'עבודה שחוזרת מעצמה',
    ur: 'کام جو خود دہرایا جائے',
    tl: 'Gawaing paulit-ulit nang kusa',
  },
  // Notification titles. The server writes these in English — one short fixed
  // set — and the inbox translates them for whoever is reading.
  'Approval needed': { he: 'ממתין לאישור', ur: 'منظوری درکار', tl: 'Kailangan ng pag-apruba' },
  'Task started': { he: 'משימה התחילה', ur: 'کام شروع ہوا', tl: 'Nagsimula ang gawain' },
  'Task approved': { he: 'משימה אושרה', ur: 'کام منظور ہوا', tl: 'Naaprubahan ang gawain' },
  'Task rejected': { he: 'משימה נדחתה', ur: 'کام مسترد ہوا', tl: 'Tinanggihan ang gawain' },
  'Task cancelled': { he: 'משימה בוטלה', ur: 'کام منسوخ ہوا', tl: 'Kinansela ang gawain' },
  'Waiting on proof': { he: 'ממתין לתמונה', ur: 'ثبوت کا انتظار', tl: 'Naghihintay ng patunay' },
  'Cash request': { he: 'בקשת מזומן', ur: 'نقد کی درخواست', tl: 'Hiling na cash' },
  'Receipt uploaded': { he: 'קבלה הועלתה', ur: 'رسید اپ لوڈ ہوئی', tl: 'Na-upload ang resibo' },
  'Loan recorded': { he: 'הלוואה נרשמה', ur: 'قرض درج ہوا', tl: 'Naitala ang utang' },
  'Loan fully repaid': {
    he: 'ההלוואה נפרעה במלואה',
    ur: 'قرض مکمل ادا ہوا',
    tl: 'Bayad na ang utang',
  },
  'Loan written off': { he: 'ההלוואה נמחקה', ur: 'قرض معاف ہوا', tl: 'Kinansela ang utang' },
  'Payslip acknowledged': { he: 'התלוש אושר', ur: 'پے سلپ کی تصدیق', tl: 'Kinilala ang payslip' },
  'Your pay terms were updated': {
    he: 'תנאי השכר שלך עודכנו',
    ur: 'آپ کی تنخواہ کی شرائط بدل گئیں',
    tl: 'Na-update ang iyong pay terms',
  },
  'You received a bonus': {
    he: 'קיבלת בונוס',
    ur: 'آپ کو بونس ملا',
    tl: 'Nakatanggap ka ng bonus',
  },
  'A tip was recorded for you': {
    he: 'נרשם עבורך טיפ',
    ur: 'آپ کے لیے ٹپ درج ہوئی',
    tl: 'May naitalang tip para sa iyo',
  },
  'A deduction was recorded': {
    he: 'נרשם ניכוי',
    ur: 'کٹوتی درج ہوئی',
    tl: 'May naitalang bawas',
  },
  'A salary advance was recorded': {
    he: 'נרשמה מקדמה',
    ur: 'پیشگی تنخواہ درج ہوئی',
    tl: 'May naitalang advance',
  },
  'A payroll entry was recorded': {
    he: 'נרשמה רשומת שכר',
    ur: 'تنخواہ کا اندراج ہوا',
    tl: 'May naitalang payroll entry',
  },
  'Someone is asking': { he: 'מישהו שואל', ur: 'کوئی پوچھ رہا ہے', tl: 'May nagtatanong' },
  'A report from your team': {
    he: 'דוח מהצוות שלך',
    ur: 'آپ کی ٹیم کی رپورٹ',
    tl: 'Ulat mula sa iyong team',
  },
  'Cash request approved': {
    he: 'בקשת המזומן אושרה',
    ur: 'نقد کی درخواست منظور ہوئی',
    tl: 'Naaprubahan ang hiling na cash',
  },
  'Cash request rejected': {
    he: 'בקשת המזומן נדחתה',
    ur: 'نقد کی درخواست مسترد ہوئی',
    tl: 'Tinanggihan ang hiling na cash',
  },
  'Cash dispensed': {
    he: 'המזומן נמסר',
    ur: 'نقد ادا کر دی گئی',
    tl: 'Naibigay na ang cash',
  },
  'Cash reconciled': {
    he: 'המזומן הותאם',
    ur: 'نقد کا حساب مکمل ہوا',
    tl: 'Na-reconcile na ang cash',
  },
  'Receipt approved': { he: 'הקבלה אושרה', ur: 'رسید منظور ہوئی', tl: 'Naaprubahan ang resibo' },
  'Receipt rejected': { he: 'הקבלה נדחתה', ur: 'رسید مسترد ہوئی', tl: 'Tinanggihan ang resibo' },
  'Reimbursement approved': {
    he: 'ההחזר אושר',
    ur: 'واپسی کی رقم منظور ہوئی',
    tl: 'Naaprubahan ang refund',
  },
  'Reimbursement rejected': {
    he: 'ההחזר נדחה',
    ur: 'واپسی کی رقم مسترد ہوئی',
    tl: 'Tinanggihan ang refund',
  },
  'Reimbursement paid': {
    he: 'ההחזר שולם',
    ur: 'واپسی کی رقم ادا ہوئی',
    tl: 'Nabayaran na ang refund',
  },
  'Loan fully paid off': {
    he: 'ההלוואה נפרעה במלואה',
    ur: 'قرض مکمل ادا ہو گیا',
    tl: 'Ganap nang bayad ang utang',
  },
  'Loan repayment recorded': {
    he: 'תשלום ההלוואה נרשם',
    ur: 'قرض کی ادائیگی درج ہوئی',
    tl: 'Naitala ang bayad sa utang',
  },
  '{{name}} logged {{hours}}h {{kind}}': {
    he: '{{name}} רשם/ה {{hours}} שעות {{kind}}',
    ur: '{{name}} نے {{hours}} گھنٹے {{kind}} درج کیں',
    tl: 'Nag-log si {{name}} ng {{hours}}h {{kind}}',
  },
  'Payslip ready — {{label}}': {
    he: 'תלוש השכר מוכן — {{label}}',
    ur: 'پے سلپ تیار ہے — {{label}}',
    tl: 'Handa na ang payslip — {{label}}',
  },
  'Salary paid — {{label}}': {
    he: 'המשכורת שולמה — {{label}}',
    ur: 'تنخواہ ادا ہوئی — {{label}}',
    tl: 'Nabayaran na ang sahod — {{label}}',
  },
  WhatsApp: { he: 'וואטסאפ', ur: 'واٹس ایپ', tl: 'WhatsApp' },
  'Your day arrives as a message, and what you reply comes back into the app.': {
    he: 'היום שלך מגיע כהודעה, ומה שאתה עונה חוזר לתוך האפליקציה.',
    ur: 'آپ کا دن ایک پیغام کی صورت آتا ہے، اور آپ کا جواب ایپ میں واپس آ جاتا ہے۔',
    tl: 'Dumarating ang araw mo bilang mensahe, at ang sagot mo ay babalik sa app.',
  },
  'Full number, starting with + and the country code.': {
    he: 'מספר מלא, שמתחיל ב‑+ ובקידומת המדינה.',
    ur: 'مکمل نمبر، + اور ملک کے کوڈ سے شروع۔',
    tl: 'Buong numero, nagsisimula sa + at country code.',
  },
  'So Call and WhatsApp work from their page.': {
    he: 'כדי שהתקשרות ווואטסאפ יעבדו מהעמוד שלהם.',
    ur: 'تاکہ ان کے صفحے سے کال اور واٹس ایپ کام کریں۔',
    tl: 'Para gumana ang Tawag at WhatsApp mula sa page nila.',
  },
  'Ready to send straight to their WhatsApp.': {
    he: 'מוכן לשליחה ישירות לוואטסאפ שלהם.',
    ur: 'ان کے واٹس ایپ پر براہ راست بھیجنے کے لیے تیار۔',
    tl: 'Handa nang ipadala diretso sa WhatsApp nila.',
  },
  'Send me my tasks on WhatsApp': {
    he: 'שלחו לי את המשימות בוואטסאפ',
    ur: 'میرے کام واٹس ایپ پر بھیجیں',
    tl: 'Ipadala ang aking mga gawain sa WhatsApp',
  },
  'That does not look like a full number. Start with + and the country code.': {
    he: 'זה לא נראה כמו מספר מלא. תתחיל ב‑+ ובקידומת המדינה.',
    ur: 'یہ مکمل نمبر نہیں لگتا۔ + اور ملک کے کوڈ سے شروع کریں۔',
    tl: 'Mukhang hindi buong numero ito. Magsimula sa + at country code.',
  },
  // The week grid — routines laid out by person and day.
  // Urdu: "the week's plan", because bare ہفتہ also reads as Saturday.
  'The week': { he: 'השבוע', ur: 'ہفتے کا پلان', tl: 'Ang linggo' },
  'What this house does, and on which day. Written once — it repeats itself.': {
    he: 'מה נעשה בבית, ובאיזה יום. נכתב פעם אחת — וחוזר על עצמו.',
    ur: 'یہ گھر کیا کرتا ہے، اور کس دن۔ ایک بار لکھیں — پھر خود دہرائے گا۔',
    tl: 'Ang ginagawa sa bahay, at sa anong araw. Isang beses isulat — uulit na ito.',
  },
  'Add someone to the team first.': {
    he: 'קודם צריך להוסיף מישהו לצוות.',
    ur: 'پہلے ٹیم میں کسی کو شامل کریں۔',
    tl: 'Magdagdag muna ng tao sa team.',
  },
  'e.g. Wash the car': {
    he: 'למשל: לשטוף את האוטו',
    ur: 'مثلاً: گاڑی دھونا',
    tl: 'hal. Hugasan ang kotse',
  },
  'Everything here reaches the person on the day, and is marked late if nobody does it. Nobody has to ask.':
    {
      he: 'כל מה שכאן מגיע לאדם ביום עצמו, ומסומן כאיחור אם אף אחד לא עשה. אף אחד לא צריך לשאול.',
      ur: 'یہاں کی ہر بات اُسی دن متعلقہ شخص تک پہنچتی ہے، اور نہ ہونے پر تاخیر شمار ہوتی ہے۔ کسی کو پوچھنا نہیں پڑتا۔',
      tl: 'Lahat dito ay dumarating sa tao sa mismong araw, at mamarkahang late kung walang gumawa. Hindi na kailangang magtanong.',
    },
  Sun: { he: 'א׳', ur: 'اتوار', tl: 'Lin' },
  Mon: { he: 'ב׳', ur: 'پیر', tl: 'Lun' },
  Tue: { he: 'ג׳', ur: 'منگل', tl: 'Mar' },
  Wed: { he: 'ד׳', ur: 'بدھ', tl: 'Miy' },
  Thu: { he: 'ה׳', ur: 'جمعرات', tl: 'Huw' },
  Fri: { he: 'ו׳', ur: 'جمعہ', tl: 'Biy' },
  Sat: { he: 'ש׳', ur: 'ہفتہ', tl: 'Sab' },
  Saved: { he: 'נשמר', ur: 'محفوظ', tl: 'Naka-save' },
  'Plan a task in your own words…': {
    he: 'תכנן משימה במילים שלך…',
    ur: 'اپنے الفاظ میں کام بتائیں…',
    tl: 'Mag-plano ng task sa sariling salita…',
  },
  Parse: { he: 'נתח', ur: 'تجزیہ', tl: 'I-parse' },
  Create: { he: 'צור', ur: 'بنائیں', tl: 'Lumikha' },
  'Speak instead': { he: 'דבר במקום', ur: 'بولیں', tl: 'Magsalita' },
  'Listening…': { he: 'מקשיב…', ur: 'سن رہا ہے…', tl: 'Nakikinig…' },
  'Drag to reorder · tap to edit': {
    he: 'גרור לסידור · הקש לעריכה',
    ur: 'ترتیب بدلیں · ترمیم کریں',
    tl: 'Pindutin para mag-edit',
  },
  Title: { he: 'כותרת', ur: 'عنوان', tl: 'Pamagat' },
  Description: { he: 'תיאור', ur: 'تفصیل', tl: 'Paglalarawan' },
  'When?': { he: 'מתי?', ur: 'کب؟', tl: 'Kailan?' },
  'Now (send)': { he: 'עכשיו (שלח)', ur: 'ابھی (بھیجیں)', tl: 'Ngayon (ipadala)' },
  Later: { he: 'מאוחר יותר', ur: 'بعد میں', tl: 'Mamaya' },
  Confidence: { he: 'ביטחון', ur: 'اعتماد', tl: 'Tiwala' },
  'Recognized intent': { he: 'כוונה שזוהתה', ur: 'پہچانی گئی نیت', tl: 'Natukoy na layunin' },
  'Create task': { he: 'צור משימה', ur: 'کام بنائیں', tl: 'Gumawa ng task' },
  'e.g. Pick the kids up at 16:00': {
    he: 'לדוגמה: לאסוף את הילדים ב־16:00',
    ur: 'مثال: 16:00 پر بچوں کو لیں',
    tl: 'hal. Sunduin ang mga bata ng 16:00',
  },
  'e.g. 24 Allenby St, Tel Aviv': {
    he: 'לדוגמה: אלנבי 24, תל אביב',
    ur: 'مثال: النبی اسٹریٹ 24، تل ابیب',
    tl: 'hal. 24 Allenby St, Tel Aviv',
  },
  'Send now': { he: 'שלח עכשיו', ur: 'ابھی بھیجیں', tl: 'Ipadala ngayon' },
  'Require proof when done': {
    he: 'דרוש הוכחה בסיום',
    ur: 'مکمل ہونے پر ثبوت درکار',
    tl: 'Kailangan ng patunay kapag tapos',
  },
  Photo: { he: 'תמונה', ur: 'تصویر', tl: 'Larawan' },
  Voice: { he: 'הקלטה', ur: 'آواز', tl: 'Boses' },
  Text: { he: 'טקסט', ur: 'متن', tl: 'Teksto' },
  'Add a photo or voice note from the task detail screen after creating.': {
    he: 'אפשר לצרף תמונה או הקלטה ממסך פרטי המשימה אחרי היצירה.',
    ur: 'تخلیق کے بعد ٹاسک کی تفصیل سے تصویر یا وائس نوٹ شامل کریں۔',
    tl: 'Magdagdag ng larawan o voice note mula sa task detail pagkatapos lumikha.',
  },
  Attachments: { he: 'קבצים מצורפים', ur: 'منسلکات', tl: 'Mga kalakip' },
  File: { he: 'קובץ', ur: 'فائل', tl: 'File' },
  'Speak as you would in real life. The AI figures out who, what, when, and proof.': {
    he: 'דבר כמו שהיית אומר. ה-AI יבין מי, מה, מתי, האם דרושה הוכחה.',
    ur: 'جیسے بات کرتے ہیں ویسے لکھیں۔ AI خود سمجھے گا کون، کیا، کب۔',
    tl: 'Magsulat tulad ng natural na salita. Hahanapin ng AI kung sino, ano, kailan.',
  },
  'No routines yet.': {
    he: 'אין עדיין שגרות.',
    ur: 'ابھی کوئی روٹین نہیں۔',
    tl: 'Wala pang rutina.',
  },
  Next: { he: 'הבא', ur: 'اگلا', tl: 'Susunod' },
  Paused: { he: 'מושהה', ur: 'روک دیا', tl: 'Naka-pause' },
  Resume: { he: 'המשך', ur: 'دوبارہ شروع', tl: 'Magpatuloy' },
  Pause: { he: 'השהה', ur: 'روک دیں', tl: 'I-pause' },
  'Skip next': { he: 'דלג על הבא', ur: 'اگلا چھوڑیں', tl: 'Laktawan susunod' },
  'New routine': { he: 'שגרה חדשה', ur: 'نئی روٹین', tl: 'Bagong rutina' },
  Daily: { he: 'יומי', ur: 'روزانہ', tl: 'Araw-araw' },
  'Weekdays (Sun–Thu)': {
    he: 'ימי חול (א-ה)',
    ur: 'ہفتے کے دن (اتوار-جمعرات)',
    tl: 'Linggo-Huwebes',
  },
  'Weekend (Fri–Sat)': { he: 'סוף שבוע (ו-ש)', ur: 'ویک اینڈ', tl: 'Sabado-Linggo' },
  'Once a week': { he: 'פעם בשבוע', ur: 'ہفتے میں ایک بار', tl: 'Isang beses sa linggo' },
  'Custom (RRULE)': { he: 'מותאם אישית (RRULE)', ur: 'حسب ضرورت (RRULE)', tl: 'Custom (RRULE)' },

  // Onboarding
  'Create your family': {
    he: 'צור את המשפחה שלך',
    ur: 'اپنا خاندان بنائیں',
    tl: 'Likhain ang iyong pamilya',
  },
  'e.g. The Cohen family': {
    he: 'לדוגמה: משפחת כהן',
    ur: 'مثال: کوہن خاندان',
    tl: 'hal. Pamilyang Cohen',
  },
  'How many family members?': {
    he: 'כמה בני משפחה?',
    ur: 'خاندان میں کتنے افراد ہیں؟',
    tl: 'Ilang miyembro ng pamilya?',
  },
  'Just me': { he: 'רק אני', ur: 'صرف میں', tl: 'Ako lang' },
  'Do you have pets?': {
    he: 'יש לך חיות מחמד?',
    ur: 'کیا آپ کے پاس پالتو جانور ہیں؟',
    tl: 'May alagang hayop ka ba?',
  },
  No: { he: 'לא', ur: 'نہیں', tl: 'Wala' },
  Dog: { he: 'כלב', ur: 'کتا', tl: 'Aso' },
  Cat: { he: 'חתול', ur: 'بلی', tl: 'Pusa' },
  Multiple: { he: 'כמה', ur: 'متعدد', tl: 'Marami' },
  Other: { he: 'אחר', ur: 'دیگر', tl: 'Iba' },
  'How many household staff?': {
    he: 'כמה עובדים בבית?',
    ur: 'گھر میں کتنے ملازم ہیں؟',
    tl: 'Ilang tauhan sa bahay?',
  },
  'Create family': { he: 'צור משפחה', ur: 'خاندان بنائیں', tl: 'Likhain ang pamilya' },

  // Quickstart
  Quickstart: { he: 'התחלה מהירה', ur: 'فوری شروعات', tl: 'Mabilis na simula' },
  'Add your first staff member': {
    he: 'הוסף עובד ראשון',
    ur: 'پہلا عملہ شامل کریں',
    tl: 'Magdagdag ng unang tauhan',
  },
  'Create first household task': {
    he: 'צור משימה ראשונה',
    ur: 'پہلا گھریلو کام بنائیں',
    tl: 'Gumawa ng unang gawain',
  },
  'Add a routine': { he: 'הוסף שגרה', ur: 'روٹین شامل کریں', tl: 'Magdagdag ng rutina' },
  'Optional context for the team': {
    he: 'הקשר לעובדים (לא חובה)',
    ur: 'ٹیم کے لیے اضافی تفصیل (اختیاری)',
    tl: 'Karagdagang detalye para sa team',
  },
  'Staff will see this task as cancelled. Add a reason if you like.': {
    he: 'העובד יראה שהמשימה בוטלה. אפשר להוסיף סיבה.',
    ur: 'عملہ دیکھے گا کہ کام منسوخ ہو گیا۔ وجہ شامل کر سکتے ہیں۔',
    tl: 'Makikita ng tauhan na kinansela ang gawain. Magdagdag ng dahilan kung gusto mo.',
  },
  'The task disappears from the dashboard. History stays intact.': {
    he: 'המשימה תיעלם מהדשבורד. ההיסטוריה נשמרת.',
    ur: 'کام ڈیش بورڈ سے ہٹ جائے گا۔ تاریخ محفوظ رہے گی۔',
    tl: 'Mawawala ang task sa dashboard. Mananatili ang kasaysayan.',
  },

  // Location / maps
  Address: { he: 'כתובת', ur: 'پتہ', tl: 'Address' },
  'Address (optional)': {
    he: 'כתובת (לא חובה)',
    ur: 'پتہ (اختیاری)',
    tl: 'Address (opsyonal)',
  },
  'Open in Google Maps': {
    he: 'פתח ב־Google Maps',
    ur: 'گوگل میپس میں کھولیں',
    tl: 'Buksan sa Google Maps',
  },
  'Open in Waze': { he: 'פתח ב־Waze', ur: 'ویز میں کھولیں', tl: 'Buksan sa Waze' },
  'Open in Apple Maps': {
    he: 'פתח ב־Apple Maps',
    ur: 'ایپل میپس میں کھولیں',
    tl: 'Buksan sa Apple Maps',
  },
  Navigate: { he: 'ניווט', ur: 'نیویگیٹ', tl: 'Mag-navigate' },
  'Open in…': { he: 'פתח ב…', ur: 'کھولیں…', tl: 'Buksan sa…' },
  'Default navigation app': {
    he: 'אפליקציית ניווט ברירת מחדל',
    ur: 'پہلے سے طے شدہ نیویگیشن ایپ',
    tl: 'Default na nabigasyon app',
  },
  'Used when you tap Navigate on a task.': {
    he: 'בשימוש כשמקישים על "ניווט" במשימה.',
    ur: 'جب آپ کسی کام پر "نیویگیٹ" پر ٹیپ کریں گے تو استعمال ہوگی۔',
    tl: 'Ginagamit kapag pinindot mo ang Navigate sa isang gawain.',
  },
  'Google Maps': { he: 'Google Maps', ur: 'گوگل میپس', tl: 'Google Maps' },
  Waze: { he: 'Waze', ur: 'ویز', tl: 'Waze' },
  'Apple Maps': { he: 'Apple Maps', ur: 'ایپل میپس', tl: 'Apple Maps' },

  // Co-owner invites
  'Invite a co-owner': {
    he: 'הזמן בעלים שותף',
    ur: 'شریک مالک کو مدعو کریں',
    tl: 'Mag-imbita ng katuwang na may-ari',
  },
  'Invite your spouse or partner so they can assign tasks too.': {
    he: 'הזמן את בן/בת הזוג שיוכלו גם להקצות משימות.',
    ur: 'اپنے ساتھی کو مدعو کریں تاکہ وہ بھی کام تفویض کر سکیں۔',
    tl: 'Imbitahan ang asawa o kapareha mo para makapag-assign din.',
  },
  'Generate invite link': {
    he: 'צור קישור הזמנה',
    ur: 'دعوتی لنک بنائیں',
    tl: 'Gumawa ng link ng paanyaya',
  },
  'Copy link': { he: 'העתק קישור', ur: 'لنک کاپی کریں', tl: 'Kopyahin ang link' },
  'Link copied': { he: 'הועתק', ur: 'کاپی ہو گیا', tl: 'Nakopya' },
  'Share via WhatsApp': {
    he: 'שלח ב־WhatsApp',
    ur: 'واٹس ایپ پر بھیجیں',
    tl: 'Ipadala sa WhatsApp',
  },
  'Pending invites': { he: 'הזמנות פתוחות', ur: 'زیر التواء دعوتیں', tl: 'Mga paanyaya' },
  Revoke: { he: 'בטל', ur: 'منسوخ', tl: 'Bawiin' },
  Expires: { he: 'תוקף עד', ur: 'ختم ہوگی', tl: 'Mawawalan ng bisa' },
  "You've been invited to join {{name}}": {
    he: 'הוזמנת להצטרף ל{{name}}',
    ur: '{{name}} میں شامل ہونے کی دعوت',
    tl: 'Inimbita ka sa {{name}}',
  },
  'Invited by {{name}}': {
    he: 'הוזמנת על־ידי {{name}}',
    ur: '{{name}} نے مدعو کیا',
    tl: 'Inimbita ni {{name}}',
  },
  'Accept invite': { he: 'אישור הצטרפות', ur: 'دعوت قبول کریں', tl: 'Tanggapin ang paanyaya' },

  // Notifications (browser web push)
  Notifications: { he: 'התראות', ur: 'اطلاعات', tl: 'Mga abiso' },
  'Get a browser banner when tasks need your attention.': {
    he: 'קבל באנר בדפדפן כשמשימה דורשת תשומת לב.',
    ur: 'جب کام پر توجہ درکار ہو تو براؤزر بینر حاصل کریں۔',
    tl: 'Tumanggap ng browser banner kapag may gawaing kailangan.',
  },
  'Enable notifications': {
    he: 'הפעל התראות',
    ur: 'اطلاعات فعال کریں',
    tl: 'I-enable ang mga abiso',
  },
  'Notifications enabled': {
    he: 'התראות פעילות',
    ur: 'اطلاعات فعال',
    tl: 'Aktibo ang mga abiso',
  },
  Disable: { he: 'בטל', ur: 'غیر فعال کریں', tl: 'I-disable' },
  'This browser does not support push notifications.': {
    he: 'הדפדפן לא תומך בהתראות דחיפה.',
    ur: 'یہ براؤزر پش اطلاعات کی حمایت نہیں کرتا۔',
    tl: 'Hindi sinusuportahan ng browser na ito ang push notifications.',
  },
  'Notifications are blocked. Allow them in your browser settings.': {
    he: 'התראות חסומות. אפשר אותן בהגדרות הדפדפן.',
    ur: 'اطلاعات بلاک ہیں۔ براؤزر کی ترتیبات میں اجازت دیں۔',
    tl: 'Naka-block ang notifications. Payagan ito sa browser settings.',
  },

  // Staff invites (shareable link, WhatsApp-friendly)
  'Invite staff member': {
    he: 'הזמן עובד',
    ur: 'عملہ کو مدعو کریں',
    tl: 'Mag-imbita ng tauhan',
  },
  'Invite via link': {
    he: 'הזמן עם קישור',
    ur: 'لنک سے مدعو کریں',
    tl: 'Mag-imbita gamit ang link',
  },
  'Send invite link': {
    he: 'שלח קישור הזמנה',
    ur: 'دعوتی لنک بھیجیں',
    tl: 'Magpadala ng link ng paanyaya',
  },
  'Generate a one-time link so this staff member can set their own password.': {
    he: 'צור קישור חד-פעמי שיאפשר לעובד להגדיר סיסמה משלו.',
    ur: 'ایک بار کا لنک بنائیں تاکہ یہ عملہ اپنا پاس ورڈ خود طے کر سکے۔',
    tl: 'Gumawa ng isang beses na link para makapag-set ng sariling password ang tauhang ito.',
  },
  'You have been invited to join the household. Set up your account here:': {
    he: 'הוזמנת להצטרף למשק הבית. הגדר את חשבונך כאן:',
    ur: 'آپ کو گھرانے میں شامل ہونے کی دعوت ہے۔ اپنا اکاؤنٹ یہاں ترتیب دیں:',
    tl: 'Inimbita ka sa sambahayan. I-set up ang account mo dito:',
  },
  'Welcome, {{name}}': {
    he: 'ברוך הבא, {{name}}',
    ur: 'خوش آمدید، {{name}}',
    tl: 'Maligayang pagdating, {{name}}',
  },
  "You're joining {{name}} as staff": {
    he: 'אתה מצטרף ל{{name}} כעובד',
    ur: 'آپ {{name}} میں عملہ کے طور پر شامل ہو رہے ہیں',
    tl: 'Sasali ka sa {{name}} bilang tauhan',
  },
  'Choose a password': {
    he: 'בחר סיסמה',
    ur: 'پاس ورڈ منتخب کریں',
    tl: 'Pumili ng password',
  },
  'Choose a username': { he: 'בחר שם משתמש', ur: 'صارف نام منتخب کریں', tl: 'Pumili ng username' },

  // Staff first-time tour (3-screen overlay shown once on first /today visit).
  // Tone: spoken to a Filipina housekeeper who has never used a task app.
  // Plain, gender-neutral plural in Hebrew. No emojis (brand rule).
  "Here's how famanage works for you.": {
    he: 'ככה famanage עובד בשבילך.',
    ur: 'famanage آپ کے لیے ایسے کام کرتا ہے۔',
    tl: 'Ganito gumagana ang famanage para sa iyo.',
  },
  'Tap a task when you start it': {
    he: 'הקש על משימה כשמתחילים אותה',
    ur: 'جب کام شروع کریں تو اس پر ٹیپ کریں',
    tl: 'Pindutin ang gawain kapag sisimulan mo',
  },
  'When you\'re working on something, tap "I\'m starting". Your owner sees instantly.': {
    he: 'כשעובדים על משהו, להקיש על "מתחיל". המעסיק רואה מיד.',
    ur: 'جب کوئی کام کر رہے ہوں تو "شروع کر رہا ہوں" پر ٹیپ کریں۔ مالک کو فوراً نظر آئے گا۔',
    tl: 'Kapag may ginagawa ka, pindutin ang "Sisimulan ko". Agad na makikita ng may-ari.',
  },
  "Send proof when you're done": {
    he: 'שולחים הוכחה כשמסיימים',
    ur: 'مکمل ہونے پر ثبوت بھیجیں',
    tl: 'Magpadala ng patunay kapag tapos na',
  },
  'Take a photo or record a voice note when you finish. Your owner approves it.': {
    he: 'מצלמים תמונה או מקליטים הודעה קולית בסיום. המעסיק מאשר.',
    ur: 'مکمل کرنے پر تصویر لیں یا وائس نوٹ ریکارڈ کریں۔ مالک منظوری دے گا۔',
    tl: 'Kumuha ng larawan o mag-record ng voice note kapag tapos ka na. Aaprubahan ng may-ari mo.',
  },
  'Got it': { he: 'הבנתי', ur: 'سمجھ گیا', tl: 'Nakuha ko' },
  'Skip tour': { he: 'דלג על הסבר', ur: 'ٹور چھوڑیں', tl: 'Laktawan ang tour' },

  // Tasks list page
  Completed: { he: 'הושלמו', ur: 'مکمل', tl: 'Tapos' },
  All: { he: 'הכל', ur: 'سب', tl: 'Lahat' },
  'No tasks here.': { he: 'אין משימות כאן.', ur: 'یہاں کوئی کام نہیں۔', tl: 'Walang gawain dito.' },
  'Search title, description, address…': {
    he: 'חיפוש כותרת, תיאור, כתובת…',
    ur: 'عنوان، تفصیل، پتہ تلاش کریں…',
    tl: 'Hanapin sa pamagat, paglalarawan, address…',
  },
  'All staff': { he: 'כל הצוות', ur: 'تمام عملہ', tl: 'Lahat ng tauhan' },
  task: { he: 'משימה', ur: 'کام', tl: 'gawain' },
  tasks: { he: 'משימות', ur: 'کام', tl: 'mga gawain' },

  // Magic moment: translation badge after task create
  'Sent to {{name}}': {
    he: 'נשלח ל{{name}}',
    ur: '{{name}} کو بھیجا',
    tl: 'Ipinadala kay {{name}}',
  },
  '{{name}} will see this in {{lang}}': {
    he: '{{name}} יראה את זה ב{{lang}}',
    ur: '{{name}} اسے {{lang}} میں دیکھے گا',
    tl: 'Makikita ito ni {{name}} sa {{lang}}',
  },
  'Translating into {{lang}}…': {
    he: 'מתרגם ל{{lang}}…',
    ur: '{{lang}} میں ترجمہ ہو رہا ہے…',
    tl: 'Sinasalin sa {{lang}}…',
  },
  'View task': { he: 'פתח משימה', ur: 'کام دیکھیں', tl: 'Tingnan ang gawain' },
  'Create another': { he: 'צור משימה נוספת', ur: 'دوسرا بنائیں', tl: 'Gumawa ulit' },

  // Receipt-scan loop (staff scans a shop receipt → AI proposes a task/routine)
  'Scan a receipt': {
    he: 'סרוק קבלה',
    ur: 'رسید اسکین کریں',
    tl: 'Mag-scan ng resibo',
  },
  'Snap a photo · we propose a task or routine for your owner.': {
    he: 'צלם תמונה · נציע משימה או שגרה למעסיק שלך.',
    ur: 'تصویر لیں · ہم آپ کے مالک کے لیے کام یا روٹین تجویز کریں گے۔',
    tl: 'Kumuha ng larawan · magmumungkahi kami ng task o rutina sa may-ari mo.',
  },
  'Open camera': { he: 'פתח מצלמה', ur: 'کیمرہ کھولیں', tl: 'Buksan ang kamera' },
  'Take photo': { he: 'צלם תמונה', ur: 'تصویر لیں', tl: 'Kumuha ng larawan' },
  'Choose from gallery': {
    he: 'בחר מהגלריה',
    ur: 'گیلری سے منتخب کریں',
    tl: 'Pumili mula sa gallery',
  },
  'Type it out instead': {
    he: 'הקלד במקום',
    ur: 'اس کے بجائے ٹائپ کریں',
    tl: 'I-type na lang',
  },
  'Describe what you bought': {
    he: 'תאר מה קנית',
    ur: 'بتائیں آپ نے کیا خریدا',
    tl: 'Ilarawan ang binili mo',
  },
  'e.g. 3 bags of chlorine and a pool brush from Carrefour, 657 AED': {
    he: 'לדוגמא 3 שקיות כלור ומברשת לבריכה מקרפור, 657 AED',
    ur: 'مثلاً کارفور سے 3 تھیلے کلورین اور پول برش، 657 AED',
    tl: 'hal. 3 sako ng chlorine at pool brush sa Carrefour, 657 AED',
  },
  Submit: { he: 'שלח', ur: 'جمع کریں', tl: 'Isumite' },
  'Take a photo, pick from gallery, or type it out.': {
    he: 'צלם תמונה, בחר מהגלריה, או הקלד את זה.',
    ur: 'تصویر لیں، گیلری سے منتخب کریں، یا ٹائپ کریں۔',
    tl: 'Kumuha ng larawan, pumili sa gallery, o i-type.',
  },
  'We will read the items, ask one question, and propose a task or routine for your owner to approve.':
    {
      he: 'נקרא את הפריטים, נשאל שאלה אחת, ונציע משימה או שגרה לאישור המעסיק.',
      ur: 'ہم اشیاء پڑھیں گے، ایک سوال پوچھیں گے، اور آپ کے مالک کی منظوری کے لیے کام یا روٹین تجویز کریں گے۔',
      tl: 'Babasahin namin ang mga item, isang tanong lang, at imumungkahi sa may-ari ang task o rutina.',
    },
  'We read the receipt for you.': {
    he: 'אנחנו קוראים את הקבלה בשבילך.',
    ur: 'ہم آپ کے لیے رسید پڑھتے ہیں۔',
    tl: 'Binabasa namin ang resibo para sa iyo.',
  },
  'Check details': { he: 'בדוק פרטים', ur: 'تفصیلات چیک کریں', tl: 'Suriin ang detalye' },
  'Looks right': { he: 'נראה נכון', ur: 'ٹھیک لگ رہا ہے', tl: 'Tama naman' },
  'Good to know': { he: 'טוב לדעת', ur: 'جاننا اچھا ہے', tl: 'Magandang malaman' },
  'Add as routine': { he: 'הוסף כשגרה', ur: 'روٹین کے طور پر شامل کریں', tl: 'Gawing routine' },
  'Just save the receipt': {
    he: 'רק שמור את הקבלה',
    ur: 'بس رسید محفوظ کریں',
    tl: 'I-save lang ang resibo',
  },
  'The receipt is saved under Expenses': {
    he: 'הקבלה נשמרת תחת הוצאות',
    ur: 'رسید اخراجات میں محفوظ ہے',
    tl: 'Naka-save ang resibo sa Expenses',
  },
  'Type your answer or use voice…': {
    he: 'הקלד תשובה או דבר…',
    ur: 'جواب لکھیں یا آواز استعمال کریں…',
    tl: 'I-type ang sagot mo o magsalita…',
  },
  'Transcribing…': { he: 'ממיר לטקסט…', ur: 'متن میں بدل رہا ہے…', tl: 'Iniipatutugma…' },
  'Could not read this receipt': {
    he: 'לא הצלחנו לקרוא את הקבלה הזו',
    ur: 'یہ رسید پڑھنے میں ناکام',
    tl: 'Hindi mabasa ang resibong ito',
  },
  'Try a clearer photo or a different angle.': {
    he: 'נסה תמונה ברורה יותר או זווית אחרת.',
    ur: 'صاف تصویر یا مختلف زاویہ سے کوشش کریں۔',
    tl: 'Subukan ang mas malinaw na larawan o ibang anggulo.',
  },
  'Try again': { he: 'נסה שוב', ur: 'دوبارہ کوشش', tl: 'Subukan muli' },
  'Retry photo': { he: 'צלם מחדש', ur: 'دوبارہ تصویر لیں', tl: 'Mag-photo muli' },
  'Ready to send to owner': {
    he: 'מוכן לשליחה למעסיק',
    ur: 'مالک کو بھیجنے کے لیے تیار',
    tl: 'Handa nang ipadala sa may-ari',
  },
  'Send to owner for approval': {
    he: 'שלח למעסיק לאישור',
    ur: 'منظوری کے لیے مالک کو بھیجیں',
    tl: 'Ipadala sa may-ari para sa pag-apruba',
  },
  'Proposed task': { he: 'משימה מוצעת', ur: 'تجویز کردہ کام', tl: 'Mungkahing gawain' },
  'Proposed routine': { he: 'שגרה מוצעת', ur: 'تجویز کردہ روٹین', tl: 'Mungkahing rutina' },
  // Two-phase receipt flow: OCR → confirm → analyze.
  'Reading…': { he: 'קורא…', ur: 'پڑھ رہا ہے…', tl: 'Binabasa…' },
  'We will read the receipt and show what we got.': {
    he: 'נקרא את הקבלה ונציג מה קלטנו.',
    ur: 'ہم رسید پڑھیں گے اور دکھائیں گے۔',
    tl: 'Babasahin namin ang resibo at ipapakita.',
  },
  'Here is what I read': {
    he: 'הנה מה שקראתי',
    ur: 'یہ ہے جو میں نے پڑھا',
    tl: 'Ito ang nabasa ko',
  },
  'Confirm reading': {
    he: 'אישור הקריאה',
    ur: 'پڑھنے کی تصدیق کریں',
    tl: 'Kumpirmahin ang nabasa',
  },
  'Retake photo': {
    he: 'צלם מחדש',
    ur: 'دوبارہ تصویر لیں',
    tl: 'Kumuha muli ng larawan',
  },
  'Thinking…': { he: 'חושב…', ur: 'سوچ رہا ہے…', tl: 'Nag-iisip…' },
  'No line items extracted.': {
    he: 'לא חולצו פריטים.',
    ur: 'کوئی آئٹم نہیں ملا۔',
    tl: 'Walang nakuhang item.',
  },
  'Sent to your owner': {
    he: 'נשלח למעסיק',
    ur: 'مالک کو بھیج دیا',
    tl: 'Ipinadala sa may-ari mo',
  },
  'Nice work': { he: 'עבודה יפה', ur: 'شاندار کام', tl: 'Magaling' },
  'They will see the proposal and approve or reject it.': {
    he: 'הם יראו את ההצעה ויאשרו או ידחו.',
    ur: 'وہ تجویز دیکھیں گے اور منظوری دیں گے یا رد کریں گے۔',
    tl: 'Makikita nila ang mungkahi at aaprubahan o tatanggihan.',
  },
  Dismiss: { he: 'הסר', ur: 'بند', tl: 'I-dismiss' },
  'Receipt proposals': {
    he: 'הצעות מקבלות',
    ur: 'رسید کی تجاویز',
    tl: 'Mga mungkahi mula sa resibo',
  },
  Task: { he: 'משימה', ur: 'کام', tl: 'Gawain' },
  Routine: { he: 'שגרה', ur: 'روٹین', tl: 'Rutina' },
  '(empty proposal)': {
    he: '(הצעה ריקה)',
    ur: '(خالی تجویز)',
    tl: '(walang laman)',
  },

  // Monthly expenses dashboard (Money screen + Owner home tile)
  'This month': { he: 'החודש', ur: 'اس مہینے', tl: 'Ngayong buwan' },
  'across {{n}} receipts': {
    he: 'מעל {{n}} קבלות',
    ur: '{{n}} رسیدوں پر',
    tl: 'sa {{n}} resibo',
  },
  'No spend tracked yet': {
    he: 'עדיין לא נרשמו הוצאות',
    ur: 'ابھی تک کوئی خرچ ریکارڈ نہیں',
    tl: 'Wala pang naitalang gastos',
  },
  'Previous month': {
    he: 'חודש קודם',
    ur: 'پچھلا مہینہ',
    tl: 'Nakaraang buwan',
  },
  'Next month': { he: 'חודש הבא', ur: 'اگلا مہینہ', tl: 'Susunod na buwan' },
  'Spent in {{month}}': {
    he: 'הוצאה ב{{month}}',
    ur: '{{month}} میں خرچ',
    tl: 'Gastos sa {{month}}',
  },
  'By category': {
    he: 'לפי קטגוריה',
    ur: 'زمرے کے لحاظ سے',
    tl: 'Ayon sa kategorya',
  },
  'By staff': {
    he: 'לפי עובד',
    ur: 'عملہ کے لحاظ سے',
    tl: 'Ayon sa tauhan',
  },
  'Recent receipts': {
    he: 'קבלות אחרונות',
    ur: 'حالیہ رسیدیں',
    tl: 'Mga huling resibo',
  },
  'No receipts in this month yet.': {
    he: 'אין קבלות בחודש הזה.',
    ur: 'اس مہینے کوئی رسید نہیں۔',
    tl: 'Walang resibo sa buwang ito.',
  },
  'Multiple currencies detected — showing {{currency}} only.': {
    he: 'התגלו כמה מטבעות — מציג רק {{currency}}.',
    ur: 'متعدد کرنسیاں — صرف {{currency}} دکھائی جا رہی ہیں۔',
    tl: 'Maraming currency — {{currency}} lang ang ipinapakita.',
  },
  '{{count}} receipts': {
    he: '{{count}} קבלות',
    ur: '{{count}} رسیدیں',
    tl: '{{count}} resibo',
  },
  Pool: { he: 'בריכה', ur: 'تالاب', tl: 'Pool' },
  Cleaning: { he: 'ניקיון', ur: 'صفائی', tl: 'Paglilinis' },
  Pet: { he: 'חיית מחמד', ur: 'پالتو جانور', tl: 'Alagang hayop' },
  Groceries: { he: 'מצרכים', ur: 'گروسری', tl: 'Pamimili' },
  Baby: { he: 'תינוק', ur: 'بچہ', tl: 'Sanggol' },
  Pharmacy: { he: 'בית מרקחת', ur: 'فارمیسی', tl: 'Botika' },
  Fuel: { he: 'דלק', ur: 'ایندھن', tl: 'Gasolina' },
  Reimbursements: {
    he: 'החזרים',
    ur: 'رقم کی واپسی',
    tl: 'Mga reimbursement',
  },
  'No reimbursements yet.': {
    he: 'אין החזרים כרגע.',
    ur: 'ابھی کوئی واپسی نہیں۔',
    tl: 'Wala pang reimbursement.',
  },
  Submitted: { he: 'הוגש', ur: 'جمع کرایا', tl: 'Ipinasa' },
  Paid: { he: 'שולם', ur: 'ادا کیا', tl: 'Nabayaran' },
  'Mark paid': { he: 'סמן ששולם', ur: 'ادا کیا گیا نشان زد کریں', tl: 'I-mark na bayad' },
  'Cash flow': { he: 'תזרים כסף', ur: 'نقد بہاؤ', tl: 'Daloy ng pera' },
  'Cash given': { he: 'מזומן שניתן', ur: 'دی گئی نقدی', tl: 'Cash na ibinigay' },
  'No cash dispensed yet.': {
    he: 'לא ניתן מזומן עדיין.',
    ur: 'ابھی تک کوئی نقدی نہیں دی گئی۔',
    tl: 'Wala pang cash na ibinigay.',
  },
  Dispensed: { he: 'ניתן', ur: 'دی گئی', tl: 'Ibinigay' },
  Cash: { he: 'מזומן', ur: 'نقد', tl: 'Cash' },
  Card: { he: 'כרטיס', ur: 'کارڈ', tl: 'Card' },
  'Payment method?': {
    he: 'דרך תשלום?',
    ur: 'ادائیگی کا طریقہ؟',
    tl: 'Paraan ng bayad?',
  },
  Receipt: { he: 'קבלה', ur: 'رسید', tl: 'Resibo' },
  Items: { he: 'פריטים', ur: 'اشیاء', tl: 'Mga item' },

  // Magic moment: WhatsApp-style read receipts
  Read: { he: 'נקרא', ur: 'پڑھ لیا', tl: 'Nabasa' },
  Delivered: { he: 'נמסר', ur: 'پہنچا', tl: 'Naipadala' },
  'First seen by {{name}} at {{time}}': {
    he: 'נראה לראשונה על־ידי {{name}} ב־{{time}}',
    ur: 'پہلی بار {{name}} نے {{time}} پر دیکھا',
    tl: 'Unang nakita ni {{name}} sa {{time}}',
  },

  // === Payroll ===
  // Deliberately plain words in every language: this screen decides what a
  // person takes home, and nobody should have to guess what a label means.
  Payroll: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  Settings: { he: 'הגדרות', ur: 'ترتیبات', tl: 'Mga setting' },
  'Payroll settings': { he: 'הגדרות שכר', ur: 'تنخواہ کی ترتیبات', tl: 'Mga setting ng sahod' },
  'Not run yet': { he: 'טרם הופעל', ur: 'ابھی نہیں چلایا', tl: 'Hindi pa pinatakbo' },
  Shifts: { he: 'משמרות', ur: 'شفٹیں', tl: 'Mga shift' },
  'Plan a shift': { he: 'תכנן משמרת', ur: 'شفٹ کی منصوبہ بندی کریں', tl: 'Magplano ng shift' },
  'On call': { he: 'כוננות', ur: 'آن کال', tl: 'On call' },
  Notes: { he: 'הערות', ur: 'نوٹس', tl: 'Mga tala' },
  'End must be after start.': {
    he: 'הסיום חייב להיות אחרי ההתחלה.',
    ur: 'اختتام شروع کے بعد ہونا چاہیے۔',
    tl: 'Dapat pagkatapos ng simula ang katapusan.',
  },
  Shift: { he: 'משמרת', ur: 'شفٹ', tl: 'Shift' },
  on_time: { he: 'בזמן', ur: 'وقت پر', tl: 'Sa oras' },
  late: { he: 'איחור', ur: 'دیر', tl: 'Huli' },
  absent: { he: 'נעדר', ur: 'غائب', tl: 'Absent' },
  early_departure: { he: 'עזב מוקדם', ur: 'جلدی روانہ', tl: 'Maagang umalis' },
  worked_unscheduled: {
    he: 'עבד מחוץ למשמרת',
    ur: 'غیر شیڈول کام',
    tl: 'Nagtrabaho nang walang iskedyul',
  },
  upcoming: { he: 'קרוב', ur: 'آنے والا', tl: 'Paparating' },
  Features: { he: 'יכולות', ur: 'خصوصیات', tl: 'Mga feature' },
  'Switch a capability off for this household without waiting for an update.': {
    he: 'כבה יכולת במשק הבית הזה בלי לחכות לעדכון.',
    ur: 'اپڈیٹ کا انتظار کیے بغیر اس گھرانے کی صلاحیت بند کریں۔',
    tl: 'I-off ang kakayahan para sa sambahayang ito nang hindi hinihintay ang update.',
  },
  'Task advisor': { he: 'יועץ המשימות', ur: 'کام کا مشیر', tl: 'Tagapayo ng gawain' },
  'Suggests a cadence and pro checklist while you type a task title.': {
    he: 'מציע תדירות וצ׳ק-ליסט מקצועי בזמן שכותבים כותרת משימה.',
    ur: 'کام کا عنوان لکھتے وقت تعدد اور پروفیشنل چیک لسٹ تجویز کرتا ہے۔',
    tl: 'Nagmumungkahi ng kadalasan at pro checklist habang ta-type ng pamagat ng gawain.',
  },
  'Lets staff time each task, so you can see how long the work takes.': {
    he: 'מאפשר לסגל למדוד זמן לכל משימה — כדי לראות כמה העבודה אורכת.',
    ur: 'عملہ ہر کام کا وقت ناپ سکتا ہے، تاکہ آپ دیکھ سکیں کام میں کتنا وقت لگتا ہے۔',
    tl: 'Nagbibigay-daan sa staff na i-time ang bawat gawain, para makita kung gaano katagal ang trabaho.',
  },
  'Translate on tap': { he: 'תרגום בלחיצה', ur: 'ایک ٹیپ پر ترجمہ', tl: 'Isalin sa isang tap' },
  'The translate button on tasks and messages.': {
    he: 'כפתור התרגום על משימות והודעות.',
    ur: 'کاموں اور پیغامات پر ترجمہ کا بٹن۔',
    tl: 'Ang button ng pagsasalin sa mga gawain at mensahe.',
  },
  'Time this week': { he: 'זמן השבוע', ur: 'اس ہفتے کا وقت', tl: 'Oras ngayong linggo' },
  'Timer running': { he: 'הטיימר רץ', ur: 'ٹائمر چل رہا ہے', tl: 'Tumatakbo ang timer' },
  'Time on this task': { he: 'זמן על המשימה', ur: 'اس کام پر وقت', tl: 'Oras sa gawaing ito' },
  running: { he: 'רץ', ur: 'چل رہا ہے', tl: 'tumatakbo' },
  Finalized: { he: 'סופי', ur: 'حتمی', tl: 'Pinal' },
  Ready: { he: 'מוכן', ur: 'تیار', tl: 'Handa na' },
  'Generate payroll': { he: 'הפק שכר', ur: 'تنخواہ بنائیں', tl: 'Gumawa ng sahod' },
  Recalculate: { he: 'חשב מחדש', ur: 'دوبارہ حساب', tl: 'Kalkulahin muli' },
  Finalize: { he: 'סגור סופית', ur: 'حتمی کریں', tl: 'Tapusin' },
  'Discard draft': { he: 'מחק טיוטה', ur: 'مسودہ ضائع کریں', tl: 'Itapon ang burador' },
  'Salaries paid for this month.': {
    he: 'המשכורות שולמו החודש.',
    ur: 'اس مہینے کی تنخواہیں ادا ہو گئیں۔',
    tl: 'Nabayaran na ang sahod ngayong buwan.',
  },
  'Pay terms are missing': {
    he: 'חסרים תנאי שכר',
    ur: 'تنخواہ کی شرائط موجود نہیں',
    tl: 'Walang kasunduan sa sahod',
  },
  'These people are not on payroll yet — set their salary to include them.': {
    he: 'האנשים האלה עדיין לא בשכר — קבע להם משכורת כדי לכלול אותם.',
    ur: 'یہ لوگ ابھی تنخواہ میں شامل نہیں — انہیں شامل کرنے کے لیے تنخواہ مقرر کریں۔',
    tl: 'Wala pa sila sa payroll — itakda ang sahod para maisama sila.',
  },
  'Set pay for {{name}}': {
    he: 'קבע שכר ל־{{name}}',
    ur: '{{name}} کی تنخواہ مقرر کریں',
    tl: 'Itakda ang sahod ni {{name}}',
  },
  'Take-home total for {{month}}': {
    he: 'סה״כ נטו ל־{{month}}',
    ur: '{{month}} کے لیے کل خالص',
    tl: 'Kabuuang uuwiin para sa {{month}}',
  },
  '{{count}} people': { he: '{{count}} אנשים', ur: '{{count}} افراد', tl: '{{count}} tao' },
  'gross {{amount}}': { he: 'ברוטו {{amount}}', ur: 'مجموعی {{amount}}', tl: 'gross {{amount}}' },
  '{{amount}} goes to loan repayment': {
    he: '{{amount}} הולך להחזר הלוואה',
    ur: '{{amount}} قرض کی واپسی میں جاتا ہے',
    tl: '{{amount}} ang napupunta sa bayad-utang',
  },
  'This month, person by person': {
    he: 'החודש, אדם אחרי אדם',
    ur: 'اس مہینے، ہر فرد الگ الگ',
    tl: 'Ngayong buwan, isa-isa',
  },
  'Nobody is on payroll yet. Set someone’s pay terms to start.': {
    he: 'אף אחד עדיין לא בשכר. קבע תנאי שכר למישהו כדי להתחיל.',
    ur: 'ابھی کوئی تنخواہ میں نہیں۔ شروع کرنے کے لیے کسی کی تنخواہ مقرر کریں۔',
    tl: 'Wala pang nasa payroll. Magtakda ng sahod para magsimula.',
  },
  'per month': { he: 'לחודש', ur: 'ماہانہ', tl: 'kada buwan' },
  'per day': { he: 'ליום', ur: 'روزانہ', tl: 'kada araw' },
  'per hour': { he: 'לשעה', ur: 'فی گھنٹہ', tl: 'kada oras' },
  'overtime ×{{x}}': {
    he: 'שעות נוספות ×{{x}}',
    ur: 'اوور ٹائم ×{{x}}',
    tl: 'overtime ×{{x}}',
  },
  'take-home': { he: 'נטו', ur: 'خالص', tl: 'uuwiin' },
  'Show breakdown': { he: 'הצג פירוט', ur: 'تفصیل دکھائیں', tl: 'Ipakita ang detalye' },
  'Hide breakdown': { he: 'הסתר פירוט', ur: 'تفصیل چھپائیں', tl: 'Itago ang detalye' },
  'Base pay': { he: 'שכר בסיס', ur: 'بنیادی تنخواہ', tl: 'Batayang sahod' },
  Overtime: { he: 'שעות נוספות', ur: 'اوور ٹائم', tl: 'Overtime' },
  Holiday: { he: 'חג', ur: 'تعطیل', tl: 'Pista opisyal' },
  Bonus: { he: 'בונוס', ur: 'بونس', tl: 'Bonus' },
  Tip: { he: 'טיפ', ur: 'ٹپ', tl: 'Tip' },
  Gross: { he: 'ברוטו', ur: 'مجموعی', tl: 'Gross' },
  'Take-home': { he: 'נטו לתשלום', ur: 'خالص وصول', tl: 'Uuwiing halaga' },
  Deduction: { he: 'ניכוי', ur: 'کٹوتی', tl: 'Bawas' },
  Deductions: { he: 'ניכויים', ur: 'کٹوتیاں', tl: 'Mga bawas' },
  'Unpaid absence': {
    he: 'היעדרות ללא תשלום',
    ur: 'بلا معاوضہ غیر حاضری',
    tl: 'Absent na walang bayad',
  },
  'Advance already paid': {
    he: 'מקדמה ששולמה כבר',
    ur: 'پیشگی جو ادا ہو چکی',
    tl: 'Paunang naibigay na',
  },
  'Salary advance already given': {
    he: 'מקדמת משכורת שכבר ניתנה',
    ur: 'تنخواہ کی پیشگی جو دی جا چکی',
    tl: 'Paunang sahod na naibigay na',
  },
  'Loan repayment': { he: 'החזר הלוואה', ur: 'قرض کی واپسی', tl: 'Bayad sa utang' },
  Loan: { he: 'הלוואה', ur: 'قرض', tl: 'Utang' },
  left: { he: 'נותר', ur: 'باقی', tl: 'natitira' },
  mo: { he: 'חודש', ur: 'ماہ', tl: 'buwan' },
  'Set plan': { he: 'קבע תוכנית', ur: 'منصوبہ بنائیں', tl: 'Magtakda ng plano' },
  'Repayment plan': { he: 'תוכנית החזר', ur: 'واپسی کا منصوبہ', tl: 'Plano ng pagbabayad' },
  Outstanding: { he: 'יתרה', ur: 'باقی رقم', tl: 'Natitirang utang' },
  'Take off each payslip': {
    he: 'נכה מכל תלוש',
    ur: 'ہر تنخواہ سے کاٹیں',
    tl: 'Ibawas sa bawat payslip',
  },
  'Leave empty to settle by hand': {
    he: 'השאר ריק כדי לסגור ידנית',
    ur: 'ہاتھ سے طے کرنے کے لیے خالی چھوڑیں',
    tl: 'Iwanang blangko para manwal na ayusin',
  },
  'Paid off in about {{n}} months.': {
    he: 'ייסגר בערך תוך {{n}} חודשים.',
    ur: 'تقریباً {{n}} مہینوں میں ادا ہو جائے گا۔',
    tl: 'Mababayaran sa humigit-kumulang {{n}} buwan.',
  },
  'Deduct automatically every payroll': {
    he: 'נכה אוטומטית בכל תלוש',
    ur: 'ہر تنخواہ پر خود بخود کاٹیں',
    tl: 'Awtomatikong ibawas kada sahod',
  },
  'Log hours': { he: 'רשום שעות', ur: 'گھنٹے درج کریں', tl: 'Itala ang oras' },
  'Log my hours': { he: 'רשום את השעות שלי', ur: 'میرے گھنٹے درج کریں', tl: 'Itala ang oras ko' },
  'Hours logged': { he: 'שעות שנרשמו', ur: 'درج شدہ گھنٹے', tl: 'Mga naitalang oras' },
  'No hours logged this month.': {
    he: 'לא נרשמו שעות החודש.',
    ur: 'اس مہینے کوئی گھنٹے درج نہیں ہوئے۔',
    tl: 'Walang naitalang oras ngayong buwan.',
  },
  Hours: { he: 'שעות', ur: 'گھنٹے', tl: 'Mga oras' },
  'Regular hours': { he: 'שעות רגילות', ur: 'عام گھنٹے', tl: 'Karaniwang oras' },
  'Sick leave': { he: 'מחלה', ur: 'بیماری کی چھٹی', tl: 'Sick leave' },
  Vacation: { he: 'חופשה', ur: 'چھٹی', tl: 'Bakasyon' },
  'Bonus / deduction': { he: 'בונוס / ניכוי', ur: 'بونس / کٹوتی', tl: 'Bonus / bawas' },
  'Change pay': { he: 'שנה שכר', ur: 'تنخواہ بدلیں', tl: 'Baguhin ang sahod' },
  'Payroll history': { he: 'היסטוריית שכר', ur: 'تنخواہ کی تاریخ', tl: 'Kasaysayan ng sahod' },
  'Pay for {{name}}': { he: 'שכר ל־{{name}}', ur: '{{name}} کی تنخواہ', tl: 'Sahod ni {{name}}' },
  Monthly: { he: 'חודשי', ur: 'ماہانہ', tl: 'Buwanan' },
  Hourly: { he: 'שעתי', ur: 'فی گھنٹہ', tl: 'Oras-oras' },
  'Salary per month': { he: 'משכורת לחודש', ur: 'ماہانہ تنخواہ', tl: 'Sahod kada buwan' },
  'Rate per day': { he: 'תעריף ליום', ur: 'روزانہ ریٹ', tl: 'Bayad kada araw' },
  'Rate per hour': { he: 'תעריף לשעה', ur: 'فی گھنٹہ ریٹ', tl: 'Bayad kada oras' },
  'Overtime multiplier': {
    he: 'מכפיל שעות נוספות',
    ur: 'اوور ٹائم ضرب',
    tl: 'Multiplier ng overtime',
  },
  'Hours in a normal day': {
    he: 'שעות ביום רגיל',
    ur: 'عام دن میں گھنٹے',
    tl: 'Oras sa karaniwang araw',
  },
  'Days in a normal month': {
    he: 'ימים בחודש רגיל',
    ur: 'عام مہینے میں دن',
    tl: 'Araw sa karaniwang buwan',
  },
  'In effect from': { he: 'בתוקף מ־', ur: 'سے نافذ', tl: 'Epektibo mula' },
  'Earlier payslips keep the terms that were in force when they were paid.': {
    he: 'תלושים קודמים שומרים על התנאים שהיו בתוקף בזמן התשלום.',
    ur: 'پرانی تنخواہیں انہی شرائط پر رہتی ہیں جو ادائیگی کے وقت نافذ تھیں۔',
    tl: 'Ang mga dating payslip ay nananatili sa kasunduang umiral noong binayaran.',
  },
  'Adjust {{name}}’s pay': {
    he: 'עדכן את השכר של {{name}}',
    ur: '{{name}} کی تنخواہ ایڈجسٹ کریں',
    tl: 'Ayusin ang sahod ni {{name}}',
  },
  Amount: { he: 'סכום', ur: 'رقم', tl: 'Halaga' },
  'What is this for?': { he: 'בשביל מה זה?', ur: 'یہ کس لیے ہے؟', tl: 'Para saan ito?' },
  'Note (optional)': { he: 'הערה (לא חובה)', ur: 'نوٹ (اختیاری)', tl: 'Tala (opsyonal)' },
  'Never take more than this share of a payslip for loans (%)': {
    he: 'לעולם אל תיקח יותר מהחלק הזה מהתלוש עבור הלוואות (%)',
    ur: 'قرض کے لیے تنخواہ سے اس حصے سے زیادہ کبھی نہ کاٹیں (%)',
    tl: 'Huwag kailanman kumuha ng higit dito sa payslip para sa utang (%)',
  },
  'Pay approved reimbursements with the salary': {
    he: 'שלם החזרי הוצאות מאושרים יחד עם המשכורת',
    ur: 'منظور شدہ اخراجات کی واپسی تنخواہ کے ساتھ ادا کریں',
    tl: 'Bayaran ang aprubadong reimbursement kasama ng sahod',
  },
  'Finalize this payroll? Hours for the period will be locked and loan repayments will be recorded.':
    {
      he: 'לסגור את השכר סופית? השעות לתקופה יינעלו והחזרי ההלוואות יירשמו.',
      ur: 'اس تنخواہ کو حتمی کریں؟ اس مدت کے گھنٹے مقفل ہو جائیں گے اور قرض کی واپسی درج ہو گی۔',
      tl: 'Tapusin ang sahod na ito? Mala-lock ang mga oras at maitatala ang bayad-utang.',
    },
  'Mark this payroll as paid? This cannot be undone once confirmed.': {
    he: 'לסמן את השכר הזה כשולם? לא ניתן לבטל לאחר האישור.',
    ur: 'اس تنخواہ کو ادا شدہ کے طور پر نشان زد کریں؟ تصدیق کے بعد اسے واپس نہیں لیا جا سکتا۔',
    tl: 'Markahan ang sahod na ito bilang bayad na? Hindi na ito maibabalik kapag nakumpirma.',
  },
  'How was it paid?': { he: 'איך שולם?', ur: 'کیسے ادا کیا گیا؟', tl: 'Paano binayaran?' },
  'bank transfer': { he: 'העברה בנקאית', ur: 'بینک ٹرانسفر', tl: 'bank transfer' },
  'Discard this draft payroll?': {
    he: 'למחוק את טיוטת השכר?',
    ur: 'اس مسودہ تنخواہ کو ضائع کریں؟',
    tl: 'Itapon ang burador ng sahod?',
  },

  // === Staff: my pay ===
  'My pay': { he: 'השכר שלי', ur: 'میری تنخواہ', tl: 'Sahod ko' },
  'My rate': { he: 'התעריף שלי', ur: 'میرا ریٹ', tl: 'Rate ko' },
  'Overtime is paid at ×{{x}}': {
    he: 'שעות נוספות משולמות ב־×{{x}}',
    ur: 'اوور ٹائم ×{{x}} پر ادا ہوتا ہے',
    tl: 'Ang overtime ay binabayaran ng ×{{x}}',
  },
  'My loans': { he: 'ההלוואות שלי', ur: 'میرے قرضے', tl: 'Mga utang ko' },
  '{{amount}} comes off each salary automatically.': {
    he: '{{amount}} יורד מכל משכורת אוטומטית.',
    ur: 'ہر تنخواہ سے {{amount}} خود بخود کٹتا ہے۔',
    tl: 'Awtomatikong nababawas ang {{amount}} sa bawat sahod.',
  },
  'No automatic deduction.': {
    he: 'אין ניכוי אוטומטי.',
    ur: 'کوئی خودکار کٹوتی نہیں۔',
    tl: 'Walang awtomatikong bawas.',
  },
  'My payslips': { he: 'התלושים שלי', ur: 'میری تنخواہ کی پرچیاں', tl: 'Mga payslip ko' },
  'No payslips yet.': {
    he: 'אין עדיין תלושים.',
    ur: 'ابھی کوئی پرچی نہیں۔',
    tl: 'Wala pang payslip.',
  },
  'Money you spent, returned': {
    he: 'כסף שהוצאת, מוחזר',
    ur: 'آپ کے خرچ کیے پیسے، واپس',
    tl: 'Pera mong ginastos, ibinalik',
  },
  'I received this': { he: 'קיבלתי את זה', ur: 'مجھے یہ مل گیا', tl: 'Natanggap ko ito' },
  'Receipt confirmed': {
    he: 'הקבלה אושרה',
    ur: 'وصولی کی تصدیق ہوئی',
    tl: 'Nakumpirma ang pagtanggap',
  },
  'This month is still being prepared.': {
    he: 'החודש הזה עדיין בהכנה.',
    ur: 'یہ مہینہ ابھی تیار ہو رہا ہے۔',
    tl: 'Inihahanda pa ang buwang ito.',
  },
  'Your employer is notified straight away when you log overtime.': {
    he: 'המעסיק מקבל התראה מיד כשאתה רושם שעות נוספות.',
    ur: 'جب آپ اوور ٹائم درج کرتے ہیں تو آپ کے آجر کو فوراً اطلاع جاتی ہے۔',
    tl: 'Agad na naaabisuhan ang employer mo kapag nag-log ka ng overtime.',
  },

  // === Staff: history ===
  'My history': { he: 'ההיסטוריה שלי', ur: 'میری تاریخ', tl: 'Kasaysayan ko' },
  'Nothing finished yet. Your completed work shows here.': {
    he: 'עדיין לא הושלם כלום. העבודות שסיימת יופיעו כאן.',
    ur: 'ابھی کچھ مکمل نہیں ہوا۔ آپ کا مکمل کام یہاں آئے گا۔',
    tl: 'Wala pang natatapos. Dito lalabas ang tapos mong trabaho.',
  },
  Yesterday: { he: 'אתמול', ur: 'کل', tl: 'Kahapon' },

  // === What a staff member is allowed to do ===
  'What they can do': {
    he: 'מה מותר להם',
    ur: 'وہ کیا کر سکتے ہیں',
    tl: 'Ano ang kaya nilang gawin',
  },
  'Hand out work': { he: 'לחלק עבודה', ur: 'کام تفویض کرنا', tl: 'Mamahagi ng trabaho' },
  'Create tasks and routines, assign them to the other staff, and keep the house rules.': {
    he: 'ליצור משימות ושגרות, להקצות אותן לשאר העובדים, ולתחזק את כללי הבית.',
    ur: 'کام اور معمولات بنانا، دوسرے عملے کو تفویض کرنا، اور گھر کے اصول سنبھالنا۔',
    tl: 'Gumawa ng gawain at routine, ibigay sa ibang tauhan, at panatilihin ang mga tuntunin ng bahay.',
  },
  'Manage the staff': {
    he: 'לנהל את העובדים',
    ur: 'عملہ سنبھالنا',
    tl: 'Pamahalaan ang mga tauhan',
  },
  'Add people, reset their passwords, and disable someone who has left.': {
    he: 'להוסיף אנשים, לאפס להם סיסמאות, ולהשבית מי שעזב.',
    ur: 'لوگ شامل کرنا، ان کے پاس ورڈ ری سیٹ کرنا، اور جانے والے کو بند کرنا۔',
    tl: 'Magdagdag ng tao, i-reset ang password nila, at i-disable ang umalis.',
  },
  'See the spending': { he: 'לראות את ההוצאות', ur: 'اخراجات دیکھنا', tl: 'Makita ang gastos' },
  'Household expenses and the receipts waiting to be reviewed. Not salaries.': {
    he: 'הוצאות הבית והקבלות שממתינות לבדיקה. לא משכורות.',
    ur: 'گھر کے اخراجات اور جائزے کی منتظر رسیدیں۔ تنخواہیں نہیں۔',
    tl: 'Gastos ng bahay at mga resibong hinihintay suriin. Hindi ang sahod.',
  },
  'Salaries and household settings stay with you.': {
    he: 'משכורות והגדרות הבית נשארות אצלך.',
    ur: 'تنخواہیں اور گھر کی ترتیبات آپ کے پاس رہتی ہیں۔',
    tl: 'Ang sahod at mga setting ng bahay ay nananatili sa iyo.',
  },

  // === Turning on push ===
  'Turn on notifications': { he: 'הפעל התראות', ur: 'اطلاعات آن کریں', tl: 'I-on ang mga abiso' },
  'Otherwise you only find out about new work when you open the app.': {
    he: 'אחרת תגלה על עבודה חדשה רק כשתפתח את האפליקציה.',
    ur: 'ورنہ آپ کو نئے کام کا پتہ صرف ایپ کھولنے پر چلے گا۔',
    tl: 'Kung hindi, malalaman mo lang ang bagong trabaho kapag binuksan mo ang app.',
  },
  'Turn on': { he: 'הפעל', ur: 'آن کریں', tl: 'I-on' },
  'Not now': { he: 'לא עכשיו', ur: 'ابھی نہیں', tl: 'Hindi ngayon' },

  // === Receipts a staff member sent ===
  'Receipts I sent': {
    he: 'קבלות ששלחתי',
    ur: 'میں نے بھیجی رسیدیں',
    tl: 'Mga resibong ipinadala ko',
  },
  'Reading it…': { he: 'קורא אותה…', ur: 'پڑھ رہے ہیں…', tl: 'Binabasa…' },
  'Read — confirm it': { he: 'נקראה — אשר', ur: 'پڑھ لی — تصدیق کریں', tl: 'Nabasa — kumpirmahin' },
  'Needs your answer': {
    he: 'ממתין לתשובה שלך',
    ur: 'آپ کے جواب کا منتظر',
    tl: 'Kailangan ng sagot mo',
  },
  'Waiting for the boss': { he: 'ממתין לבוס', ur: 'مالک کے انتظار میں', tl: 'Hinihintay ang amo' },
  'Could not read — send it again': {
    he: 'לא הצלחנו לקרוא — שלח שוב',
    ur: 'پڑھ نہیں سکے — دوبارہ بھیجیں',
    tl: 'Hindi nabasa — ipadala ulit',
  },

  // === Notifications inbox ===
  'Mark all read': {
    he: 'סמן הכל כנקרא',
    ur: 'سب پڑھا ہوا نشان زد کریں',
    tl: 'Markahang lahat ng nabasa',
  },
  'Nothing new.': { he: 'אין חדש.', ur: 'کچھ نیا نہیں۔', tl: 'Walang bago.' },

  // === Pulse: the week ahead ===
  'What’s coming': { he: 'מה מגיע', ur: 'کیا آنے والا ہے', tl: 'Ano ang paparating' },
  'Next {{n}} days': { he: '{{n}} הימים הבאים', ur: 'اگلے {{n}} دن', tl: 'Susunod na {{n}} araw' },
  'Nothing scheduled in this window.': {
    he: 'אין שום דבר מתוכנן בטווח הזה.',
    ur: 'اس مدت میں کچھ طے شدہ نہیں۔',
    tl: 'Walang nakatakda sa panahong ito.',
  },
  'Nothing left today': {
    he: 'לא נשאר כלום היום',
    ur: 'آج کچھ باقی نہیں',
    tl: 'Wala nang natitira ngayon',
  },
  '{{n}} today': { he: '{{n}} היום', ur: 'آج {{n}}', tl: '{{n}} ngayong araw' },
  'Tasks, routines and events': {
    he: 'משימות, שגרות ואירועים',
    ur: 'کام، معمولات اور تقریبات',
    tl: 'Mga gawain, routine at kaganapan',
  },
  'Add event': { he: 'הוסף אירוע', ur: 'تقریب شامل کریں', tl: 'Magdagdag ng kaganapan' },
  'Remove this event?': {
    he: 'להסיר את האירוע?',
    ur: 'یہ تقریب ہٹا دیں؟',
    tl: 'Alisin ang kaganapang ito?',
  },
  Event: { he: 'אירוע', ur: 'تقریب', tl: 'Kaganapan' },
  Appointment: { he: 'פגישה', ur: 'ملاقات', tl: 'Appointment' },
  Delivery: { he: 'משלוח', ur: 'ترسیل', tl: 'Delivery' },
  Maintenance: { he: 'תחזוקה', ur: 'دیکھ بھال', tl: 'Pagpapanatili' },
  Errand: { he: 'סידור', ur: 'کام کاج', tl: 'Lakad' },
  'Where?': { he: 'איפה?', ur: 'کہاں؟', tl: 'Saan?' },
  Details: { he: 'פרטים', ur: 'تفصیلات', tl: 'Mga detalye' },

  // === Timeline: the household spine (Wave 3) ===
  Timeline: { he: 'ציר הזמן', ur: 'ٹائم لائن', tl: 'Timeline' },
  Agenda: { he: 'סדר יום', ur: 'ایجنڈا', tl: 'Agenda' },
  Birthdays: { he: 'ימי הולדת', ur: 'سالگرہیں', tl: 'Mga kaarawan' },
  Care: { he: 'טיפול', ur: 'دیکھ بھال', tl: 'Alaga' },
  'School/Medical': { he: 'בית ספר/רפואה', ur: 'اسکول/طبی', tl: 'Eskwela/Medikal' },

  // === A co-owner joining a house that already exists ===
  'Join {{name}}': {
    he: 'הצטרפות ל{{name}}',
    ur: '{{name}} میں شامل ہوں',
    tl: 'Sumali sa {{name}}',
  },
  'The house is already here. Three questions and you are in.': {
    he: 'הבית כבר קיים. שלוש שאלות ואתה בפנים.',
    ur: 'گھر پہلے سے موجود ہے۔ تین سوال اور آپ اندر ہیں۔',
    tl: 'Nandiyan na ang bahay. Tatlong tanong at pasok ka na.',
  },

  // === Contacts: a number you can tap, and who is allowed to see it ===
  'Call {{number}}': { he: 'חייג {{number}}', ur: 'کال {{number}}', tl: 'Tawagan {{number}}' },
  'Phone (optional) — makes this tap-to-call': {
    he: 'טלפון (לא חובה) — הופך את זה ללחיצה אחת לחיוג',
    ur: 'فون (اختیاری) — ایک ٹیپ سے کال',
    tl: 'Telepono (opsyonal) — para matawagan sa isang tap',
  },
  'Who sees this?': {
    he: 'מי רואה את זה?',
    ur: 'یہ کون دیکھے گا؟',
    tl: 'Sino ang makakakita nito?',
  },
  'Everyone in the house': {
    he: 'כל מי שבבית',
    ur: 'گھر میں سب',
    tl: 'Lahat sa bahay',
  },
  'Only {{n}} people see this': {
    he: 'רק {{n}} אנשים רואים את זה',
    ur: 'صرف {{n}} افراد یہ دیکھتے ہیں',
    tl: '{{n}} tao lang ang nakakakita nito',
  },
  'You always see everything you wrote.': {
    he: 'אתה תמיד רואה את כל מה שכתבת.',
    ur: 'آپ نے جو لکھا وہ آپ ہمیشہ دیکھیں گے۔',
    tl: 'Lagi mong nakikita ang lahat ng isinulat mo.',
  },

  // === Late work ===
  // Set by the server, not guessed by the screen. These two states existed in
  // the database from the first migration and nothing could ever reach them.
  Late: { he: 'באיחור', ur: 'تاخیر سے', tl: 'Huli na' },
  'Still not done': { he: 'עדיין לא בוצע', ur: 'ابھی تک نہیں ہوا', tl: 'Hindi pa rin tapos' },
  'This is late': { he: 'זה באיחור', ur: 'یہ تاخیر سے ہے', tl: 'Huli na ito' },

  // === Leave balance ===
  // Days off are a statutory entitlement in the UAE, not a favour, so the
  // person who earned them reads the number on their own screen.
  'Leave left': { he: 'ימי חופשה שנותרו', ur: 'باقی چھٹیاں', tl: 'Natitirang leave' },
  '{{left}} of {{total}} days': {
    he: '{{left}} מתוך {{total}} ימים',
    ur: '{{total}} میں سے {{left}} دن',
    tl: '{{left}} sa {{total}} araw',
  },
  '{{taken}} taken of {{total}} days, since {{start}}': {
    he: 'נוצלו {{taken}} מתוך {{total}} ימים, מאז {{start}}',
    ur: '{{start}} سے اب تک {{total}} میں سے {{taken}} دن لیے گئے',
    tl: '{{taken}} sa {{total}} araw ang nagamit, mula {{start}}',
  },
  'Sick days do not come out of this: {{n}} this year': {
    he: 'ימי מחלה לא יורדים מכאן: {{n}} השנה',
    ur: 'بیماری کی چھٹیاں اس میں شامل نہیں: اس سال {{n}}',
    tl: 'Hindi kasama rito ang sick leave: {{n}} ngayong taon',
  },
  'Leave — {{name}}': { he: 'חופשה — {{name}}', ur: 'چھٹیاں — {{name}}', tl: 'Leave — {{name}}' },
  'Taken this year': { he: 'נוצלו השנה', ur: 'اس سال لیے گئے', tl: 'Nagamit ngayong taon' },
  Left: { he: 'נותרו', ur: 'باقی', tl: 'Natitira' },
  'Sick days (separate)': {
    he: 'ימי מחלה (נפרד)',
    ur: 'بیماری کی چھٹیاں (الگ)',
    tl: 'Sick days (hiwalay)',
  },
  'Counted from {{start}}': {
    he: 'נספר מאז {{start}}',
    ur: '{{start}} سے شمار',
    tl: 'Binibilang mula {{start}}',
  },
  'Paid days off per year': {
    he: 'ימי חופשה בתשלום בשנה',
    ur: 'سالانہ تنخواہ کے ساتھ چھٹیاں',
    tl: 'Bayad na araw ng leave bawat taon',
  },
  'Started on': { he: 'תאריך תחילת עבודה', ur: 'کام شروع کرنے کی تاریخ', tl: 'Nagsimula noong' },
  'The leave year runs from this date, not from January.': {
    he: 'שנת החופשה נספרת מהתאריך הזה, לא מינואר.',
    ur: 'چھٹیوں کا سال اسی تاریخ سے شروع ہوتا ہے، جنوری سے نہیں۔',
    tl: 'Ang taon ng leave ay mula sa petsang ito, hindi mula Enero.',
  },

  // === One event and the tasks hanging off it ===
  // A dinner for eight is a shop, a table, a car and a guest room. Both roles
  // read this screen: the housekeeper with one job for Thursday sees what
  // Thursday is.
  'Add task': { he: 'הוסף משימה', ur: 'کام شامل کریں', tl: 'Magdagdag ng gawain' },
  'Part of': { he: 'שייך ל־', ur: 'کا حصہ', tl: 'Bahagi ng' },
  'Event not found': {
    he: 'האירוע לא נמצא',
    ur: 'تقریب نہیں ملی',
    tl: 'Hindi nakita ang kaganapan',
  },
  'No tasks yet': { he: 'עוד אין משימות', ur: 'ابھی کوئی کام نہیں', tl: 'Wala pang gawain' },
  '{{done}} of {{total}} done': {
    he: '{{done}} מתוך {{total}} הושלמו',
    ur: '{{total}} میں سے {{done}} مکمل',
    tl: '{{done}} sa {{total}} tapos na',
  },
  'Add the jobs this event needs — the shop, the table, the car.': {
    he: 'הוסף את המשימות שהאירוע דורש — הקניות, השולחן, הרכב.',
    ur: 'اس تقریب کے لیے درکار کام شامل کریں — خریداری، میز، گاڑی۔',
    tl: 'Idagdag ang mga gawaing kailangan nito — ang pamimili, ang mesa, ang sasakyan.',
  },
  'Nothing assigned for this event yet.': {
    he: 'עוד לא הוקצתה משימה לאירוע הזה.',
    ur: 'اس تقریب کے لیے ابھی کچھ تفویض نہیں ہوا۔',
    tl: 'Wala pang naitalagang gawain para dito.',
  },
  'Remove this event': { he: 'הסר את האירוע', ur: 'تقریب ہٹا دیں', tl: 'Alisin ang kaganapan' },
  'The tasks stay — only the event they were grouped under goes away.': {
    he: 'המשימות נשארות — רק האירוע שקיבץ אותן נמחק.',
    ur: 'کام باقی رہیں گے — صرف وہ تقریب ہٹے گی جس کے تحت وہ جمع تھے۔',
    tl: 'Mananatili ang mga gawain — ang kaganapan lang ang aalisin.',
  },

  // === The staff screen ===
  // Open to a staff member with `manage_staff` now, not only the owner, so
  // every string here has to exist in their language. It was the last screen
  // written straight in Hebrew.
  'The household team': { he: 'צוות הבית', ur: 'گھر کی ٹیم', tl: 'Ang koponan ng bahay' },
  'Add a person': { he: 'הוסף עובד', ur: 'فرد شامل کریں', tl: 'Magdagdag ng tao' },
  'New staff member': { he: 'עובד חדש', ur: 'نیا ملازم', tl: 'Bagong tauhan' },
  'No people yet.': { he: 'אין עדיין עובדים.', ur: 'ابھی کوئی نہیں۔', tl: 'Wala pang tao.' },
  'Add the first person so you can start handing out work.': {
    he: 'הוסף את העובד הראשון כדי להתחיל לחלק עבודה.',
    ur: 'کام تفویض کرنا شروع کرنے کے لیے پہلا فرد شامل کریں۔',
    tl: 'Magdagdag ng unang tao para makapagbigay ka na ng trabaho.',
  },
  Disabled: { he: 'מושבת', ur: 'غیر فعال', tl: 'Naka-disable' },
  Enable: { he: 'הפעל', ur: 'فعال کریں', tl: 'Paganahin' },
  'Reset password': { he: 'איפוס סיסמה', ur: 'پاس ورڈ ری سیٹ', tl: 'I-reset ang password' },
  'Generate a new password?': {
    he: 'להפיק סיסמה חדשה?',
    ur: 'نیا پاس ورڈ بنائیں؟',
    tl: 'Gumawa ng bagong password?',
  },
  'New password': { he: 'סיסמה חדשה', ur: 'نیا پاس ورڈ', tl: 'Bagong password' },
  'Hand it over in person.': {
    he: 'מסור באופן אישי.',
    ur: 'ذاتی طور پر دیں۔',
    tl: 'Ibigay nang personal.',
  },
  'Sign-in details': { he: 'פרטי כניסה', ur: 'سائن اِن تفصیلات', tl: 'Mga detalye ng pag-sign in' },
  'Hand these over in person. The password is not shown again.': {
    he: 'מסור באופן אישי. הסיסמה לא תוצג שוב.',
    ur: 'یہ ذاتی طور پر دیں۔ پاس ورڈ دوبارہ نہیں دکھایا جائے گا۔',
    tl: 'Ibigay ito nang personal. Hindi na ipapakita muli ang password.',
  },
  Copy: { he: 'העתק', ur: 'کاپی', tl: 'Kopyahin' },
  Copied: { he: 'הועתק', ur: 'کاپی ہو گیا', tl: 'Nakopya' },
  'Send on WhatsApp': { he: 'שלח בוואטסאפ', ur: 'واٹس ایپ پر بھیجیں', tl: 'Ipadala sa WhatsApp' },
  Username: { he: 'שם משתמש', ur: 'صارف نام', tl: 'Username' },
  'First password': { he: 'סיסמה ראשונה', ur: 'پہلا پاس ورڈ', tl: 'Unang password' },
  'Open the app and sign in. You will be asked to choose your own password.': {
    he: 'פתח את האפליקציה והיכנס. תתבקש לבחור סיסמה משלך.',
    ur: 'ایپ کھولیں اور سائن اِن کریں۔ آپ سے اپنا پاس ورڈ منتخب کرنے کو کہا جائے گا۔',
    tl: 'Buksan ang app at mag-sign in. Hihilingin sa iyong pumili ng sarili mong password.',
  },
  // Job positions
  Driver: { he: 'נהג', ur: 'ڈرائیور', tl: 'Drayber' },
  'Personal assistant': { he: 'עוזר אישי', ur: 'ذاتی معاون', tl: 'Personal assistant' },
  Housekeeper: { he: 'עוזרת בית', ur: 'گھریلو ملازمہ', tl: 'Katulong sa bahay' },
  Gardener: { he: 'גנן', ur: 'مالی', tl: 'Hardinero' },
  Nanny: { he: 'מטפלת', ur: 'آیا', tl: 'Yaya' },
  Cook: { he: 'טבח', ur: 'باورچی', tl: 'Kusinero' },

  // === House rules (memory) ===
  // Read by everyone in the household, written by the owner and by any staff
  // member with `assign_work`. The reader is usually the person whose first
  // language is not the owner's, so every string here matters.
  Rules: { he: 'חוקים', ur: 'اصول', tl: 'Mga tuntunin' },
  'House rules': { he: 'חוקי הבית', ur: 'گھر کے اصول', tl: 'Mga tuntunin ng bahay' },
  'How this house likes things done.': {
    he: 'איך אוהבים לעשות דברים בבית הזה.',
    ur: 'اس گھر میں کام کیسے کیے جاتے ہیں۔',
    tl: 'Kung paano ginagawa ang mga bagay sa bahay na ito.',
  },
  'Always remember': { he: 'תמיד לזכור', ur: 'ہمیشہ یاد رکھیں', tl: 'Laging tandaan' },
  'The rest': { he: 'השאר', ur: 'باقی', tl: 'Ang iba pa' },
  'No house rules yet.': {
    he: 'עדיין אין חוקי בית.',
    ur: 'ابھی تک گھر کے کوئی اصول نہیں۔',
    tl: 'Wala pang mga tuntunin ng bahay.',
  },
  'Add a rule': { he: 'הוסף חוק', ur: 'اصول شامل کریں', tl: 'Magdagdag ng tuntunin' },
  'New house rule': { he: 'חוק בית חדש', ur: 'گھر کا نیا اصول', tl: 'Bagong tuntunin ng bahay' },
  'Edit rule': { he: 'עריכת חוק', ur: 'اصول میں ترمیم', tl: 'I-edit ang tuntunin' },
  'Delete this rule?': {
    he: 'למחוק את החוק?',
    ur: 'یہ اصول حذف کریں؟',
    tl: 'Burahin ang tuntuning ito?',
  },
  'Short title': { he: 'כותרת קצרה', ur: 'مختصر عنوان', tl: 'Maikling pamagat' },
  'What the house needs to remember': {
    he: 'מה שהבית צריך לזכור',
    ur: 'جو گھر کو یاد رکھنا ہے',
    tl: 'Ang dapat tandaan ng bahay',
  },
  'Pin to the top — everyone sees this first': {
    he: 'הצמד למעלה — כולם רואים את זה ראשון',
    ur: 'اوپر لگائیں — سب سے پہلے یہی نظر آئے گا',
    tl: 'I-pin sa itaas — ito ang unang makikita ng lahat',
  },
  'Only 8 rules can be pinned. Unpin one first.': {
    he: 'אפשר להצמיד עד 8 חוקים. שחרר אחד קודם.',
    ur: 'صرف 8 اصول اوپر لگ سکتے ہیں۔ پہلے ایک ہٹائیں۔',
    tl: 'Walong tuntunin lang ang maaaring i-pin. Mag-unpin muna ng isa.',
  },
  Pin: { he: 'הצמד', ur: 'اوپر لگائیں', tl: 'I-pin' },
  Unpin: { he: 'שחרר', ur: 'ہٹائیں', tl: 'I-unpin' },
  Pinned: { he: 'מוצמד', ur: 'اوپر لگا ہوا', tl: 'Naka-pin' },
  'Saving failed': { he: 'השמירה נכשלה', ur: 'محفوظ کرنا ناکام', tl: 'Nabigo ang pag-save' },
  // Memory categories
  Shopping: { he: 'קניות', ur: 'خریداری', tl: 'Pamimili' },
  Car: { he: 'רכב', ur: 'گاڑی', tl: 'Sasakyan' },
  Guests: { he: 'אורחים', ur: 'مہمان', tl: 'Mga bisita' },
  Deliveries: { he: 'משלוחים', ur: 'ترسیلات', tl: 'Mga delivery' },
  Contacts: { he: 'אנשי קשר', ur: 'رابطے', tl: 'Mga contact' },

  // === Standing arrangements, loans, and how the salary is handed over ===
  // Read by the person being paid as much as by the one paying. Every line
  // here is about money leaving or reaching somebody's hand, so it says what
  // happens rather than naming a setting.
  'Monthly deduction': {
    he: 'ניכוי חודשי',
    ur: 'ماہانہ کٹوتی',
    tl: 'Buwanang kaltas',
  },
  'Every month from {{name}}': {
    he: 'כל חודש מ{{name}}',
    ur: '{{name}} سے ہر مہینے',
    tl: 'Bawat buwan mula kay {{name}}',
  },
  'Set once. It comes off every payslip until you stop it.': {
    he: 'מגדירים פעם אחת. יורד מכל תלוש עד שעוצרים.',
    ur: 'ایک بار مقرر کریں۔ روکنے تک ہر تنخواہ سے کٹتا رہے گا۔',
    tl: 'Isang beses lang itakda. Kakaltasin sa bawat sahod hanggang ihinto mo.',
  },
  'What is it for': { he: 'בשביל מה זה', ur: 'یہ کس لیے ہے', tl: 'Para saan ito' },
  Accommodation: { he: 'מגורים', ur: 'رہائش', tl: 'Tirahan' },
  'Amount each month': { he: 'סכום כל חודש', ur: 'ہر مہینے کی رقم', tl: 'Halaga bawat buwan' },
  From: { he: 'מתאריך', ur: 'اس تاریخ سے', tl: 'Mula' },
  'Until (optional)': { he: 'עד (לא חובה)', ur: 'تک (اختیاری)', tl: 'Hanggang (opsyonal)' },
  'every month': { he: 'כל חודש', ur: 'ہر مہینے', tl: 'bawat buwan' },
  'from {{date}}': { he: 'מ־{{date}}', ur: '{{date}} سے', tl: 'mula {{date}}' },
  'until {{date}}': { he: 'עד {{date}}', ur: '{{date}} تک', tl: 'hanggang {{date}}' },
  Stop: { he: 'עצור', ur: 'روکیں', tl: 'Ihinto' },
  'Stop taking {{label}} off every month?': {
    he: 'להפסיק לנכות {{label}} כל חודש?',
    ur: 'ہر مہینے {{label}} کاٹنا بند کریں؟',
    tl: 'Ihinto ang buwanang pagkaltas ng {{label}}?',
  },

  'Add loan': { he: 'הוסף הלוואה', ur: 'قرض شامل کریں', tl: 'Magdagdag ng utang' },
  'Money lent to {{name}}': {
    he: 'כסף שהולווה ל{{name}}',
    ur: '{{name}} کو دیا گیا قرض',
    tl: 'Perang ipinahiram kay {{name}}',
  },
  'How much was lent': {
    he: 'כמה הולווה',
    ur: 'کتنا قرض دیا گیا',
    tl: 'Magkano ang ipinahiram',
  },
  'Handed over on': { he: 'נמסר בתאריך', ur: 'کس دن دیا گیا', tl: 'Ibinigay noong' },
  'How it comes back': {
    he: 'איך זה חוזר',
    ur: 'یہ کیسے واپس آئے گا',
    tl: 'Paano ito babalik',
  },
  'Start taking it from': {
    he: 'להתחיל לנכות מ־',
    ur: 'کٹوتی اس تاریخ سے شروع کریں',
    tl: 'Simulan ang pagkaltas mula',
  },
  'Leave empty to start with the next payroll.': {
    he: 'השאר ריק כדי להתחיל כבר בשכר הבא.',
    ur: 'اگلی تنخواہ سے شروع کرنے کے لیے خالی چھوڑ دیں۔',
    tl: 'Iwanang blangko para magsimula sa susunod na sahod.',
  },
  '{{amount}}/mo from {{date}}': {
    he: '{{amount}} לחודש מ־{{date}}',
    ur: '{{date}} سے {{amount}} ماہانہ',
    tl: '{{amount}}/buwan mula {{date}}',
  },
  '{{amount}} starts coming off your salary on {{date}}.': {
    he: '{{amount}} יתחיל לרדת מהמשכורת שלך ב־{{date}}.',
    ur: '{{date}} سے آپ کی تنخواہ سے {{amount}} کٹنا شروع ہوگا۔',
    tl: 'Magsisimulang kaltasin ang {{amount}} sa sahod mo sa {{date}}.',
  },

  'How the salary is handed over': {
    he: 'איך המשכורת נמסרת',
    ur: 'تنخواہ کیسے دی جاتی ہے',
    tl: 'Paano ibinibigay ang sahod',
  },
  'How this is handed over': {
    he: 'איך זה נמסר',
    ur: 'یہ کیسے دیا جاتا ہے',
    tl: 'Paano ito ibinibigay',
  },
  'How you are paid this': {
    he: 'איך משלמים לך את זה',
    ur: 'یہ آپ کو کیسے ادا کیا جاتا ہے',
    tl: 'Paano ito ibinabayad sa iyo',
  },
  'Leave the amount empty on one line and it takes whatever is left.': {
    he: 'השאר שורה אחת בלי סכום והיא תיקח את מה שנשאר.',
    ur: 'ایک لائن میں رقم خالی چھوڑ دیں، وہ باقی سب لے لے گی۔',
    tl: 'Iwanang blangko ang halaga sa isang linya at kukunin nito ang natitira.',
  },
  'What this part is': {
    he: 'מה החלק הזה',
    ur: 'یہ حصہ کیا ہے',
    tl: 'Ano ang bahaging ito',
  },
  'the rest': { he: 'השאר', ur: 'باقی', tl: 'ang natitira' },
  'Add a part': { he: 'הוסף חלק', ur: 'حصہ شامل کریں', tl: 'Magdagdag ng bahagi' },
  'Through the agency': {
    he: 'דרך הסוכנות',
    ur: 'ایجنسی کے ذریعے',
    tl: 'Sa pamamagitan ng ahensya',
  },
  'Bank transfer': { he: 'העברה בנקאית', ur: 'بینک ٹرانسفر', tl: 'Bank transfer' },
  'Cash in hand': { he: 'מזומן ליד', ur: 'نقد ہاتھ میں', tl: 'Cash sa kamay' },

  'What you pay on top': {
    he: 'מה שאתה משלם מעבר',
    ur: 'جو آپ اوپر سے ادا کرتے ہیں',
    tl: 'Ang binabayaran mo bukod pa',
  },
  'An agency fee or visa cost you carry. It is never taken from their salary.': {
    he: 'עמלת סוכנות או עלות ויזה שאתה נושא. זה אף פעם לא יורד מהמשכורת שלהם.',
    ur: 'ایجنسی فیس یا ویزا خرچ جو آپ اٹھاتے ہیں۔ یہ کبھی اُن کی تنخواہ سے نہیں کٹتا۔',
    tl: 'Bayad sa ahensya o gastos sa visa na ikaw ang sumasagot. Hindi ito kailanman kinakaltas sa sahod nila.',
  },
  'Agency fee': { he: 'עמלת סוכנות', ur: 'ایجنسی فیس', tl: 'Bayad sa ahensya' },
  'you pay this on top': {
    he: 'אתה משלם את זה מעבר',
    ur: 'یہ آپ اوپر سے ادا کرتے ہیں',
    tl: 'ikaw ang nagbabayad nito bukod pa',
  },
  '{{amount}} more you pay to agencies — not taken from anyone’s salary': {
    he: 'עוד {{amount}} שאתה משלם לסוכנויות — לא יורד מהמשכורת של אף אחד',
    ur: 'مزید {{amount}} جو آپ ایجنسیوں کو دیتے ہیں — کسی کی تنخواہ سے نہیں کٹتا',
    tl: 'Dagdag na {{amount}} na binabayaran mo sa mga ahensya — hindi kinakaltas sa sahod ninuman',
  },
  'The month costs the household {{amount}} in all': {
    he: 'החודש עולה לבית {{amount}} בסך הכול',
    ur: 'اس مہینے گھر پر کل {{amount}} خرچ آتا ہے',
    tl: 'Umaabot sa {{amount}} ang kabuuang gastos ng bahay ngayong buwan',
  },

  'Set pay': { he: 'קבע שכר', ur: 'تنخواہ مقرر کریں', tl: 'Itakda ang sahod' },
  'On trial for now': {
    he: 'בתקופת ניסיון בינתיים',
    ur: 'فی الحال آزمائشی مدت پر',
    tl: 'Nasa subok muna',
  },
  'On trial means no amount has been agreed yet. We stop asking until it is.': {
    he: 'תקופת ניסיון פירושה שעוד לא סוכם סכום. נפסיק לשאול עד שיסוכם.',
    ur: 'آزمائشی مدت کا مطلب ہے ابھی کوئی رقم طے نہیں ہوئی۔ طے ہونے تک ہم پوچھنا بند کر دیں گے۔',
    tl: 'Ibig sabihin ng nasa subok, wala pang napagkasunduang halaga. Hindi na kami magtatanong hangga’t wala pa.',
  },
  'On trial': { he: 'בתקופת ניסיון', ur: 'آزمائشی مدت پر', tl: 'Nasa subok' },
  'No pay agreed yet, so they are left out of payroll.': {
    he: 'עוד לא סוכם שכר, ולכן הם לא נכללים בשכר החודשי.',
    ur: 'ابھی تنخواہ طے نہیں ہوئی، اس لیے وہ تنخواہ کی فہرست میں شامل نہیں۔',
    tl: 'Wala pang napagkasunduang sahod, kaya hindi sila kasama sa payroll.',
  },
  'Agree on pay': { he: 'סכם שכר', ur: 'تنخواہ طے کریں', tl: 'Pagkasunduan ang sahod' },
  'End trial': { he: 'סיים ניסיון', ur: 'آزمائش ختم کریں', tl: 'Tapusin ang subok' },

  // === Owner Home — the queue ===
  'Requires decision': {
    he: 'דורש החלטה',
    ur: 'فیصلہ درکار',
    tl: 'Kailangan ng desisyon',
  },
  'Needs attention': { he: 'דורש תשומת לב', ur: 'توجہ درکار', tl: 'Kailangan ng atensyon' },
  'Worth a look': { he: 'שווה מבט', ur: 'ایک نظر ڈالیں', tl: 'Sulit tingnan' },
  'Running now': { he: 'קורה עכשיו', ur: 'ابھی جاری', tl: 'Ginagawa ngayon' },
  "Today's timeline": { he: 'ציר היום', ur: 'آج کی ترتیب', tl: 'Takbo ng araw' },
  'One thing needs you.': {
    he: 'דבר אחד מחכה לך.',
    ur: 'ایک چیز آپ کی منتظر ہے۔',
    tl: 'May isang bagay na naghihintay sa iyo.',
  },
  '{{n}} things need you.': {
    he: '{{n}} דברים מחכים לך.',
    ur: '{{n}} چیزیں آپ کی منتظر ہیں۔',
    tl: '{{n}} bagay ang naghihintay sa iyo.',
  },
  // The same two phrases without the full stop — the new status line runs
  // them into "… · 2 running now", where a mid-sentence period reads wrong.
  '{{n}} things need you': {
    he: '{{n}} דברים מחכים לך',
    ur: '{{n}} چیزیں آپ کی منتظر ہیں',
    tl: '{{n}} bagay ang naghihintay sa iyo',
  },
  'One thing needs you': {
    he: 'דבר אחד מחכה לך',
    ur: 'ایک چیز آپ کی منتظر ہے',
    tl: 'May isang bagay na naghihintay sa iyo',
  },
  'All under control.': { he: 'הכול תחת שליטה.', ur: 'سب کچھ قابو میں ہے۔', tl: 'Ayos ang lahat.' },
  '{{n}} running now': {
    he: '{{n}} בעבודה עכשיו',
    ur: '{{n}} ابھی جاری',
    tl: '{{n}} ang ginagawa ngayon',
  },
  '{{n}} people': { he: '{{n}} אנשים', ur: '{{n}} افراد', tl: '{{n}} tao' },
  Someone: { he: 'מישהו', ur: 'کوئی', tl: 'May isang tao' },
  // Reads as "AED 150 for garden supplies" — a preposition, not a label.
  for: { he: 'עבור', ur: 'برائے', tl: 'para sa' },
  'Approve & dispense': {
    he: 'אשר ומסור',
    ur: 'منظور کریں اور دیں',
    tl: 'Aprubahan at ibigay',
  },
  'Mark as handed over': {
    he: 'סמן שנמסר',
    ur: 'نشان لگائیں کہ دے دیا',
    tl: 'Markahan bilang naibigay',
  },
  'Paid out of pocket': {
    he: 'שילם/ה מכיסו',
    ur: 'اپنی جیب سے ادا کیا',
    tl: 'Binayaran mula sa sariling bulsa',
  },
  'They have been reminded.': {
    he: 'נשלחה תזכורת.',
    ur: 'یاد دہانی بھیج دی گئی۔',
    tl: 'Naipaalala na sa kanila.',
  },
  '{{name}} is late — due {{when}}': {
    he: '{{name}} מאחר/ת — היה אמור {{when}}',
    ur: '{{name}} کو دیر ہو گئی — مقررہ وقت {{when}}',
    tl: 'Huli na si {{name}} — dapat {{when}}',
  },
  'Past its deadline': {
    he: 'עבר המועד',
    ur: 'مقررہ وقت گزر گیا',
    tl: 'Lampas na sa takdang oras',
  },
  'Suggested action': {
    he: 'מה כדאי לעשות',
    ur: 'تجویز کردہ اقدام',
    tl: 'Iminumungkahing hakbang',
  },
  'Nobody has picked this up. Ask for a status or hand it to someone else.': {
    he: 'אף אחד לא לקח את זה. אפשר לבקש עדכון או להעביר למישהו אחר.',
    ur: 'کسی نے یہ کام نہیں سنبھالا۔ صورتحال پوچھیں یا کسی اور کو دے دیں۔',
    tl: 'Walang kumuha nito. Humingi ng update o ibigay sa iba.',
  },
  Nudge: { he: 'תזכורת', ur: 'یاد دہانی', tl: 'Paalala' },
  'Nudge {{name}}': {
    he: 'שלח תזכורת ל{{name}}',
    ur: '{{name}} کو یاد دہانی بھیجیں',
    tl: 'Paalalahanan si {{name}}',
  },
  Open: { he: 'פתח', ur: 'کھولیں', tl: 'Buksan' },
  Unassigned: { he: 'לא שויך', ur: 'کسی کو نہیں دیا گیا', tl: 'Walang nakatalaga' },

  // === The composer ===
  'What would you like to happen?': {
    he: 'מה תרצה שיקרה?',
    ur: 'آپ کیا چاہتے ہیں کہ ہو؟',
    tl: 'Ano ang gusto mong mangyari?',
  },
  'Say it instead': { he: 'תגיד את זה', ur: 'بول کر بتائیں', tl: 'Sabihin na lang' },
  'Nobody yet': { he: 'עדיין אף אחד', ur: 'ابھی کوئی نہیں', tl: 'Wala pa' },
  When: { he: 'מתי', ur: 'کب', tl: 'Kailan' },
  'Pick a time': { he: 'בחר שעה', ur: 'وقت منتخب کریں', tl: 'Pumili ng oras' },
  'Pick a deadline': { he: 'בחר מועד אחרון', ur: 'آخری وقت منتخب کریں', tl: 'Pumili ng taning' },
  Checklist: { he: 'רשימה', ur: 'فہرست', tl: 'Listahan' },
  Clear: { he: 'נקה', ur: 'صاف کریں', tl: 'Burahin' },
  'Photo proof': { he: 'הוכחה בתמונה', ur: 'تصویری ثبوت', tl: 'Patunay na larawan' },
  Camera: { he: 'מצלמה', ur: 'کیمرہ', tl: 'Kamera' },
  'Schedule task': { he: 'תזמן משימה', ur: 'کام شیڈول کریں', tl: 'Iiskedyul ang gawain' },
  'Send task': { he: 'שלח משימה', ur: 'کام بھیجیں', tl: 'Ipadala ang gawain' },
  'Who is doing this?': { he: 'מי עושה את זה?', ur: 'یہ کون کرے گا؟', tl: 'Sino ang gagawa nito?' },
  'Schedule for': { he: 'תזמן ל', ur: 'کے لیے شیڈول', tl: 'Iiskedyul para sa' },
  'Extra detail': { he: 'פרטים נוספים', ur: 'اضافی تفصیل', tl: 'Karagdagang detalye' },
  'New operation': { he: 'פעולה חדשה', ur: 'نیا کام', tl: 'Bagong operasyon' },
  'Usually done: {{cadence}}': {
    he: 'בדרך כלל עושים: {{cadence}}',
    ur: 'عام طور پر کیا جاتا ہے: {{cadence}}',
    tl: 'Karaniwang ginagawa: {{cadence}}',
  },
  'Add the pro checklist ({{n}})': {
    he: 'הוסף צ׳ק-ליסט מקצועי ({{n}})',
    ur: 'پروفیشنل چیک لسٹ شامل کریں ({{n}})',
    tl: 'Idagdag ang pro checklist ({{n}})',
  },
  'Checklist added': {
    he: 'הצ׳ק-ליסט נוסף',
    ur: 'چیک لسٹ شامل کر دی گئی',
    tl: 'Naidagdag ang checklist',
  },
  'Make it a routine': { he: 'הפוך לשגרה', ur: 'روٹین بنائیں', tl: 'Gawing routine' },
  Rule: { he: 'כלל', ur: 'قاعدہ', tl: 'Tuntunin' },
  'Sent to the owner': { he: 'נשלח לבעלים', ur: 'مالک کو بھیج دیا', tl: 'Naipadala sa may-ari' },
  'Checklist ({{n}})': { he: 'צ׳ק-ליסט ({{n}})', ur: 'چیک لسٹ ({{n}})', tl: 'Checklist ({{n}})' },
  'Task created': { he: 'המשימה נוצרה', ur: 'کام بن گیا', tl: 'Nagawa ang gawain' },
  Dismissed: { he: 'נדחה', ur: 'مسترد کر دیا گیا', tl: 'Tinanggihan' },
  'Routine created — it will run on its own': {
    he: 'השגרה נוצרה — היא תרוץ לבד',
    ur: 'روٹین بن گئی — یہ خود چلے گی',
    tl: 'Nagawa ang routine — tatakbo ito nang mag-isa',
  },
  'Twice a week': {
    he: 'פעמיים בשבוע',
    ur: 'ہفتے میں دو بار',
    tl: 'Dalawang beses sa isang linggo',
  },
  Weekly: { he: 'שבועי', ur: 'ہفتہ وار', tl: 'Lingguhan' },
  'Once a month': { he: 'פעם בחודש', ur: 'مہینے میں ایک بار', tl: 'Minsan sa isang buwan' },
  Translate: { he: 'תרגם', ur: 'ترجمہ کریں', tl: 'Isalin' },
  Original: { he: 'מקור', ur: 'اصل', tl: 'Orihinal' },
  'Could not translate right now': {
    he: 'לא ניתן לתרגם כרגע',
    ur: 'ابھی ترجمہ نہیں ہو سکا',
    tl: 'Hindi maisalin sa ngayon',
  },
  'Create operation': { he: 'צור פעולה', ur: 'کام بنائیں', tl: 'Gawin ang operasyon' },
  'What needs doing?': {
    he: 'מה צריך לעשות?',
    ur: 'کیا کرنا ہے؟',
    tl: 'Ano ang kailangang gawin?',
  },
  Assignee: { he: 'אחראי', ur: 'ذمہ دار', tl: 'Assign kay' },
  'Add...': { he: 'הוסף…', ur: 'شامل کریں…', tl: 'Magdagdag…' },
  'Require photo proof': {
    he: 'דרוש הוכחת תמונה',
    ur: 'تصویری ثبوت درکار',
    tl: 'Kailangan ng photo proof',
  },
  'For task completion': {
    he: 'להשלמת המשימה',
    ur: 'کام مکمل کرنے کے لیے',
    tl: 'Para makumpleto ang gawain',
  },
  'Progress timeline': { he: 'ציר התקדמות', ur: 'پیش رفت ٹائم لائن', tl: 'Timeline ng progreso' },
  'Task sent': { he: 'המשימה נשלחה', ur: 'کام بھیج دیا گیا', tl: 'Naipadala ang gawain' },
  'Task seen': { he: 'המשימה נצפתה', ur: 'کام دیکھ لیا گیا', tl: 'Nakita ang gawain' },
  'Waiting approval': {
    he: 'ממתין לאישור',
    ur: 'منظوری کا انتظار',
    tl: 'Hinihintay ang pag-apruba',
  },

  // === Home digest ===
  Started: { he: 'התחיל/ה', ur: 'شروع کر دیا', tl: 'Sinimulan' },
  'Opened it': { he: 'פתח/ה', ur: 'کھول لیا', tl: 'Binuksan' },
  'Sent, not opened yet': {
    he: 'נשלח, עוד לא נפתח',
    ur: 'بھیج دیا، ابھی کھولا نہیں',
    tl: 'Naipadala, hindi pa binubuksan',
  },

  // === Money ===
  'Total house cost': {
    he: 'עלות הבית החודש',
    ur: 'گھر کی کل لاگت',
    tl: 'Kabuuang gastos ng bahay',
  },
  '{{n}}% vs last month': {
    he: '{{n}}% מול החודש שעבר',
    ur: 'پچھلے مہینے سے {{n}}%',
    tl: '{{n}}% kumpara noong nakaraang buwan',
  },
  '{{spend}} spent + {{pay}} in wages': {
    he: '{{spend}} הוצאות + {{pay}} שכר',
    ur: '{{spend}} اخراجات + {{pay}} تنخواہ',
    tl: '{{spend}} gastos + {{pay}} sahod',
  },
  'Nothing recorded this month yet.': {
    he: 'עוד לא נרשם כלום החודש.',
    ur: 'اس مہینے ابھی کچھ درج نہیں ہوا۔',
    tl: 'Wala pang naitala ngayong buwan.',
  },
  'Last month came to {{total}}. Tap to see it.': {
    he: 'החודש שעבר הסתכם ב־{{total}}. הקש כדי לראות.',
    ur: 'پچھلا مہینہ {{total}} رہا۔ دیکھنے کے لیے دبائیں۔',
    tl: 'Umabot sa {{total}} noong nakaraang buwan. Pindutin para makita.',
  },
  'Receipts and wages will show up here as they happen.': {
    he: 'קבלות ומשכורות יופיעו כאן כשהן קורות.',
    ur: 'رسیدیں اور تنخواہیں یہاں آتی جائیں گی۔',
    tl: 'Lalabas dito ang mga resibo at sahod habang nangyayari.',
  },
  'House expenses': { he: 'הוצאות הבית', ur: 'گھریلو اخراجات', tl: 'Gastos sa bahay' },
  Wages: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  'Still to pay': { he: 'עוד לתשלום', ur: 'ابھی ادا کرنا ہے', tl: 'Babayaran pa' },
  'Receipts to review': {
    he: 'קבלות לאישור',
    ur: 'جائزے کے لیے رسیدیں',
    tl: 'Mga resibong susuriin',
  },
  '{{n}} pending': { he: '{{n}} ממתינות', ur: '{{n}} زیر التوا', tl: '{{n}} naghihintay' },
  'Where it went': { he: 'לאן זה הלך', ur: 'کہاں خرچ ہوا', tl: 'Saan napunta' },
  'Projected next month': {
    he: 'צפי לחודש הבא',
    ur: 'اگلے مہینے کا تخمینہ',
    tl: 'Tantiya sa susunod na buwan',
  },
  // Owner money screen — the team's money under the snapshot slab: salaries,
  // leave, loans, cash advances, payroll runs, and their state labels.
  'Team salaries': { he: 'משכורות הצוות', ur: 'ٹیم کی تنخواہیں', tl: 'Mga sahod ng team' },
  'Leave balances': { he: 'יתרות חופשה', ur: 'چھٹیوں کے بقایا', tl: 'Mga natitirang bakasyon' },
  'Vacation days': { he: 'ימי חופשה', ur: 'چھٹی کے دن', tl: 'Araw ng bakasyon' },
  'Sick days': { he: 'ימי מחלה', ur: 'بیماری کے دن', tl: 'Araw ng sakit' },
  Loans: { he: 'הלוואות', ur: 'قرضے', tl: 'Mga utang' },
  Advances: { he: 'מקדמות', ur: 'پیشگیاں', tl: 'Mga advance' },
  'Payroll runs': { he: 'סבבי שכר', ur: 'تنخواہ کے رن', tl: 'Mga payroll run' },
  Remaining: { he: 'נותר', ur: 'باقی', tl: 'Natitira' },
  Active: { he: 'פעיל', ur: 'فعال', tl: 'Aktibo' },
  'Paid off': { he: 'שולם במלואו', ur: 'مکمل ادا', tl: 'Bayad na nang buo' },
  'Written off': { he: 'נמחק מהספרים', ur: 'معاف کر دیا گیا', tl: 'Pinatawad na' },
  Requested: { he: 'נתבקש', ur: 'درخواست کی گئی', tl: 'Hiniling' },
  Reconciled: { he: 'יושב', ur: 'تصفیہ شدہ', tl: 'Naitugma' },
  'No loans.': { he: 'אין הלוואות.', ur: 'کوئی قرضے نہیں۔', tl: 'Walang mga utang.' },
  'No advances given yet.': {
    he: 'עוד לא ניתנו מקדמות.',
    ur: 'ابھی تک کوئی پیشگی نہیں دی گئی۔',
    tl: 'Wala pang naibigay na advance.',
  },
  'No payroll runs yet.': {
    he: 'עוד לא הופק שכר.',
    ur: 'ابھی تک کوئی تنخواہ کا رن نہیں۔',
    tl: 'Wala pang payroll run.',
  },
  // Suggestion kind labels (Live Home Section D).
  'Unreconciled cash': {
    he: 'מזומן לא מיושב',
    ur: 'غیر تصفیہ شدہ نقدی',
    tl: 'Hindi pa naitutugmang cash',
  },
  'Dormant loan': { he: 'הלוואה רדומה', ur: 'غیر فعال قرض', tl: 'Tulog na pautang' },
  'Unused note': { he: 'הערה שלא בשימוש', ur: 'غیر استعمال شدہ نوٹ', tl: 'Hindi ginamit na tala' },
  'Delegate work': { he: 'האצלת עבודה', ur: 'کام سونپیں', tl: 'Italaga ang trabaho' },
  'Unusual spend': { he: 'הוצאה חריגה', ur: 'غیر معمولی خرچ', tl: 'Hindi pangkaraniwang gastos' },
  'New on the team': { he: 'חדש בצוות', ur: 'ٹیم میں نیا', tl: 'Bago sa team' },
  'Ready to pay': { he: 'מוכן לתשלום', ur: 'ادائیگی کے لیے تیار', tl: 'Handa nang bayaran' },
  'Not started': { he: 'לא התחיל', ur: 'شروع نہیں ہوا', tl: 'Hindi pa sinisimulan' },
  Checked: { he: 'נבדק', ur: 'جانچ لیا', tl: 'Nasuri' },
  'Start this month': {
    he: 'התחל את החודש',
    ur: 'یہ مہینہ شروع کریں',
    tl: 'Simulan ang buwang ito',
  },
  'Open payroll': { he: 'פתח שכר', ur: 'پے رول کھولیں', tl: 'Buksan ang payroll' },
  Total: { he: 'סה"כ', ur: 'کل', tl: 'Kabuuan' },
  Who: { he: 'מי', ur: 'کون', tl: 'Sino' },
  Category: { he: 'קטגוריה', ur: 'زمرہ', tl: 'Kategorya' },
  'Which month?': { he: 'איזה חודש?', ur: 'کون سا مہینہ؟', tl: 'Aling buwan?' },

  // === Tasks ===
  'To do': { he: 'לעשות', ur: 'کرنے کے لیے', tl: 'Gagawin' },
  Week: { he: 'שבוע', ur: 'ہفتہ', tl: 'Linggo' },
  Search: { he: 'חיפוש', ur: 'تلاش', tl: 'Maghanap' },
  'All work': { he: 'הכול', ur: 'تمام کام', tl: 'Lahat ng gawain' },
  Running: { he: 'בעבודה', ur: 'جاری', tl: 'Ginagawa' },
  'High priority': { he: 'דחוף', ur: 'اہم', tl: 'Mahalaga' },
  'Nothing open right now.': {
    he: 'אין כרגע שום דבר פתוח.',
    ur: 'ابھی کچھ کھلا نہیں ہے۔',
    tl: 'Walang bukas ngayon.',
  },
  'Nothing finished yet.': {
    he: 'עוד לא הסתיים כלום.',
    ur: 'ابھی کچھ مکمل نہیں ہوا۔',
    tl: 'Wala pang natatapos.',
  },
  'Late · due {{when}}': {
    he: 'מאחר · היה אמור {{when}}',
    ur: 'تاخیر · مقررہ {{when}}',
    tl: 'Huli · dapat {{when}}',
  },
  Working: { he: 'עובד/ת', ur: 'کام کر رہے ہیں', tl: 'Nagtatrabaho' },
  'Waiting for work': { he: 'מחכה לעבודה', ur: 'کام کا انتظار', tl: 'Naghihintay ng gawain' },
  '{{n}} to do': { he: '{{n}} לעשות', ur: '{{n}} کرنے ہیں', tl: '{{n}} ang gagawin' },
  'The week, on one screen': {
    he: 'השבוע, במסך אחד',
    ur: 'پورا ہفتہ، ایک اسکرین پر',
    tl: 'Ang linggo, sa isang screen',
  },
  'Set what repeats': {
    he: 'קבע מה חוזר',
    ur: 'طے کریں کیا دہرایا جائے',
    tl: 'Itakda kung ano ang paulit-ulit',
  },
  Nothing: { he: 'כלום', ur: 'کچھ نہیں', tl: 'Wala' },

  // === People ===
  Attention: { he: 'דורש טיפול', ur: 'توجہ طلب', tl: 'Kailangan ng atensyon' },
  Trial: { he: 'תקופת ניסיון', ur: 'آزمائشی', tl: 'Subok' },
  Managers: { he: 'בעלי הרשאות', ur: 'اختیار رکھنے والے', tl: 'May pahintulot' },
  'Working now': { he: 'עובדים עכשיו', ur: 'ابھی کام پر', tl: 'Nagtatrabaho ngayon' },
  'Everyone else': { he: 'כל השאר', ur: 'باقی سب', tl: 'Lahat ng iba' },
  'No position set': {
    he: 'לא הוגדר תפקיד',
    ur: 'عہدہ مقرر نہیں',
    tl: 'Walang nakatakdang posisyon',
  },
  // Position enum values — rendered raw from the API, so keys stay snake_case.
  driver: { he: 'נהג', ur: 'ڈرائیور', tl: 'Driver' },
  personal_assistant: {
    he: 'עוזר אישי',
    ur: 'ذاتی معاون',
    tl: 'Personal na katulong',
  },
  housemaid: { he: 'עוזרת בית', ur: 'گھریلو مددگار', tl: 'Katulong sa bahay' },
  gardener: { he: 'גנן', ur: 'باغبان', tl: 'Hardinero' },
  nanny: { he: 'בייביסיטר', ur: 'نینی', tl: 'Yaya' },
  cook: { he: 'טבח', ur: 'باورچی', tl: 'Tagapagluto' },
  other: { he: 'אחר', ur: 'دیگر', tl: 'Iba pa' },
  'Has not set their own password yet': {
    he: 'עוד לא קבע/ה סיסמה משלו/ה',
    ur: 'ابھی اپنا پاس ورڈ مقرر نہیں کیا',
    tl: 'Hindi pa naglalagay ng sariling password',
  },
  Access: { he: 'הרשאות', ur: 'رسائی', tl: 'Access' },
  "Today's progress": { he: 'ההתקדמות היום', ur: 'آج کی پیش رفت', tl: 'Progreso ngayon' },

  // === Person page ===
  'Person not found': { he: 'האדם לא נמצא', ur: 'فرد نہیں ملا', tl: 'Hindi natagpuan ang tao' },
  Call: { he: 'התקשר', ur: 'کال کریں', tl: 'Tawagan' },
  Message: { he: 'הודעה', ur: 'پیغام', tl: 'Mensahe' },
  Assign: { he: 'הטל משימה', ur: 'کام دیں', tl: 'Magbigay ng gawain' },
  Progress: { he: 'התקדמות', ur: 'پیش رفت', tl: 'Progreso' },
  'Their work': { he: 'העבודה שלו/ה', ur: 'ان کا کام', tl: 'Ang kanilang trabaho' },
  Pay: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  'Per day': { he: 'ליום', ur: 'یومیہ', tl: 'Kada araw' },
  'Per hour': { he: 'לשעה', ur: 'فی گھنٹہ', tl: 'Kada oras' },
  'On trial — no pay agreed yet': {
    he: 'בתקופת ניסיון — עוד לא סוכם שכר',
    ur: 'آزمائشی مدت — تنخواہ ابھی طے نہیں',
    tl: 'Nasa subok — wala pang napagkasunduang sahod',
  },
  'No pay set yet': {
    he: 'עוד לא נקבע שכר',
    ur: 'تنخواہ ابھی مقرر نہیں',
    tl: 'Wala pang nakatakdang sahod',
  },
  Account: { he: 'חשבון', ur: 'اکاؤنٹ', tl: 'Account' },

  // === Task page ===
  Status: { he: 'מצב', ur: 'حالت', tl: 'Katayuan' },
  Instructions: { he: 'הוראות', ur: 'ہدایات', tl: 'Mga tagubilin' },
  'Execution proof': { he: 'הוכחת ביצוע', ur: 'کام کا ثبوت', tl: 'Patunay ng pagkakagawa' },
  Reference: { he: 'רפרנס', ur: 'حوالہ', tl: 'Sanggunian' },
  Required: { he: 'חובה', ur: 'لازمی', tl: 'Kailangan' },
  'Add proof': { he: 'הוסף הוכחה', ur: 'ثبوت شامل کریں', tl: 'Magdagdag ng patunay' },
  Location: { he: 'מיקום', ur: 'مقام', tl: 'Lokasyon' },
  Updates: { he: 'עדכונים', ur: 'اپ ڈیٹس', tl: 'Mga update' },
  'Full history': { he: 'היסטוריה מלאה', ur: 'مکمل تاریخ', tl: 'Buong kasaysayan' },
  Related: { he: 'קשור', ur: 'متعلقہ', tl: 'Kaugnay' },
  'Send update…': { he: 'שלח עדכון…', ur: 'اپ ڈیٹ بھیجیں…', tl: 'Magpadala ng update…' },
  Actions: { he: 'פעולות', ur: 'کارروائیاں', tl: 'Mga aksyon' },
  'No deadline': { he: 'אין מועד יעד', ur: 'کوئی آخری وقت نہیں', tl: 'Walang takdang oras' },
  'Proof required': { he: 'נדרשת הוכחה', ur: 'ثبوت درکار ہے', tl: 'Kailangan ng patunay' },
  You: { he: 'אתה', ur: 'آپ', tl: 'Ikaw' },
  Unknown: { he: 'לא ידוע', ur: 'نامعلوم', tl: 'Hindi kilala' },
  Listen: { he: 'האזן', ur: 'سنیں', tl: 'Pakinggan' },
  '{{done}} of {{total}} steps done': {
    he: '{{done}} מתוך {{total}} שלבים הושלמו',
    ur: '{{total}} میں سے {{done}} مراحل مکمل',
    tl: '{{done}} sa {{total}} hakbang tapos na',
  },
  '1 proof sent': {
    he: 'הוכחה אחת נשלחה',
    ur: 'ایک ثبوت بھیجا گیا',
    tl: 'May 1 patunay na naipadala',
  },
  '{{n}} proofs sent': {
    he: '{{n}} הוכחות נשלחו',
    ur: '{{n}} ثبوت بھیجے گئے',
    tl: '{{n}} patunay ang naipadala',
  },
  behind: { he: 'באיחור', ur: 'تاخیر سے', tl: 'huli' },
  '1 message': { he: 'הודעה אחת', ur: 'ایک پیغام', tl: '1 mensahe' },
  '{{n}} messages': { he: '{{n}} הודעות', ur: '{{n}} پیغامات', tl: '{{n}} mensahe' },
  'Translated to {{lang}} for {{name}}': {
    he: 'תורגם ל{{lang}} עבור {{name}}',
    ur: '{{name}} کے لیے {{lang}} میں ترجمہ ہوا',
    tl: 'Isinalin sa {{lang}} para kay {{name}}',
  },
  '{{n}} min': { he: '{{n}} דק׳', ur: '{{n}} منٹ', tl: '{{n}} min' },
  '{{h}}h': { he: '{{h}} ש׳', ur: '{{h}} گھنٹے', tl: '{{h}}h' },
  '{{h}}h {{m}}m': { he: '{{h}} ש׳ {{m}} דק׳', ur: '{{h}} گھنٹے {{m}} منٹ', tl: '{{h}}h {{m}}m' },
  Due: { he: 'מועד', ur: 'مقررہ وقت', tl: 'Takda' },
  'Due today': { he: 'להיום', ur: 'آج تک', tl: 'Ngayong araw' },
  'Due tomorrow': { he: 'למחר', ur: 'کل تک', tl: 'Bukas' },

  // === Staff today ===
  'Coming next': { he: 'הבא בתור', ur: 'اگلا', tl: 'Susunod' },
  '{{n}} tasks today': { he: '{{n}} משימות היום', ur: 'آج {{n}} کام', tl: '{{n}} gawain ngayon' },
  '{{done}} of {{total}} tasks': {
    he: '{{done}} מתוך {{total}} משימות',
    ur: '{{total}} میں سے {{done}} کام',
    tl: '{{done}} sa {{total}} gawain',
  },
  'Completed today': { he: 'הושלמו היום', ur: 'آج مکمل ہوئے', tl: 'Natapos ngayong araw' },
  'Report issue': { he: 'דיווח על תקלה', ur: 'مسئلہ رپورٹ کریں', tl: 'Mag-ulat ng problema' },
  'What is wrong?': { he: 'מה הבעיה?', ur: 'کیا مسئلہ ہے؟', tl: 'Ano ang problema?' },
  'Report something': { he: 'דיווח על משהו', ur: 'کچھ رپورٹ کریں', tl: 'Mag-ulat ng kahit ano' },
  'Not about a specific task': {
    he: 'לא קשור למשימה מסוימת',
    ur: 'کسی خاص کام کے بارے میں نہیں',
    tl: 'Hindi tungkol sa partikular na trabaho',
  },
  'What is going on?': { he: 'מה קורה?', ur: 'کیا ہو رہا ہے؟', tl: 'Ano ang nangyayari?' },
  'Start work': { he: 'התחל לעבוד', ur: 'کام شروع کریں', tl: 'Simulan ang trabaho' },
  'Photo proof required': {
    he: 'נדרשת תמונה כהוכחה',
    ur: 'تصویری ثبوت لازمی ہے',
    tl: 'Kailangan ng larawang patunay',
  },
  'Was due': { he: 'היה אמור', ur: 'مقررہ وقت تھا', tl: 'Dapat noong' },
  'Start at {{time}}': {
    he: 'להתחיל ב־{{time}}',
    ur: '{{time}} پر شروع',
    tl: 'Simulan sa {{time}}',
  },
  'No time set': { he: 'לא נקבעה שעה', ur: 'وقت مقرر نہیں', tl: 'Walang oras na nakatakda' },

  // === Staff history ===
  Filter: { he: 'סינון', ur: 'فلٹر', tl: 'Salain' },
  'This week': { he: 'השבוע', ur: 'اس ہفتے', tl: 'Ngayong linggo' },
  'On time': { he: 'בזמן', ur: 'وقت پر', tl: 'Nasa oras' },
  '1 task': { he: 'משימה אחת', ur: 'ایک کام', tl: '1 gawain' },
  '{{n}} tasks': { he: '{{n}} משימות', ur: '{{n}} کام', tl: '{{n}} gawain' },
  'Search your work…': {
    he: 'חיפוש בעבודה שלך…',
    ur: 'اپنے کام میں تلاش کریں…',
    tl: 'Hanapin ang iyong trabaho…',
  },
  'Nothing matches that.': {
    he: 'אין תוצאות מתאימות.',
    ur: 'اس سے کچھ میل نہیں کھاتا۔',
    tl: 'Walang tumugma.',
  },
  'Proof accepted': { he: 'ההוכחה התקבלה', ur: 'ثبوت قبول ہوا', tl: 'Tinanggap ang patunay' },
  'Finished late ({{time}})': {
    he: 'הסתיים באיחור ({{time}})',
    ur: 'دیر سے مکمل ({{time}})',
    tl: 'Natapos nang huli ({{time}})',
  },
  '{{state}} at {{time}}': {
    he: '{{state}} ב־{{time}}',
    ur: '{{time}} پر {{state}}',
    tl: '{{state}} sa {{time}}',
  },

  // === My pay ===
  'Not opened yet': { he: 'עוד לא נפתח', ur: 'ابھی نہیں کھلا', tl: 'Hindi pa binubuksan' },
  'Being prepared': { he: 'בהכנה', ur: 'تیار ہو رہا ہے', tl: 'Inihahanda' },
  'Total earned this month': {
    he: 'סך ההשתכרות החודש',
    ur: 'اس مہینے کی کل کمائی',
    tl: 'Kabuuang kinita ngayong buwan',
  },
  'Your agreed rate': { he: 'השכר שסוכם', ur: 'طے شدہ تنخواہ', tl: 'Napagkasunduang sahod' },
  'Month progress': { he: 'התקדמות החודש', ur: 'مہینے کی پیش رفت', tl: 'Progreso ng buwan' },
  'Day {{d}} of {{n}}': {
    he: 'יום {{d}} מתוך {{n}}',
    ur: '{{n}} میں سے دن {{d}}',
    tl: 'Araw {{d}} sa {{n}}',
  },
  'Pay breakdown': { he: 'פירוט השכר', ur: 'تنخواہ کی تفصیل', tl: 'Detalye ng sahod' },
  'Hours worked': { he: 'שעות עבודה', ur: 'کام کے گھنٹے', tl: 'Oras na trabaho' },
  Month: { he: 'חודש', ur: 'مہینہ', tl: 'Buwan' },
  '{{h}}h of that is overtime': {
    he: '{{h}} שעות מתוכן שעות נוספות',
    ur: 'ان میں سے {{h}} گھنٹے اوور ٹائم ہیں',
    tl: '{{h}}h nito ay overtime',
  },
  'Hide the days': { he: 'הסתר את הימים', ur: 'دن چھپائیں', tl: 'Itago ang mga araw' },
  'Show every day': { he: 'הצג כל יום', ur: 'ہر دن دکھائیں', tl: 'Ipakita ang bawat araw' },
  'Leave balance': { he: 'יתרת חופשה', ur: 'چھٹیوں کا توازن', tl: 'Natitirang bakasyon' },
  'Vacation remaining': { he: 'ימי חופשה שנותרו', ur: 'باقی چھٹیاں', tl: 'Natitirang bakasyon' },
  '{{n}} days': { he: '{{n}} ימים', ur: '{{n}} دن', tl: '{{n}} araw' },
  'Book a day off': {
    he: 'בקשת יום חופש',
    ur: 'چھٹی کا دن رکھیں',
    tl: 'Mag-book ng araw ng pahinga',
  },
  'Past payslips': { he: 'תלושים קודמים', ur: 'پچھلی تنخواہ پرچیاں', tl: 'Mga nakaraang payslip' },
  '{{amount}} still owed': {
    he: 'נותרו {{amount}}',
    ur: '{{amount}} ابھی باقی',
    tl: '{{amount}} pa ang utang',
  },

  // === More / notifications / house rules ===
  House: { he: 'הבית', ur: 'گھر', tl: 'Bahay' },
  'Running the house': { he: 'ניהול הבית', ur: 'گھر چلانا', tl: 'Pagpapatakbo ng bahay' },
  'Emergency contacts': {
    he: 'טלפונים לחירום',
    ur: 'ہنگامی رابطے',
    tl: 'Mga contact sa emergency',
  },
  'My profile': { he: 'הפרופיל שלי', ur: 'میری پروفائل', tl: 'Aking profile' },
  Unread: { he: 'לא נקראו', ur: 'اَن پڑھے', tl: 'Hindi pa nababasa' },
  'Search the rules…': {
    he: 'חיפוש בכללים…',
    ur: 'قواعد میں تلاش کریں…',
    tl: 'Hanapin sa mga panuntunan…',
  },
  'All rules': { he: 'כל הכללים', ur: 'تمام قواعد', tl: 'Lahat ng panuntunan' },
  Everyone: { he: 'כולם', ur: 'سب', tl: 'Lahat' },

  // === Profile ===
  Owner: { he: 'בעלים', ur: 'مالک', tl: 'May-ari' },
  'Personal information': { he: 'פרטים אישיים', ur: 'ذاتی معلومات', tl: 'Personal na impormasyon' },
  'Email address': { he: 'כתובת אימייל', ur: 'ای میل پتہ', tl: 'Email address' },
  'Phone number': { he: 'מספר טלפון', ur: 'فون نمبر', tl: 'Numero ng telepono' },
  'Not set': { he: 'לא הוגדר', ur: 'مقرر نہیں', tl: 'Hindi nakatakda' },
  'Edit personal details': {
    he: 'עריכת הפרטים האישיים',
    ur: 'ذاتی تفصیلات میں ترمیم',
    tl: 'I-edit ang personal na detalye',
  },
  Preferences: { he: 'העדפות', ur: 'ترجیحات', tl: 'Mga kagustuhan' },
  'App language': { he: 'שפת האפליקציה', ur: 'ایپ کی زبان', tl: 'Wika ng app' },
  'Navigation app': { he: 'אפליקציית ניווט', ur: 'نیویگیشن ایپ', tl: 'Navigation app' },
  On: { he: 'פעיל', ur: 'آن', tl: 'Naka-on' },
  Off: { he: 'כבוי', ur: 'آف', tl: 'Naka-off' },
  Security: { he: 'אבטחה', ur: 'سیکیورٹی', tl: 'Seguridad' },
  'Push notifications': { he: 'התראות דחיפה', ur: 'پش نوٹیفیکیشنز', tl: 'Push notifications' },

  // === Event page ===
  'Happening now': { he: 'קורה עכשיו', ur: 'ابھی جاری ہے', tl: 'Nangyayari ngayon' },
  Over: { he: 'הסתיים', ur: 'ختم', tl: 'Tapos na' },
  Kind: { he: 'סוג', ur: 'قسم', tl: 'Uri' },
  'Before it starts': { he: 'לפני שזה מתחיל', ur: 'شروع ہونے سے پہلے', tl: 'Bago magsimula' },
  'While it runs': { he: 'בזמן האירוע', ur: 'دوران', tl: 'Habang tumatakbo' },
  'People involved': { he: 'מי מעורב', ur: 'شامل لوگ', tl: 'Mga taong kasama' },
  '1 person': { he: 'אדם אחד', ur: 'ایک فرد', tl: '1 tao' },

  // === Sign in / setup ===
  'The art of household management.': {
    he: 'האמנות של ניהול הבית.',
    ur: 'گھر کے انتظام کا فن۔',
    tl: 'Ang sining ng pamamahala ng tahanan.',
  },
  'Enter details': { he: 'הזן פרטים', ur: 'تفصیلات درج کریں', tl: 'Ilagay ang detalye' },
  'One more thing': { he: 'עוד דבר אחד', ur: 'ایک اور بات', tl: 'Isa pang bagay' },
  'Your household': { he: 'משק הבית שלך', ur: 'آپ کا گھرانہ', tl: 'Ang iyong sambahayan' },
  'Welcome back': { he: 'ברוך שובך', ur: 'خوش آمدید', tl: 'Maligayang pagbabalik' },
  'Sign in to your household': {
    he: 'התחבר למשק הבית שלך',
    ur: 'اپنے گھرانے میں سائن ان کریں',
    tl: 'Mag-sign in sa iyong sambahayan',
  },
  'New here?': { he: 'חדש כאן?', ur: 'یہاں نئے ہیں؟', tl: 'Bago ka dito?' },
  'Create your household': {
    he: 'צור את משק הבית שלך',
    ur: 'اپنا گھرانہ بنائیں',
    tl: 'Gumawa ng sambahayan mo',
  },
  'Protected with two-factor authentication': {
    he: 'מוגן באימות דו-שלבי',
    ur: 'دو مرحلہ تصدیق سے محفوظ',
    tl: 'Protektado ng two-factor authentication',
  },
  'Your house,': { he: 'הבית שלך,', ur: 'آپ کا گھر،', tl: 'Ang bahay mo,' },
  'running itself.': { he: 'מתנהל בעצמו.', ur: 'خود چلتا ہوا۔', tl: 'tumatakbong mag-isa.' },
  'Already have an account?': {
    he: 'כבר יש לך חשבון?',
    ur: 'پہلے سے اکاؤنٹ ہے؟',
    tl: 'May account ka na?',
  },
  'Create household': { he: 'צור משק בית', ur: 'گھرانہ بنائیں', tl: 'Gumawa ng sambahayan' },
  '1 of 1 — almost there': {
    he: '1 מתוך 1 — כמעט שם',
    ur: '1 از 1 — تقریباً ہو گیا',
    tl: '1 ng 1 — malapit na',
  },
  'A few things about your home': {
    he: 'כמה דברים על הבית שלך',
    ur: 'آپ کے گھر کے بارے میں چند باتیں',
    tl: 'Ilang bagay tungkol sa bahay mo',
  },
  'Start running my house': {
    he: 'התחל לנהל את הבית שלי',
    ur: 'میرا گھر چلانا شروع کریں',
    tl: 'Simulan ang pagpapatakbo ng bahay ko',
  },
  'Skip for now': { he: 'דלג לעת עתה', ur: 'ابھی چھوڑیں', tl: 'Laktawan muna' },
  "You're invited": { he: 'הוזמנת', ur: 'آپ کو دعوت ہے', tl: 'Inanyayahan ka' },
  'Welcome to {{name}}': {
    he: 'ברוך הבא ל{{name}}',
    ur: '{{name}} میں خوش آمدید',
    tl: 'Maligayang pagdating sa {{name}}',
  },
  'Choose a password to get started.': {
    he: 'בחר סיסמה כדי להתחיל.',
    ur: 'شروع کرنے کے لیے پاس ورڈ منتخب کریں۔',
    tl: 'Pumili ng password para makapagsimula.',
  },
  'Join the household': {
    he: 'הצטרף למשק הבית',
    ur: 'گھرانے میں شامل ہوں',
    tl: 'Sumali sa sambahayan',
  },
  'Household invitation': {
    he: 'הזמנה למשק בית',
    ur: 'گھرانے کی دعوت',
    tl: 'Imbitasyon sa sambahayan',
  },
  'Run the house together': {
    he: 'לנהל את הבית ביחד',
    ur: 'مل کر گھر چلائیں',
    tl: 'Patakbuhin ang bahay nang sabay',
  },
  '{{inviter}} invited you to co-own {{household}} — full access to operations, money and staff.': {
    he: '{{inviter}} הזמין אותך להיות שותף ב{{household}} — גישה מלאה לפעולות, כסף וסגל.',
    ur: '{{inviter}} نے آپ کو {{household}} کا شریک مالک بننے کی دعوت دی — عملیات، رقم اور عملہ تک مکمل رسائی۔',
    tl: 'Inanyayahan ka ni {{inviter}} na maging co-owner ng {{household}} — buong access sa operations, pera at staff.',
  },
  'Accept and join': { he: 'אשר והצטרף', ur: 'قبول کریں اور شامل ہوں', tl: 'Tanggapin at sumali' },
  'This link expires on {{date}}': {
    he: 'הקישור פג תוקף ב{{date}}',
    ur: 'یہ لنک {{date}} کو ختم ہو جاتا ہے',
    tl: 'Mag-e-expire ang link na ito sa {{date}}',
  },
  // === Toasts, undo, handover ===
  Undo: { he: 'ביטול', ur: 'واپس', tl: 'I-undo' },
  Unpinned: { he: 'הוסר מהצמדה', ur: 'پن ہٹا دیا', tl: 'Inalis sa pin' },
  'I have written it down': {
    he: 'רשמתי',
    ur: 'میں نے لکھ لیا',
    tl: 'Naisulat ko na',
  },
  Generate: { he: 'צור', ur: 'بنائیں', tl: 'Bumuo' },
  'Their current password stops working the moment you do this.': {
    he: 'הסיסמה הנוכחית שלהם מפסיקה לעבוד ברגע שתעשה את זה.',
    ur: 'ایسا کرتے ہی ان کا موجودہ پاس ورڈ کام کرنا بند کر دے گا۔',
    tl: 'Titigil na gumana ang kasalukuyang password nila sa sandaling gawin mo ito.',
  },

  'Four answers and you are in.': {
    he: 'ארבע תשובות ואתה בפנים.',
    ur: 'چار جواب اور آپ اندر ہیں۔',
    tl: 'Apat na sagot at pasok ka na.',
  },

  // === Activity log ===
  Activity: { he: 'פעילות', ur: 'سرگرمی', tl: 'Aktibidad' },
  'Activity log': { he: 'יומן פעילות', ur: 'سرگرمی کا ریکارڈ', tl: 'Tala ng aktibidad' },
  'Everything that happened in this household, and who did it.': {
    he: 'כל מה שקרה בבית הזה, ומי עשה את זה.',
    ur: 'اس گھر میں جو کچھ ہوا، اور کس نے کیا۔',
    tl: 'Lahat ng nangyari sa tahanang ito, at kung sino ang gumawa.',
  },
  'Nothing recorded yet.': {
    he: 'עדיין לא נרשם דבר.',
    ur: 'ابھی تک کچھ درج نہیں ہوا۔',
    tl: 'Wala pang naitala.',
  },
  'Someone no longer here': {
    he: 'מישהו שכבר לא כאן',
    ur: 'کوئی جو اب یہاں نہیں',
    tl: 'Isang taong wala na rito',
  },
  'Showing the most recent 200 entries.': {
    he: 'מוצגות 200 הרשומות האחרונות.',
    ur: 'حالیہ 200 اندراجات دکھائے جا رہے ہیں۔',
    tl: 'Ipinapakita ang pinakahuling 200 tala.',
  },
  // Not "Access" — that key already means the permission switches.
  'Sign-ins': { he: 'כניסות', ur: 'سائن ان', tl: 'Mga sign-in' },
  Changes: { he: 'שינויים', ur: 'تبدیلیاں', tl: 'Mga pagbabago' },
  Work: { he: 'עבודה', ur: 'کام', tl: 'Trabaho' },
  created: { he: 'יצר', ur: 'بنایا', tl: 'gumawa' },
  changed: { he: 'שינה', ur: 'تبدیل کیا', tl: 'binago' },
  removed: { he: 'מחק', ur: 'ہٹایا', tl: 'inalis' },
  moved: { he: 'קידם', ur: 'منتقل کیا', tl: 'inilipat' },
  'signed in': { he: 'נכנס', ur: 'سائن ان ہوا', tl: 'nag-sign in' },
  'signed out': { he: 'יצא', ur: 'سائن آؤٹ ہوا', tl: 'nag-sign out' },
  invited: { he: 'הזמין', ur: 'دعوت دی', tl: 'nag-imbita' },
  'reset a password': {
    he: 'איפס סיסמה',
    ur: 'پاس ورڈ ری سیٹ کیا',
    tl: 'nag-reset ng password',
  },
  translated: { he: 'תרגם', ur: 'ترجمہ کیا', tl: 'isinalin' },
  person: { he: 'אדם', ur: 'شخص', tl: 'tao' },
  household: { he: 'בית', ur: 'گھرانہ', tl: 'sambahayan' },
  routine: { he: 'שגרה', ur: 'معمول', tl: 'rutina' },
  receipt: { he: 'קבלה', ur: 'رسید', tl: 'resibo' },
  loan: { he: 'הלוואה', ur: 'قرض', tl: 'utang' },
  reimbursement: { he: 'החזר', ur: 'واپسی', tl: 'reimbursement' },
  payslip: { he: 'תלוש', ur: 'تنخواہ پرچی', tl: 'payslip' },
  'staff invite': { he: 'הזמנת עובד', ur: 'عملے کی دعوت', tl: 'imbitasyon sa tauhan' },
  'co-owner invite': {
    he: 'הזמנת בעלים שותף',
    ur: 'شریک مالک کی دعوت',
    tl: 'imbitasyon sa co-owner',
  },
  'IP address': { he: 'כתובת IP', ur: 'آئی پی ایڈریس', tl: 'IP address' },
  Device: { he: 'מכשיר', ur: 'آلہ', tl: 'Device' },

  // === Deciding from the inbox ===
  'Send rejection': { he: 'שלח דחייה', ur: 'رد بھیجیں', tl: 'Ipadala ang pagtanggi' },

  // === Bills the household already knows about ===
  'Coming up': { he: 'בקרוב', ur: 'آنے والا', tl: 'Paparating' },
  'Add a bill': { he: 'הוסף חשבון', ur: 'بل شامل کریں', tl: 'Magdagdag ng bayarin' },
  'One-off': { he: 'חד-פעמי', ur: 'ایک بار', tl: 'Minsanan' },
  'Every month': { he: 'כל חודש', ur: 'ہر مہینے', tl: 'Buwan-buwan' },
  'Every quarter': { he: 'כל רבעון', ur: 'ہر سہ ماہی', tl: 'Kada quarter' },
  'Every year': { he: 'כל שנה', ur: 'ہر سال', tl: 'Taon-taon' },
  'In {{n}} days': { he: 'בעוד {{n}} ימים', ur: '{{n}} دن میں', tl: 'Sa {{n}} araw' },
  'Marked paid': { he: 'סומן כשולם', ur: 'ادا شدہ نشان زد', tl: 'Namarkahang bayad' },
  Removed: { he: 'הוסר', ur: 'ہٹا دیا', tl: 'Inalis' },
  Added: { he: 'נוסף', ur: 'شامل ہو گیا', tl: 'Naidagdag' },
  'Nothing written down yet.': {
    he: 'עדיין לא נרשם כלום.',
    ur: 'ابھی کچھ درج نہیں کیا گیا۔',
    tl: 'Wala pang naisulat.',
  },
  'The pool service, a visa renewal — anything the app cannot work out on its own.': {
    he: 'שירות הבריכה, חידוש ויזה — כל מה שהאפליקציה לא יכולה להסיק לבד.',
    ur: 'پول سروس، ویزا کی تجدید — ہر وہ چیز جو ایپ خود اندازہ نہیں لگا سکتی۔',
    tl: 'Ang pool service, pag-renew ng visa — anumang hindi kayang hulaan ng app.',
  },
  'What is it? e.g. Pool service': {
    he: 'מה זה? למשל שירות בריכה',
    ur: 'یہ کیا ہے؟ مثلاً پول سروس',
    tl: 'Ano ito? hal. Pool service',
  },

  // === End of service ===
  'If everyone left today': {
    he: 'אם כולם היו עוזבים היום',
    ur: 'اگر سب آج چلے جائیں',
    tl: 'Kung aalis lahat ngayon',
  },
  'End of service (estimate)': {
    he: 'גמר חשבון (הערכה)',
    ur: 'اختتامِ ملازمت (تخمینہ)',
    tl: 'Katapusan ng serbisyo (tantiya)',
  },
  'End of service': {
    he: 'גמר חשבון',
    ur: 'اختتامِ ملازمت',
    tl: 'Katapusan ng serbisyo',
  },
  '{{d}} days a year, after {{q}} days': {
    he: '{{d}} ימים בשנה, אחרי {{q}} ימים',
    ur: 'سالانہ {{d}} دن، {{q}} دن کے بعد',
    tl: '{{d}} araw bawat taon, pagkatapos ng {{q}} araw',
  },
  '{{n}} yrs': { he: '{{n}} שנים', ur: '{{n}} سال', tl: '{{n}} taon' },
  // === Payslip you can keep ===
  'Save as PDF': {
    he: 'שמירה כ-PDF',
    ur: 'پی ڈی ایف کے طور پر محفوظ کریں',
    tl: 'I-save bilang PDF',
  },
  'Payslip not found': {
    he: 'התלוש לא נמצא',
    ur: 'پرچی نہیں ملی',
    tl: 'Hindi natagpuan ang payslip',
  },
  Earnings: { he: 'תשלומים', ur: 'کمائی', tl: 'Kita' },
  Absence: { he: 'היעדרות', ur: 'غیر حاضری', tl: 'Pagliban' },
  Adjustments: { he: 'התאמות', ur: 'ایڈجسٹمنٹ', tl: 'Mga pagsasaayos' },
  Advance: { he: 'מקדמה', ur: 'پیشگی', tl: 'Paunang bayad' },
  'Not yet paid': { he: 'טרם שולם', ur: 'ابھی ادا نہیں ہوا', tl: 'Hindi pa nababayaran' },

  'Saved — it will send when you have signal.': {
    he: 'נשמר — יישלח כשתהיה קליטה.',
    ur: 'محفوظ ہو گیا — سگنل آنے پر بھیج دیا جائے گا۔',
    tl: 'Naka-save — ipapadala kapag may signal.',
  },
  '{{n}} waiting to send': {
    he: '{{n}} ממתינים לשליחה',
    ur: '{{n}} بھیجنے کے منتظر',
    tl: '{{n}} ang naghihintay maipadala',
  },

  // === Sync status (More) — queued vs failed vs gone, never blurred ===
  'Sync status': { he: 'סטטוס סנכרון', ur: 'سینک کی صورتحال', tl: 'Katayuan ng sync' },
  "Couldn't send": { he: 'לא נשלח', ur: 'نہیں بھیجا جا سکا', tl: 'Hindi naipadala' },
  '{{n}} could not be sent': {
    he: '{{n}} לא נשלחו',
    ur: '{{n}} نہیں بھیجے جا سکے',
    tl: '{{n}} ang hindi naipadala',
  },
  Discard: { he: 'מחיקה', ur: 'حذف کریں', tl: 'Alisin' },
  'Delete the actions that could not be sent? This cannot be undone.': {
    he: 'למחוק את הפעולות שלא נשלחו? אי אפשר לבטל את זה.',
    ur: 'وہ کارروائیاں حذف کریں جو نہیں بھیجی جا سکیں؟ یہ واپس نہیں ہو سکتا۔',
    tl: 'Tanggalin ang mga aksyong hindi naipadala? Hindi na ito mababawi.',
  },
  'The photo or voice note is gone from this phone — capture it again.': {
    he: 'התמונה או ההקלטה נמחקו מהטלפון — צלמו שוב.',
    ur: 'تصویر یا وائس نوٹ اس فون سے غائب ہے — دوبارہ بنائیں۔',
    tl: 'Nawala ang larawan o voice note sa teleponong ito — kunan ulit.',
  },
  'Kept on this phone — retry from More': {
    he: 'נשמר בטלפון — נסו שוב מ"עוד"',
    ur: 'فون پر محفوظ ہے — "مزید" سے دوبارہ کوشش کریں',
    tl: 'Naka-save sa telepono — subukan muli mula sa More',
  },

  Appearance: { he: 'מראה', ur: 'ظاہری شکل', tl: 'Anyo' },
  Light: { he: 'בהיר', ur: 'روشن', tl: 'Maliwanag' },
  Dark: { he: 'כהה', ur: 'گہرا', tl: 'Madilim' },
  'Match my phone': {
    he: 'לפי הטלפון',
    ur: 'میرے فون کے مطابق',
    tl: 'Sundan ang telepono',
  },

  // === Clock in, clock out ===
  "I'm here": { he: 'הגעתי', ur: 'میں یہاں ہوں', tl: 'Nandito na ako' },
  "I'm done": { he: 'סיימתי', ur: 'میں فارغ ہوں', tl: 'Tapos na ako' },
  'Tap when you arrive': {
    he: 'הקש כשאת מגיעה',
    ur: 'پہنچنے پر ٹیپ کریں',
    tl: 'I-tap pagdating mo',
  },
  'Working for {{time}}': {
    he: 'בעבודה כבר {{time}}',
    ur: '{{time}} سے کام پر',
    tl: 'Nagtatrabaho nang {{time}}',
  },
  'Clocked in': { he: 'נרשמה כניסה', ur: 'حاضری درج ہوئی', tl: 'Naka-clock in' },
  'Clocked out': { he: 'נרשמה יציאה', ur: 'روانگی درج ہوئی', tl: 'Naka-clock out' },
  'Waiting to send': { he: 'ממתין לשליחה', ur: 'بھیجنے کا منتظر', tl: 'Naghihintay ipadala' },
  'Will send when you have signal': {
    he: 'יישלח כשתהיה קליטה',
    ur: 'سگنل آنے پر بھیج دیا جائے گا',
    tl: 'Ipapadala kapag may signal',
  },

  // === Search, and doing several at once ===
  'People, tasks, rules, money…': {
    he: 'אנשים, משימות, כללים, כסף…',
    ur: 'لوگ، کام، اصول، پیسہ…',
    tl: 'Mga tao, gawain, panuntunan, pera…',
  },
  'Type at least two letters.': {
    he: 'הקלד לפחות שתי אותיות.',
    ur: 'کم از کم دو حروف لکھیں۔',
    tl: 'Mag-type ng hindi bababa sa dalawang letra.',
  },
  Receipts: { he: 'קבלות', ur: 'رسیدیں', tl: 'Mga resibo' },
  Events: { he: 'אירועים', ur: 'تقریبات', tl: 'Mga kaganapan' },
  'Select several': { he: 'בחירה מרובה', ur: 'کئی منتخب کریں', tl: 'Pumili ng ilan' },
  '{{n}} selected': { he: '{{n}} נבחרו', ur: '{{n}} منتخب', tl: '{{n}} ang napili' },
  '{{n}} updated': { he: '{{n}} עודכנו', ur: '{{n}} اپ ڈیٹ ہوئے', tl: '{{n}} ang na-update' },
  '{{done}} updated, {{failed}} could not be': {
    he: '{{done}} עודכנו, {{failed}} לא הצליחו',
    ur: '{{done}} اپ ڈیٹ ہوئے، {{failed}} نہیں ہو سکے',
    tl: '{{done}} ang na-update, {{failed}} ang hindi',
  },
  'Pick some tasks.': {
    he: 'בחר כמה משימות.',
    ur: 'کچھ کام منتخب کریں۔',
    tl: 'Pumili ng ilang gawain.',
  },
  'These are in different states — nothing applies to all of them.': {
    he: 'הן במצבים שונים — אין פעולה שמתאימה לכולן.',
    ur: 'یہ مختلف حالتوں میں ہیں — کوئی عمل سب پر لاگو نہیں ہوتا۔',
    tl: 'Magkakaiba ang kalagayan nila — walang aksyong akma sa lahat.',
  },
  'Cancelled in bulk': {
    he: 'בוטלו יחד',
    ur: 'اجتماعی طور پر منسوخ',
    tl: 'Kinansela nang sabay-sabay',
  },

  // === Your data ===
  'Your data': { he: 'הנתונים שלך', ur: 'آپ کا ڈیٹا', tl: 'Ang iyong data' },
  'Download everything': {
    he: 'הורדה של הכול',
    ur: 'سب کچھ ڈاؤن لوڈ کریں',
    tl: 'I-download ang lahat',
  },
  'One file with every task, receipt, payslip and message.': {
    he: 'קובץ אחד עם כל משימה, קבלה, תלוש והודעה.',
    ur: 'ایک فائل جس میں ہر کام، رسید، تنخواہ پرچی اور پیغام ہے۔',
    tl: 'Isang file na may bawat gawain, resibo, payslip at mensahe.',
  },
  Downloaded: { he: 'הורד', ur: 'ڈاؤن لوڈ ہو گیا', tl: 'Na-download' },
  'Export failed': { he: 'הייצוא נכשל', ur: 'ایکسپورٹ ناکام', tl: 'Nabigo ang pag-export' },
  'Download my data': {
    he: 'הורדת הנתונים שלי',
    ur: 'میرا ڈیٹا ڈاؤن لوڈ کریں',
    tl: 'I-download ang aking data',
  },
  'Your profile, messages, tasks, proof and pay — in one file.': {
    he: 'הפרופיל, ההודעות, המשימות, ההוכחות והשכר שלך — בקובץ אחד.',
    ur: 'آپ کی پروفائل، پیغامات، کام، ثبوت اور تنخواہ — ایک فائل میں۔',
    tl: 'Ang iyong profile, mensahe, gawain, patunay at sahod — sa isang file.',
  },
  'Delete this household': {
    he: 'מחיקת משק הבית',
    ur: 'یہ گھرانہ حذف کریں',
    tl: 'Burahin ang sambahayang ito',
  },
  'Everything, for everyone, permanently. There is no undo.': {
    he: 'הכול, לכולם, לצמיתות. אין דרך חזרה.',
    ur: 'سب کچھ، سب کے لیے، ہمیشہ کے لیے۔ واپسی ممکن نہیں۔',
    tl: 'Lahat, para sa lahat, tuluyan. Walang balikan.',
  },
  'Download a copy first — this cannot be reversed.': {
    he: 'הורד עותק קודם — אי אפשר לבטל את זה.',
    ur: 'پہلے ایک کاپی ڈاؤن لوڈ کریں — یہ واپس نہیں ہو سکتا۔',
    tl: 'Mag-download muna ng kopya — hindi ito mababawi.',
  },
  'Type the household name': {
    he: 'הקלד את שם משק הבית',
    ur: 'گھرانے کا نام لکھیں',
    tl: 'I-type ang pangalan ng sambahayan',
  },
  'Your password': { he: 'הסיסמה שלך', ur: 'آپ کا پاس ورڈ', tl: 'Ang iyong password' },

  '{{n}} people are missing a start date or pay terms, so they are not counted.': {
    he: 'ל-{{n}} אנשים חסר תאריך תחילה או תנאי שכר, ולכן הם לא נספרים.',
    ur: '{{n}} افراد کی تاریخِ آغاز یا تنخواہ کی شرائط موجود نہیں، اس لیے وہ شمار نہیں ہوئے۔',
    tl: '{{n}} tao ang walang petsa ng simula o kasunduan sa sahod, kaya hindi sila binibilang.',
  },

  // Billing
  Billing: { he: 'חיוב', ur: 'بلنگ', tl: 'Billing' },
  Subscription: { he: 'מנוי', ur: 'رکنیت', tl: 'Subscription' },
  'Payment failed': { he: 'התשלום נכשל', ur: 'ادائیگی ناکام ہوگئی', tl: 'Nabigo ang bayad' },
  Canceled: { he: 'בוטל', ur: 'منسوخ', tl: 'Kinansela' },
  'No subscription': { he: 'אין מנוי', ur: 'کوئی رکنیت نہیں', tl: 'Walang subscription' },
  'Renews on': { he: 'מתחדש ב-', ur: 'تجدید بتاریخ', tl: 'Mare-renew sa' },
  'Cancels on': { he: 'מסתיים ב-', ur: 'ختم ہوگا بتاریخ', tl: 'Matatapos sa' },
  Subscribe: { he: 'הרשמה למנוי', ur: 'سبسکرائب کریں', tl: 'Mag-subscribe' },
  'Manage billing': { he: 'ניהול חיוב', ur: 'بلنگ کا انتظام', tl: 'Pamahalaan ang billing' },
  'Subscription updated.': {
    he: 'המנוי עודכן.',
    ur: 'رکنیت اپ ڈیٹ ہوگئی۔',
    tl: 'Na-update ang subscription.',
  },
  'Checkout canceled.': {
    he: 'התהליך בוטל.',
    ur: 'چیک آؤٹ منسوخ ہوگیا۔',
    tl: 'Kinansela ang checkout.',
  },
  'Billing is not set up on this server yet.': {
    he: 'החיוב עדיין לא הוגדר בשרת הזה.',
    ur: 'اس سرور پر بلنگ ابھی سیٹ اپ نہیں ہوئی۔',
    tl: 'Hindi pa naka-set up ang billing sa server na ito.',
  },

  // === Landing page ===
  'A calmer morning in under two minutes.': {
    he: 'בוקר רגוע יותר בפחות משתי דקות.',
    ur: 'دو منٹ سے بھی کم میں ایک پرسکون صبح۔',
    tl: 'Mas kalmadong umaga sa loob ng dalawang minuto.',
  },
  'Bring calm home first.': {
    he: 'קודם מביאים רוגע הביתה.',
    ur: 'پہلے گھر میں سکون لائیں۔',
    tl: 'Iuwi muna ang kalmahan.',
  },
  'The private operating system for your home': {
    he: 'מערכת ההפעלה הפרטית של הבית',
    ur: 'آپ کے گھر کا نجی آپریٹنگ سسٹم',
    tl: 'Ang pribadong operating system ng inyong tahanan',
  },
  'Not another to-do app. The operating layer for your home.': {
    he: 'לא עוד אפליקציית משימות. שכבת ההפעלה של הבית.',
    ur: 'کوئی اور ٹو ڈو ایپ نہیں۔ آپ کے گھر کی آپریٹنگ پرت۔',
    tl: 'Hindi lang isa pang to-do app. Ang operating layer ng inyong tahanan.',
  },
  'Put the household somewhere other than your head.': {
    he: 'תוציאו את משק הבית מהראש שלכם.',
    ur: 'گھر کو اپنے سر سے باہر کسی جگہ رکھیں۔',
    tl: 'Ilagay ang sambahayan sa ibang lugar, hindi lang sa isip mo.',
  },
  'Your household, in sync.': {
    he: 'משק הבית שלכם, מסונכרן.',
    ur: 'آپ کا گھر، ہم آہنگ۔',
    tl: 'Ang inyong sambahayan, naka-sync.',
  },
  'WhatsApp was never meant to run a home.': {
    he: 'WhatsApp מעולם לא נועד לנהל בית.',
    ur: 'WhatsApp کبھی گھر چلانے کے لیے نہیں بنی تھی۔',
    tl: 'Ang WhatsApp ay hindi kailanman ginawa para magpatakbo ng tahanan.',
  },
  'Instructions disappear beneath the next message.': {
    he: 'ההוראות נקברות מתחת להודעה הבאה.',
    ur: 'ہدایات اگلے پیغام کے نیچے دب جاتی ہیں۔',
    tl: 'Nalulunod ang mga tagubilin sa susunod na mensahe.',
  },
  'Chats lose context': {
    he: 'בצ׳אטים ההקשר הולך לאיבוד',
    ur: 'چیٹس میں سیاق کھو جاتا ہے',
    tl: 'Nawawala ang konteksto sa mga chat',
  },
  'Money gets fragmented': {
    he: 'הכסף מתפזר',
    ur: 'پیسہ بکھر جاتا ہے',
    tl: 'Nahahati-hati ang pera',
  },
  'Receipts, cash and reimbursements tell no shared story.': {
    he: 'קבלות, מזומן והחזרים לא מספרים סיפור אחד משותף.',
    ur: 'رسیدیں، نقدی اور واپسیاں کوئی مشترکہ کہانی نہیں بتاتیں۔',
    tl: 'Ang mga resibo, cash, at reimbursement ay walang iisang kwento.',
  },
  'You keep remembering what everyone else needs.': {
    he: 'אתם ממשיכים לזכור מה כולם האחרים צריכים.',
    ur: 'آپ ہی سب کی ضروریات یاد رکھتے ہیں۔',
    tl: 'Ikaw ang laging naaalala kung ano ang kailangan ng iba.',
  },
  'Memory becomes work': {
    he: 'זיכרון הופך לעבודה',
    ur: 'یادداشت کام بن جاتی ہے',
    tl: 'Ang alaala ay nagiging trabaho',
  },
  'From noise to knowing': {
    he: 'מרעש לידיעה',
    ur: 'شور سے آگہی تک',
    tl: 'Mula sa ingay tungo sa kaalaman',
  },
  'One household system': {
    he: 'מערכת אחת למשק הבית',
    ur: 'ایک گھریلو نظام',
    tl: 'Isang sistema ng sambahayan',
  },
  'One loop from thought to handled.': {
    he: 'לולאה אחת ממחשבה לטיפול.',
    ur: 'خیال سے نمٹنے تک ایک دائرہ۔',
    tl: 'Isang ikot mula sa isip hanggang sa natapos.',
  },
  'Give one natural command': {
    he: 'תנו פקודה אחת, בשפה טבעית',
    ur: 'ایک قدرتی ہدایت دیں',
    tl: 'Magbigay ng isang natural na utos',
  },
  'The right work reaches the right person.': {
    he: 'העבודה הנכונה מגיעה לאדם הנכון.',
    ur: 'صحیح کام صحیح شخص تک پہنچتا ہے۔',
    tl: 'Ang tamang trabaho ay napupunta sa tamang tao.',
  },
  'Every expense keeps its context.': {
    he: 'כל הוצאה שומרת על ההקשר שלה.',
    ur: 'ہر خرچ اپنے سیاق کے ساتھ۔',
    tl: 'Bawat gastos, may kasamang konteksto.',
  },
  'The expense keeps its context': {
    he: 'ההוצאה שומרת על ההקשר שלה',
    ur: 'خرچ اپنے سیاق کے ساتھ رہتا ہے',
    tl: 'Napapanatili ng gastos ang konteksto nito',
  },
  'The home remembers how you like things done.': {
    he: 'הבית זוכר איך אתם אוהבים שדברים נעשים.',
    ur: 'گھر یاد رکھتا ہے کہ آپ کام کیسے کروانا پسند کرتے ہیں۔',
    tl: 'Tandaan ng tahanan kung paano mo gustong gawin ang mga bagay.',
  },
  'The whole home becomes one calm signal.': {
    he: 'הבית כולו הופך לאות שקט אחד.',
    ur: 'پورا گھر ایک پرسکون اشارہ بن جاتا ہے۔',
    tl: 'Ang buong tahanan ay nagiging isang kalmadong senyas.',
  },
  'Say it once. Everyone understands.': {
    he: 'אומרים פעם אחת. כולם מבינים.',
    ur: 'ایک بار کہیں۔ سب سمجھ جائیں۔',
    tl: 'Sabihin nang isang beses. Maiintindihan ng lahat.',
  },
  'See it happen as it happens': {
    he: 'ראו את זה קורה ברגע שזה קורה',
    ur: 'جیسے ہوتا ہے ویسے دیکھیں',
    tl: 'Masaksihan habang nangyayari',
  },
  'Decisions in seconds': {
    he: 'החלטות בשניות',
    ur: 'فیصلے چند سیکنڈ میں',
    tl: 'Mga desisyon sa ilang segundo',
  },
  'Total control, without the chasing': {
    he: 'שליטה מלאה, בלי הרדיפה',
    ur: 'مکمل کنٹرول، بغیر پیچھا کیے',
    tl: 'Kumpletong kontrol, walang paghahabol',
  },
  'Get on with your day': {
    he: 'תמשיכו ביום שלכם',
    ur: 'اپنا دن جاری رکھیں',
    tl: 'Ituloy lang ang araw mo',
  },
  'One source of truth': {
    he: 'מקור אמת אחד',
    ur: 'سچ کا ایک ماخذ',
    tl: 'Iisang batayan ng katotohanan',
  },
  'Everything in one place': {
    he: 'הכל במקום אחד',
    ur: 'سب کچھ ایک جگہ',
    tl: 'Lahat nasa iisang lugar',
  },
  'A quiet daily rhythm': {
    he: 'קצב יומי שקט',
    ur: 'پرسکون روزمرہ کی روانی',
    tl: 'Tahimik na daloy ng bawat araw',
  },
  'A complete household money trail': {
    he: 'מסלול כספים מלא של משק הבית',
    ur: 'گھر کے پیسے کا مکمل ریکارڈ',
    tl: 'Kumpletong bakas ng pera ng sambahayan',
  },
  'How it works': {
    he: 'איך זה עובד',
    ur: 'یہ کیسے کام کرتا ہے',
    tl: 'Paano ito gumagana',
  },
  'See how it works': {
    he: 'ראו איך זה עובד',
    ur: 'دیکھیں یہ کیسے کام کرتا ہے',
    tl: 'Tingnan kung paano ito gumagana',
  },
  'Live Home': {
    he: 'הבית החי',
    ur: 'لائیو ہوم',
    tl: 'Live Home',
  },
  'Open Live Home': {
    he: 'פתיחת הבית החי',
    ur: 'لائیو ہوم کھولیں',
    tl: 'Buksan ang Live Home',
  },
  'Live updates': {
    he: 'עדכונים בזמן אמת',
    ur: 'براہ راست اپڈیٹس',
    tl: 'Mga live na update',
  },
  'The system': {
    he: 'המערכת',
    ur: 'نظام',
    tl: 'Ang sistema',
  },
  Review: {
    he: 'בדיקה',
    ur: 'جائزہ',
    tl: 'Suriin',
  },
  'Next action proposed': {
    he: 'הוצעה הפעולה הבאה',
    ur: 'اگلی کارروائی تجویز کردہ',
    tl: 'Iminungkahing susunod na hakbang',
  },
  'Ready for owner approval': {
    he: 'מוכן לאישור הבעלים',
    ur: 'مالک کی منظوری کے لیے تیار',
    tl: 'Handa na sa pag-apruba ng may-ari',
  },
  'Clear status and photo proof': {
    he: 'סטטוס ברור והוכחה בתמונה',
    ur: 'واضح صورتحال اور تصویری ثبوت',
    tl: 'Malinaw na status at patunay na larawan',
  },
  'Person, timing and proof identified': {
    he: 'זוהו האדם, התזמון וההוכחה',
    ur: 'شخص، وقت اور ثبوت کی شناخت',
    tl: 'Natukoy ang tao, oras, at patunay',
  },
  'Receipt captured': {
    he: 'הקבלה צולמה',
    ur: 'رسید محفوظ ہوگئی',
    tl: 'Nakuhanan ang resibo',
  },
  'Receipt capture and review': {
    he: 'צילום ובדיקת קבלות',
    ur: 'رسید کی تصویر اور جائزہ',
    tl: 'Pagkuha at pagsusuri ng resibo',
  },
  'Groceries receipt': {
    he: 'קבלת מכולת',
    ur: 'کریانے کی رسید',
    tl: 'Resibo ng grocery',
  },
  'Cash and reimbursement records': {
    he: 'רישומי מזומן והחזרים',
    ur: 'نقدی اور واپسی کے ریکارڈ',
    tl: 'Mga record ng cash at reimbursement',
  },
  'Reusable routines and protocols': {
    he: 'שגרות ופרוטוקולים לשימוש חוזר',
    ur: 'دوبارہ استعمال کے قابل روٹین اور ضوابط',
    tl: 'Mga routine at protocol na magagamit muli',
  },
  'Household standards': {
    he: 'הסטנדרטים של הבית',
    ur: 'گھریلو معیارات',
    tl: 'Mga pamantayan ng tahanan',
  },
  'Vendor and purchase preferences': {
    he: 'העדפות ספקים ורכש',
    ur: 'وینڈر اور خریداری کی ترجیحات',
    tl: 'Mga preference sa vendor at pagbili',
  },
  'Leave, pay and responsibilities': {
    he: 'חופשות, שכר ואחריות',
    ur: 'چھٹیاں، تنخواہ اور ذمہ داریاں',
    tl: 'Leave, sweldo, at mga responsibilidad',
  },
  'Owner-created staff access': {
    he: 'גישת צוות שנוצרה על ידי הבעלים',
    ur: 'مالک کی بنائی ہوئی عملے کی رسائی',
    tl: 'Akses ng staff na gawa ng may-ari',
  },
  'Personal household setup': {
    he: 'הגדרה אישית של משק הבית',
    ur: 'ذاتی گھریلو ترتیب',
    tl: 'Personal na setup ng sambahayan',
  },
  'Four languages built in': {
    he: 'ארבע שפות מובנות',
    ur: 'چار زبانیں پہلے سے شامل',
    tl: 'Apat na wika, kasama na',
  },
  Languages: {
    he: 'שפות',
    ur: 'زبانیں',
    tl: 'Mga wika',
  },
  'Marta starts at 9:00': {
    he: 'מרתה מתחילה ב-9:00',
    ur: 'مارٹا 9:00 پر شروع کرتی ہے',
    tl: 'Magsisimula si Marta nang 9:00',
  },
  'Wash the car': {
    he: 'לשטוף את האוטו',
    ur: 'گاڑی دھونا',
    tl: 'Hugasan ang kotse',
  },
  '“Wash the car before 3 PM and send photos.”': {
    he: '“לשטוף את האוטו לפני 15:00 ולשלוח תמונות.”',
    ur: '“گاڑی دوپہر 3 بجے سے پہلے دھوئیں اور تصاویر بھیجیں۔”',
    tl: '“Hugasan ang kotse bago mag-3 PM at magpadala ng mga litrato.”',
  },
  'Founding access': {
    he: 'גישת מייסדים',
    ur: 'بانی رسائی',
    tl: 'Founding access',
  },
  'Founding offer:': {
    he: 'הצעת המייסדים:',
    ur: 'بانی آفر:',
    tl: 'Alok sa mga kauna-unahan:',
  },
  'Join the founding release and help shape the calmest way to run a home.': {
    he: 'הצטרפו למהדורת המייסדים ועזרו לעצב את הדרך הרגועה ביותר לנהל בית.',
    ur: 'بانی ریلیز میں شامل ہوں اور گھر چلانے کے سب سے پرسکون طریقے کو تشکیل دیں۔',
    tl: 'Sumali sa founding release at tumulong sa paghubog ng pinakakalmadong paraan ng pamamahala ng tahanan.',
  },
  'Launch membership': {
    he: 'מנוי ההשקה',
    ur: 'لانچ رکنیت',
    tl: 'Membership sa launch',
  },
  'per month at launch': {
    he: 'לחודש בהשקה',
    ur: 'فی ماہ، لانچ پر',
    tl: 'bawat buwan sa launch',
  },
  '50% off annual membership when you reserve now': {
    he: 'הנחה של 50% על מנוי שנתי בהזמנה מוקדמת',
    ur: 'ابھی ریزرو کریں اور سالانہ رکنیت پر 50% رعایت پائیں',
    tl: '50% diskuwento sa taunang membership kapag nag-reserve ka ngayon',
  },
  'Lock in 50%': {
    he: 'לנעול 50% הנחה',
    ur: '50% محفوظ کریں',
    tl: 'I-lock ang 50%',
  },
  'Pay only when you launch': {
    he: 'משלמים רק כשמשיקים',
    ur: 'ادائیگی صرف لانچ پر',
    tl: 'Magbayad lang kapag nag-launch ka na',
  },
  'Before you bring it home.': {
    he: 'לפני שמכניסים את זה הביתה.',
    ur: 'اسے گھر لانے سے پہلے۔',
    tl: 'Bago mo ito iuwi.',
  },
  Questions: {
    he: 'שאלות',
    ur: 'سوالات',
    tl: 'Mga tanong',
  },
  Contact: {
    he: 'צור קשר',
    ur: 'رابطہ',
    tl: 'Makipag-ugnayan',
  },
  'What does founding access include?': {
    he: 'מה כוללת גישת המייסדים?',
    ur: 'بانی رسائی میں کیا شامل ہے؟',
    tl: 'Ano ang kasama sa founding access?',
  },
  'How does my household team get access?': {
    he: 'איך צוות הבית שלי מקבל גישה?',
    ur: 'میری گھریلو ٹیم کو رسائی کیسے ملے گی؟',
    tl: 'Paano makakakuha ng akses ang aking household team?',
  },
  'Is Famanage a smart-home controller?': {
    he: 'האם Famanage הוא בקר לבית חכם?',
    ur: 'کیا Famanage ایک سمارٹ ہوم کنٹرولر ہے؟',
    tl: 'Smart-home controller ba ang Famanage?',
  },
  'Which languages are supported?': {
    he: 'אילו שפות נתמכות?',
    ur: 'کون سی زبانیں معاون ہیں؟',
    tl: 'Anong mga wika ang sinusuportahan?',
  },

  // === Landing page v2 — sharpened narrative ===
  'For households with staff': {
    he: 'לבתים עם צוות עובדים',
    ur: 'عملے والے گھروں کے لیے',
    tl: 'Para sa mga sambahayang may staff',
  },
  'Famanage gives your home a brain of its own — tasks, routines, receipts, approvals, and payroll, handled. The kids were picked up. The driver arrived. The housekeeper was reimbursed. And you never had to think about any of it.':
    {
      he: 'Famanage נותנת לבית שלך מוח משלו — משימות, שגרות, קבלות, אישורים ושכר, מטופלים. הילדים נאספו. הנהג הגיע. העוזרת קיבלה החזר. ואתה לא היית צריך לחשוב על שום דבר מזה.',
      ur: 'Famanage آپ کے گھر کو اپنا دماغ دیتی ہے — کام، روٹین، رسیدیں، منظوریاں اور تنخواہ، سب سنبھالے ہوئے۔ بچے اٹھا لیے گئے۔ ڈرائیور پہنچ گیا۔ ملازمہ کو رقم واپس مل گئی۔ اور آپ کو کسی بھی چیز کے بارے میں سوچنا نہیں پڑا۔',
      tl: 'Binibigyan ng Famanage ang bahay mo ng sarili nitong utak — mga gawain, routines, resibo, approvals, at payroll, ayos na. Nakuha ang mga bata. Dumating ang driver. Na-reimburse ang katulong. At hindi mo na kailangang isipin ang alinman diyan.',
    },
  'Less than one hour of household help per week.': {
    he: 'פחות משעה של עזרה ביתית בשבוע.',
    ur: 'ہفتے میں ایک گھنٹے سے بھی کم گھریلو مدد۔',
    tl: 'Mas mababa sa isang oras ng tulong sa bahay bawat linggo.',
  },
  'For staff': { he: 'לצוות העובדים', ur: 'عملے کے لیے', tl: 'Para sa staff' },
  'Your workday, finally organized.': {
    he: 'יום העבודה שלך, סוף סוף מאורגן.',
    ur: 'آپ کا کام کا دن، آخر کار منظم۔',
    tl: 'Ang araw mo sa trabaho, sa wakas ay ayos na.',
  },
  'You keep the house running. You deserve better than 40 WhatsApp messages and a crumpled receipt. Your tasks for today, in your language. Routines that tell you what a perfect day looks like. Receipts you snap once and get reimbursed for — fast.':
    {
      he: 'את/ה מחזיק/ה את הבית. מגיע לך יותר מ-40 הודעות וואטסאפ וקבלה מקומטת. המשימות שלך להיום, בשפה שלך. שגרות שמראות איך נראה יום מושלם. קבלות שמצלמים פעם אחת — ומקבלים החזר מהר.',
      ur: 'آپ گھر چلاتے ہیں۔ آپ 40 واٹس ایپ پیغامات اور ایک ملی ہوئی رسید سے بہتر کے مستحق ہیں۔ آج کے آپ کے کام، آپ کی زبان میں۔ روٹین جو بتاتی ہے کہ کامل دن کیسا ہوتا ہے۔ رسیدیں جن کی ایک تصویر لیں — اور جلدی رقم واپس پائیں۔',
      tl: 'Ikaw ang nagpapatakbo ng bahay. Karapat-dapat ka sa higit sa 40 WhatsApp messages at gusot na resibo. Ang mga gawain mo ngayon, sa wika mo. Mga routine na nagsasabi kung ano ang perpektong araw. Mga resibong isang snap lang — mabilis ang reimbursement.',
    },
  "You're a professional. This is your tool.": {
    he: 'את/ה איש/אשת מקצוע. זה הכלי שלך.',
    ur: 'آپ پیشہ ور ہیں۔ یہ آپ کا آلہ ہے۔',
    tl: 'Propesyonal ka. Ito ang kasangkapan mo.',
  },
  'Free for staff, always. Your employer invites you; you’re set up in two minutes.': {
    he: 'חינם לעובדים, תמיד. המעסיק מזמין אותך; אתה מוכן תוך שתי דקות.',
    ur: 'عملے کے لیے ہمیشہ مفت۔ آپ کا آجر دعوت دیتا ہے؛ آپ دو منٹ میں تیار ہیں۔',
    tl: 'Libre para sa staff, lagi. Iniimbitahan ka ng employer mo; handa ka na sa dalawang minuto.',
  },
  'Today, in your language — not 40 chat messages': {
    he: 'היום, בשפה שלך — לא 40 הודעות צ׳אט',
    ur: 'آج، آپ کی زبان میں — 40 چیٹ پیغامات نہیں',
    tl: 'Ngayon, sa wika mo — hindi 40 chat messages',
  },
  'One big button per task. No guessing what "clean the house" means': {
    he: 'כפתור אחד גדול לכל משימה. בלי לנחש מה זה "לנקות את הבית"',
    ur: 'ہر کام کے لیے ایک بڑا بٹن۔ "گھر صاف کرنا" کا اندازہ لگانے کی ضرورت نہیں',
    tl: 'Isang malaking button bawat gawain. Walang hulaan kung ano ang "linisin ang bahay"',
  },
  'Snap a receipt once — get reimbursed fast': {
    he: 'מצלמים קבלה פעם אחת — ומקבלים החזר מהר',
    ur: 'رسید کی ایک تصویر لیں — جلدی رقم واپس پائیں',
    tl: 'Isang snap ng resibo — mabilis ang reimbursement',
  },
  'Staff see only what they need.': {
    he: 'העובדים רואים רק מה שהם צריכים.',
    ur: 'عملہ صرف وہی دیکھتا ہے جس کی اسے ضرورت ہے۔',
    tl: 'Ang staff ay nakikita lang ang kailangan nila.',
  },
  'Revoke access in one tap.': {
    he: 'מבטלים גישה בלחיצה אחת.',
    ur: 'ایک ٹیپ میں رسائی منسوخ کریں۔',
    tl: 'I-revoke ang access sa isang tap.',
  },
  'Your data is never sold, and never trains public AI models.': {
    he: 'הנתונים שלך לעולם לא נמכרים, ולעולם לא מאמנים מודלי AI ציבוריים.',
    ur: 'آپ کا ڈیٹا کبھی فروخت نہیں ہوتا، اور کبھی عوامی AI ماڈلز کی تربیت نہیں کرتا۔',
    tl: 'Ang data mo ay hindi ibinibenta, at hindi ginagamit sa pag-train ng public AI models.',
  },
  'Stop managing your own house.': {
    he: 'די לנהל את הבית בעצמכם.',
    ur: 'اپنے گھر کا منیجر بننا چھوڑ دیں۔',
    tl: 'Itigil na ang pag-manage ng sarili mong bahay.',
  },
  'Delegate the work, see the proof, approve the decisions — in the language each person speaks. And it never nags.':
    {
      he: 'מאצילים את העבודה, רואים את ההוכחה, מאשרים את ההחלטות — בשפה שכל אחד מדבר. וזה אף פעם לא מציק.',
      ur: 'کام سونپیں، ثبوت دیکھیں، فیصلے منظور کریں — ہر شخص اپنی زبان میں۔ اور یہ کبھی تنگ نہیں کرتا۔',
      tl: 'I-delegate ang trabaho, tingnan ang patunay, aprubahan ang desisyon — sa wikang nauunawaan ng bawat isa. At hindi ka nito kukulitin.',
    },
  'Built for the worker': {
    he: 'נבנה עבור העובד',
    ur: 'کارکن کے لیے بنایا گیا',
    tl: 'Ginawa para sa manggagawa',
  },
  'One big button, in their language. Zero training.': {
    he: 'כפתור אחד גדול, בשפה שלהם. בלי הדרכה.',
    ur: 'ایک بڑا بٹن، ان کی اپنی زبان میں۔ کوئی تربیت نہیں۔',
    tl: 'Isang malaking button, sa sarili nilang wika. Walang training.',
  },
  'The money loop closes itself': {
    he: 'מעגל הכסף נסגר מעצמו',
    ur: 'پیسے کا چکر خود بخود مکمل ہوتا ہے',
    tl: 'Ang ikot ng pera, kusang nagsasara',
  },
  'Receipt, approval, payroll': {
    he: 'קבלה, אישור, שכר',
    ur: 'رسید، منظوری، تنخواہ',
    tl: 'Resibo, approval, payroll',
  },
  'Never nags': {
    he: 'אף פעם לא מציק',
    ur: 'کبھی تنگ نہیں کرتا',
    tl: 'Hindi kumukulit',
  },
  'It speaks up only when a decision is needed': {
    he: 'מדבר רק כשצריך החלטה',
    ur: 'صرف فیصلے کی ضرورت پر آواز اٹھاتا ہے',
    tl: 'Magsasalita lang kapag may desisyong kailangan',
  },
  'When work, receipts and house rules live in separate chats, you become the manager of your own house. Famanage gives the whole household one calm source of truth.':
    {
      he: 'כשעבודה, קבלות וכללי הבית חיים בצ׳אטים נפרדים, אתם הופכים למנהלים של הבית של עצמכם. Famanage נותן לכל משק הבית מקור אמת אחד ורגוע.',
      ur: 'جب کام، رسیدیں اور گھر کے اصول الگ الگ چیٹس میں ہوتے ہیں، تو آپ اپنے ہی گھر کے منیجر بن جاتے ہیں۔ Famanage پورے گھر کو سچ کا ایک پرسکون ماخذ دیتا ہے۔',
      tl: 'Kapag ang trabaho, resibo, at house rules ay nasa magkakahiwalay na chat, ikaw ang nagiging tagapamahala ng sarili mong tahanan. Ang Famanage ang nagbibigay sa buong sambahayan ng iisang kalmadong batayan ng katotohanan.',
    },
  'Live Home gives you the day in one glance: the decisions waiting on you, today’s work, team status. Nothing else demands your attention.':
    {
      he: 'הבית החי נותן לכם את היום במבט אחד: ההחלטות שמחכות לכם, עבודת היום, סטטוס הצוות. שום דבר אחר לא דורש את תשומת הלב שלכם.',
      ur: 'لائیو ہوم آپ کو پورا دن ایک نظر میں دیتا ہے: آپ کے منتظر فیصلے، آج کا کام، ٹیم کی صورتحال۔ باقی کچھ آپ کی توجہ نہیں مانگتا۔',
      tl: 'Ibinibigay ng Live Home ang buong araw sa isang tingin: ang mga desisyong naghihintay sa iyo, ang gawain ngayong araw, ang status ng team. Wala nang iba pang hihingi ng atensyon mo.',
    },
  '03 · Money loop': {
    he: '03 · מעגל הכסף',
    ur: '03 · پیسے کا چکر',
    tl: '03 · Ikot ng pera',
  },
  'A receipt becomes an expense, approved expenses become payroll. Famanage reads the purchase and proposes the next action — you only approve.':
    {
      he: 'קבלה הופכת להוצאה, הוצאות מאושרות הופכות לשכר. Famanage קורא את הקנייה ומציע את הפעולה הבאה — אתם רק מאשרים.',
      ur: 'رسید خرچ بن جاتی ہے، منظور شدہ اخراجات تنخواہ بن جاتے ہیں۔ Famanage خریداری پڑھ کر اگلا قدم تجویز کرتا ہے — آپ صرف منظور کرتے ہیں۔',
      tl: 'Ang resibo ay nagiging gastos, ang mga aprubadong gastos ay nagiging payroll. Binabasa ng Famanage ang binili at iminumungkahi ang susunod na hakbang — ikaw, aaprubahan lang.',
    },
  'The house stops reporting problems. It presents decisions.': {
    he: 'הבית מפסיק לדווח על בעיות. הוא מציג החלטות.',
    ur: 'گھر مسائل کی رپورٹ کرنا چھوڑ دیتا ہے۔ فیصلے پیش کرتا ہے۔',
    tl: 'Hindi na nag-uulat ng problema ang bahay. Mga desisyon na ang ipinapakita.',
  },
  'Famanage stays quiet while the work gets done and speaks up only when something needs you. No dashboards, no noise, no nagging.':
    {
      he: 'Famanage שותק בזמן שהעבודה נעשית ומדבר רק כשמשהו צריך אתכם. בלי דשבורדים, בלי רעש, בלי להציק.',
      ur: 'Famanage کام کے دوران خاموش رہتا ہے اور صرف اس وقت بولتا ہے جب آپ کی ضرورت ہو۔ نہ ڈیش بورڈ، نہ شور، نہ تنگ کرنا۔',
      tl: 'Tahimik ang Famanage habang nagagawa ang trabaho at magsasalita lang kapag may kailangan sa iyo. Walang dashboards, walang ingay, walang pangungulit.',
    },
  'Our founder’s own house has run on it for months — every task, receipt and payslip.': {
    he: 'הבית של המייסד שלנו רץ על זה כבר חודשים — כל משימה, קבלה ותלוש שכר.',
    ur: 'ہمارے بانی کا اپنا گھر مہینوں سے اسی پر چل رہا ہے — ہر کام، رسید اور تنخواہ کی پرداخت۔',
    tl: 'Ang sariling tahanan ng aming founder, ilang buwan nang tumatakbo dito — bawat gawain, resibo, at payslip.',
  },
  'Built for the worker, not the manager.': {
    he: 'נבנה עבור העובד, לא עבור המנהל.',
    ur: 'کارکن کے لیے بنایا گیا، منیجر کے لیے نہیں۔',
    tl: 'Ginawa para sa manggagawa, hindi para sa tagapamahala.',
  },
  'A housekeeper with broken English opens one big button in her own language — Hebrew, Urdu or Tagalog, right-to-left — and just works. Owners see proof instead of chasing.':
    {
      he: 'עוזרת בית עם אנגלית שבורה פותחת כפתור אחד גדול בשפה שלה — עברית, אורדו או טגלוג, מימין לשמאל — ופשוט עובדת. הבעלים רואים הוכחה במקום לרדוף.',
      ur: 'ٹوٹی پھوٹی انگریزی والی گھریلو ملازمہ ایک بڑا بٹن اپنی زبان میں کھولتی ہے — عبرانی، اردو یا تگالوگ، دائیں سے بائیں — اور بس کام کرتی ہے۔ مالک پیچھا کرنے کے بجائے ثبوت دیکھتے ہیں۔',
      tl: 'Ang katulong na hirap sa English ay magbubukas ng isang malaking button sa sarili niyang wika — Hebrew, Urdu o Tagalog, kanan-pakaliwa — at dire-diretsong magtatrabaho. Ang may-ari, patunay ang nakikita imbes na manghabol.',
    },
  'One big button, zero training': {
    he: 'כפתור אחד גדול, אפס הדרכה',
    ur: 'ایک بڑا بٹن، صفر تربیت',
    tl: 'Isang malaking button, walang training',
  },
  'Receipt in, payroll out.': {
    he: 'קבלה נכנסת, שכר יוצא.',
    ur: 'رسید اندر، تنخواہ باہر۔',
    tl: 'Resibo papasok, payroll palabas.',
  },
  'Receipts become expenses, approved expenses become payroll, and the loop closes itself. Cash, loans and reimbursements live alongside the work that created them.':
    {
      he: 'קבלות הופכות להוצאות, הוצאות מאושרות הופכות לשכר, והמעגל נסגר מעצמו. מזומן, הלוואות והחזרים חיים לצד העבודה שיצרה אותם.',
      ur: 'رسیدیں اخراجات بن جاتی ہیں، منظور شدہ اخراجات تنخواہ، اور چکر خود بخود مکمل۔ نقدی، قرض اور واپسیاں اسی کام کے ساتھ رہتی ہیں جس نے انہیں پیدا کیا۔',
      tl: 'Ang mga resibo ay nagiging gastos, ang mga aprubadong gastos ay nagiging payroll, at kusang nagsasara ang ikot. Ang cash, utang, at reimbursement ay kasama ng trabahong lumikha sa kanila.',
    },
  'Standards, preferences, vendor rules and recurring protocols stop living inside one person’s head. The house learns what it needs — and speaks up only when it’s useful.':
    {
      he: 'סטנדרטים, העדפות, כללי ספקים ופרוטוקולים חוזרים מפסיקים לחיות בראש של אדם אחד. הבית לומד מה הוא צריך — ומדבר רק כשזה שימושי.',
      ur: 'معیارات، ترجیحات، وینڈر کے اصول اور دہرائے جانے والے ضوابط اب کسی ایک شخص کے ذہن میں نہیں رہتے۔ گھر سیکھتا ہے کہ اسے کیا چاہیے — اور صرف اس وقت بولتا ہے جب فائدہ ہو۔',
      tl: 'Ang mga pamantayan, preference, vendor rules, at paulit-ulit na protocol ay hindi na nakatira sa isip ng iisang tao. Natututo ang bahay kung ano ang kailangan nito — at magsasalita lang kapag may silbi.',
    },
  'Who is Famanage for?': {
    he: 'למי Famanage מתאים?',
    ur: 'Famanage کس کے لیے ہے؟',
    tl: 'Para kanino ang Famanage?',
  },
  'Households with staff — a housekeeper, a driver, a nanny, a cook. If you run your home on your own, you don’t need us.':
    {
      he: 'לבתים עם צוות — עוזרת בית, נהג, מטפלת, טבח. אם אתם מנהלים את הבית לבד, אתם לא צריכים אותנו.',
      ur: 'عملے والے گھرانے — گھریلو ملازمہ، ڈرائیور، نینی، باورچی۔ اگر آپ گھر اکیلے چلاتے ہیں تو آپ کو ہماری ضرورت نہیں۔',
      tl: 'Sa mga sambahayang may staff — katulong, driver, yaya, kusinero. Kung ikaw lang ang nagpapatakbo ng bahay, hindi mo kami kailangan.',
    },
  'The owner creates each staff account and controls what that person can see. Staff get one big button and a focused Today view — in their own language, right-to-left included.':
    {
      he: 'הבעלים יוצרים כל חשבון עובד ושולטים במה שהאדם רואה. העובדים מקבלים כפתור אחד גדול ותצוגת "היום" ממוקדת — בשפה שלהם, כולל מימין לשמאל.',
      ur: 'مالک ہر ملازم کا اکاؤنٹ بناتا ہے اور کنٹرول کرتا ہے کہ وہ کیا دیکھ سکتا ہے۔ عملے کو ایک بڑا بٹن اور آج کا مرکوز منظر ملتا ہے — اپنی زبان میں، دائیں سے بائیں سمیت۔',
      tl: 'Ang may-ari ang lumilikha ng bawat staff account at kumokontrol kung ano ang nakikita nito. Ang staff ay may isang malaking button at nakapokus na Today view — sa sarili nilang wika, pati right-to-left.',
    },
  'Does it work offline?': {
    he: 'זה עובד בלי אינטרנט?',
    ur: 'کیا یہ بغیر انٹرنیٹ کام کرتا ہے؟',
    tl: 'Gumagana ba ito kahit offline?',
  },
  'Yes. The work of the house doesn’t stop when the internet does.': {
    he: 'כן. העבודה בבית לא נעצרת כשהאינטרנט נעצר.',
    ur: 'جی ہاں۔ انٹرنیٹ رک جائے تو گھر کا کام نہیں رکتا۔',
    tl: 'Oo. Hindi humihinto ang trabaho sa bahay kapag huminto ang internet.',
  },
  'Freedom from managing your own house.': {
    he: 'חופש מלנהל את הבית של עצמכם.',
    ur: 'اپنے گھر کی مینجمنٹ سے آزادی۔',
    tl: 'Kalayaan mula sa pag-manage ng sarili mong tahanan.',
  },

  // ── Mobile: t() literals that fell through the table (2026-08-04) ─────
  // Retry affordance on every failed-load state (today / pay / history / more).
  'Tap to retry': {
    he: 'הקש לנסות שוב',
    ur: 'دوبارہ کوشش کے لیے ٹیپ کریں',
    tl: 'I-tap para subukan muli',
  },
  // Toast after a staff member pulls a routine/suggestion into their day.
  'Added to your day': {
    he: 'נוסף להיום שלך',
    ur: 'آپ کے دن میں شامل کر دیا گیا',
    tl: 'Naidagdag sa araw mo',
  },
  // Empty updates feed on the op page.
  'No updates yet.': {
    he: 'אין עדכונים עדיין.',
    ur: 'ابھی کوئی تازہ کاری نہیں۔',
    tl: 'Wala pang update.',
  },
  // Password visibility toggle on the sign-in screens.
  'Show password': { he: 'הצג סיסמה', ur: 'پاس ورڈ دکھائیں', tl: 'Ipakita ang password' },
  'Hide password': { he: 'הסתר סיסמה', ur: 'پاس ورڈ چھپائیں', tl: 'Itago ang password' },
  // Pay screen: header above a weekly payslip.
  'Week of {{date}}': { he: 'שבוע של {{date}}', ur: '{{date}} کا ہفتہ', tl: 'Linggo ng {{date}}' },
  // Scan-insight schedule summaries ("Daily" and "Weekly" already exist).
  'Every {{n}} days': { he: 'כל {{n}} ימים', ur: 'ہر {{n}} دن بعد', tl: 'Kada {{n}} araw' },
  'Every {{n}} weeks': { he: 'כל {{n}} שבועות', ur: 'ہر {{n}} ہفتے بعد', tl: 'Kada {{n}} linggo' },
  '{{n}}× per week': { he: '{{n}}× בשבוע', ur: 'ہفتے میں {{n}}×', tl: '{{n}}× kada linggo' },
  // Staff More screen: lead-in to the permission list the owner granted.
  'Your owner trusts you to': {
    he: 'הבעלים שלך סומכים עליך ל',
    ur: 'آپ کا مالک آپ کو یہ اجازت دیتا ہے کہ',
    tl: 'Pinagkakatiwalaan ka ng may-ari mong',
  },
  // Owner tasks: group of ops the owner himself still needs to act on.
  'Waiting on you': { he: 'ממתין לך', ur: 'آپ کے فیصلے کا انتظار', tl: 'Naghihintay sa iyo' },
  // Owner home status line: "{n} running now" — matches '{{n}} running now'.
  'running now': { he: 'בעבודה עכשיו', ur: 'ابھی جاری', tl: 'ang ginagawa ngayon' },
  // Owner people: badge on a deactivated person.
  disabled: { he: 'מושבת', ur: 'غیر فعال', tl: 'hindi aktibo' },

  // ── Mobile: accessibility-only labels (S7 a11y pass, 2026-08-05) ───────
  // Never rendered as visible copy — accessibilityLabel values for the
  // camera preview and the profile avatar, images with no visible caption
  // of their own. ('Loading', the spinner label, already exists above.)
  'Photo preview': {
    he: 'תצוגה מקדימה של התמונה',
    ur: 'تصویر کا پیش منظر',
    tl: 'Preview ng larawan',
  },
  'Profile photo': { he: 'תמונת פרופיל', ur: 'پروفائل تصویر', tl: 'Larawan ng profile' },

  // === Staff management (add person / invites / person detail) ===
  'That username is taken in your household — pick another one.': {
    he: 'שם המשתמש הזה תפוס במשק הבית — בחר שם אחר.',
    ur: 'یہ صارف نام آپ کے گھرانے میں پہلے سے موجود ہے — کوئی اور منتخب کریں۔',
    tl: 'Ginagamit na ang username na iyan sa inyong sambahayan — pumili ng iba.',
  },
  'That email is already in use.': {
    he: 'האימייל הזה כבר בשימוש.',
    ur: 'یہ ای میل پہلے سے استعمال میں ہے۔',
    tl: 'Ginagamit na ang email na iyan.',
  },
  'Choose a username to sign in with.': {
    he: 'בחר שם משתמש להתחברות.',
    ur: 'سائن اِن کے لیے صارف نام منتخب کریں۔',
    tl: 'Pumili ng username para sa pag-sign in.',
  },
  'Some details need fixing — check the highlighted fields and try again.': {
    he: 'חלק מהפרטים דורשים תיקון — בדוק את השדות המסומנים ונסה שוב.',
    ur: 'کچھ تفصیلات درست کرنے کی ضرورت ہے — نمایاں خانے دیکھیں اور دوبارہ کوشش کریں۔',
    tl: 'May ilang detalyeng kailangang ayusin — tingnan ang mga naka-highlight na field at subukan muli.',
  },
  'Not found — it may have been removed already.': {
    he: 'לא נמצא — ייתכן שכבר הוסר.',
    ur: 'نہیں ملا — شاید پہلے ہی ہٹا دیا گیا ہے۔',
    tl: 'Hindi natagpuan — maaaring natanggal na ito.',
  },
  'You are not allowed to do that.': {
    he: 'אין לך הרשאה לעשות זאת.',
    ur: 'آپ کو ایسا کرنے کی اجازت نہیں۔',
    tl: 'Hindi ka pinapayagang gawin iyan.',
  },
  'This account is disabled.': {
    he: 'החשבון הזה מושבת.',
    ur: 'یہ اکاؤنٹ غیر فعال ہے۔',
    tl: 'Naka-disable ang account na ito.',
  },
  'This invite link was already used.': {
    he: 'קישור ההזמנה הזה כבר נוצל.',
    ur: 'یہ دعوتی لنک پہلے ہی استعمال ہو چکا ہے۔',
    tl: 'Nagamit na ang invite link na ito.',
  },
  'This invite link was revoked — ask the owner for a new one.': {
    he: 'קישור ההזמנה בוטל — בקש מהבעלים קישור חדש.',
    ur: 'یہ دعوتی لنک منسوخ کر دیا گیا ہے — مالک سے نیا لنک لیں۔',
    tl: 'Na-revoke ang invite link na ito — humingi ng bago sa may-ari.',
  },
  'This invite link expired — ask the owner for a new one.': {
    he: 'תוקף קישור ההזמנה פג — בקש מהבעלים קישור חדש.',
    ur: 'یہ دعوتی لنک ختم ہو گیا ہے — مالک سے نیا لنک لیں۔',
    tl: 'Nag-expire na ang invite link na ito — humingi ng bago sa may-ari.',
  },
  'This link is no longer valid.': {
    he: 'הקישור הזה כבר לא בתוקף.',
    ur: 'یہ لنک اب درست نہیں ہے۔',
    tl: 'Hindi na wasto ang link na ito.',
  },
  'No connection — check your signal and try again.': {
    he: 'אין חיבור — בדוק את הקליטה ונסה שוב.',
    ur: 'کوئی کنکشن نہیں — سگنل چیک کریں اور دوبارہ کوشش کریں۔',
    tl: 'Walang koneksyon — tingnan ang signal at subukan muli.',
  },
  Share: { he: 'שתף', ur: 'شیئر کریں', tl: 'Ibahagi' },
  'The link works for 7 days and can be used once.': {
    he: 'הקישור תקף ל-7 ימים וניתן לשימוש חד-פעמי.',
    ur: 'یہ لنک 7 دن کام کرتا ہے اور صرف ایک بار استعمال ہو سکتا ہے۔',
    tl: 'Gagana ang link nang 7 araw at isang beses lang magagamit.',
  },
  'Add them now': { he: 'הוסף עכשיו', ur: 'ابھی شامل کریں', tl: 'Idagdag ngayon' },
  'You set up their sign-in and hand over the details.': {
    he: 'אתה מגדיר את הכניסה שלהם ומוסר את הפרטים.',
    ur: 'آپ خود ان کا سائن اِن بناتے ہیں اور تفصیلات دیتے ہیں۔',
    tl: 'Ikaw ang magse-set up ng kanilang sign-in at magbibigay ng mga detalye.',
  },
  'Send an invite link': {
    he: 'שלח קישור הזמנה',
    ur: 'دعوتی لنک بھیجیں',
    tl: 'Magpadala ng invite link',
  },
  'They choose their own username and password from the link.': {
    he: 'הם בוחרים שם משתמש וסיסמה בעצמם דרך הקישור.',
    ur: 'وہ لنک سے خود اپنا صارف نام اور پاس ورڈ منتخب کرتے ہیں۔',
    tl: 'Sila ang pipili ng sariling username at password mula sa link.',
  },
  Position: { he: 'תפקיד', ur: 'عہدہ', tl: 'Posisyon' },
  'Phone (optional)': {
    he: 'טלפון (אופציונלי)',
    ur: 'فون (اختیاری)',
    tl: 'Telepono (opsyonal)',
  },
  'Full number with + and country code, e.g. +971501234567': {
    he: 'מספר מלא עם + וקידומת מדינה, למשל +971501234567',
    ur: '+ اور کنٹری کوڈ کے ساتھ مکمل نمبر، مثلاً +971501234567',
    tl: 'Buong numero na may + at country code, hal. +971501234567',
  },
  'Their full name is missing.': {
    he: 'חסר השם המלא שלהם.',
    ur: 'ان کا پورا نام درج نہیں ہے۔',
    tl: 'Kulang ang kanilang buong pangalan.',
  },
  'Usernames are 3–24 lowercase letters, digits, dots or underscores.': {
    he: 'שמות משתמש הם 3–24 אותיות קטנות, ספרות, נקודות או קווים תחתונים.',
    ur: 'صارف نام 3–24 چھوٹے حروف، ہندسے، نقطے یا انڈر سکور پر مشتمل ہوتا ہے۔',
    tl: 'Ang username ay 3–24 maliliit na letra, numero, tuldok o underscore.',
  },
  'Passwords are at least 10 characters.': {
    he: 'סיסמאות הן לפחות 10 תווים.',
    ur: 'پاس ورڈ کم از کم 10 حروف کا ہوتا ہے۔',
    tl: 'Ang password ay hindi bababa sa 10 character.',
  },
  'Create sign-in': { he: 'צור כניסה', ur: 'سائن اِن بنائیں', tl: 'Gumawa ng sign-in' },
  'Create link': { he: 'צור קישור', ur: 'لنک بنائیں', tl: 'Gumawa ng link' },
  Enabled: { he: 'מופעל', ur: 'فعال', tl: 'Naka-enable' },
  'Generate a new first password and sign them out everywhere.': {
    he: 'הפק סיסמה ראשונה חדשה ונתק אותם מכל המכשירים.',
    ur: 'نیا پہلا پاس ورڈ بنائیں اور انہیں ہر جگہ سے سائن آؤٹ کریں۔',
    tl: 'Gumawa ng bagong unang password at i-sign out sila sa lahat ng device.',
  },
  'Send a setup link': {
    he: 'שלח קישור הגדרה',
    ur: 'سیٹ اپ لنک بھیجیں',
    tl: 'Magpadala ng setup link',
  },
  'A one-time link where they set their own password.': {
    he: 'קישור חד-פעמי שבו הם קובעים סיסמה בעצמם.',
    ur: 'ایک بار کا لنک جہاں وہ خود اپنا پاس ورڈ بناتے ہیں۔',
    tl: 'Isang beses lang magagamit na link kung saan sila ang gagawa ng sariling password.',
  },
  'Let them sign in again.': {
    he: 'אפשר להם להיכנס שוב.',
    ur: 'انہیں دوبارہ سائن اِن کرنے دیں۔',
    tl: 'Payagan silang mag-sign in muli.',
  },
  'Blocks sign-in and signs them out everywhere.': {
    he: 'חוסם כניסה ומנתק אותם מכל המכשירים.',
    ur: 'سائن اِن بلاک کرتا ہے اور انہیں ہر جگہ سے سائن آؤٹ کرتا ہے۔',
    tl: 'Hinaharang ang sign-in at sine-sign out sila sa lahat ng device.',
  },
  'This link does not work': {
    he: 'הקישור הזה לא עובד',
    ur: 'یہ لنک کام نہیں کرتا',
    tl: 'Hindi gumagana ang link na ito',
  },
  'Go to sign in': { he: 'עבור לכניסה', ur: 'سائن اِن پر جائیں', tl: 'Pumunta sa sign in' },
  'Choose your password': {
    he: 'בחר סיסמה',
    ur: 'اپنا پاس ورڈ منتخب کریں',
    tl: 'Pumili ng iyong password',
  },
  'Already have an account? Sign in': {
    he: 'כבר יש לך חשבון? היכנס',
    ur: 'پہلے سے اکاؤنٹ ہے؟ سائن اِن کریں',
    tl: 'May account ka na? Mag-sign in',
  },
  'This tunes the app to your household. You can change it later.': {
    he: 'זה מתאים את האפליקציה למשק הבית שלך. אפשר לשנות מאוחר יותר.',
    ur: 'اس سے ایپ آپ کے گھرانے کے مطابق ڈھل جاتی ہے۔ آپ بعد میں بدل سکتے ہیں۔',
    tl: 'Iniaangkop nito ang app sa iyong sambahayan. Mababago mo ito mamaya.',
  },
  'Who lives at home?': {
    he: 'מי גר בבית?',
    ur: 'گھر میں کون رہتا ہے؟',
    tl: 'Sino ang nakatira sa bahay?',
  },
  Pets: { he: 'חיות מחמד', ur: 'پالتو جانور', tl: 'Mga alaga' },
  None: { he: 'אין', ur: 'کوئی نہیں', tl: 'Wala' },
  'None yet': { he: 'עוד לא', ur: 'ابھی کوئی نہیں', tl: 'Wala pa' },
  'Your home is set up': {
    he: 'הבית שלך מוכן',
    ur: 'آپ کا گھر تیار ہے',
    tl: 'Handa na ang iyong tahanan',
  },

  // === The worker-improvement loop (S5) ===
  // Guidance on the task, the worker's own transparency surface, the owner's
  // learning cards, and the coded rejection taxonomy. The tone is the whole
  // point here: guidance WITHDRAWN is the reward for competence, never a
  // punishment — so nothing below reads as a score, a rank, or a grade.
  // ('Good to know' and 'Worth a look' already existed in the table.)
  'General practice': { he: 'פרקטיקה מקצועית', ur: 'عمومی طریقہ', tl: 'Karaniwang gawi' },
  'House rule': { he: 'חוק בית', ur: 'گھر کا اصول', tl: 'Tuntunin ng bahay' },
  'What the house expects of me': {
    he: 'מה הבית מצפה ממני',
    ur: 'گھر کو مجھ سے کیا توقع ہے',
    tl: 'Ang inaasahan ng bahay sa akin',
  },
  'As you grow, the house explains less — guidance turns off when a kind of work is yours.': {
    he: 'ככל שגדלים, הבית מסביר פחות — ההדרכה נכבית כשסוג עבודה כבר שלך.',
    ur: 'جیسے جیسے آپ سیکھتے ہیں، گھر کم سمجھاتا ہے — جب کام آپ کا ہو جائے تو رہنمائی بند ہو جاتی ہے۔',
    tl: 'Habang lumalawak ang kakayahan mo, unti-unting bumibigay ang bahay sa pagpapaliwanag — nawawala ang gabay kapag iyo na ang gawain.',
  },
  '{{done}} done well · {{asked}} questions · {{back}} sent back': {
    he: '{{done}} הצליחו · {{asked}} שאלות · {{back}} הוחזרו',
    ur: '{{done}} کامیاب · {{asked}} سوالات · {{back}} واپس بھیجے گئے',
    tl: '{{done}} tagumpay · {{asked}} tanong · {{back}} ibinalik',
  },
  'Guidance off — this kind of work is yours.': {
    he: 'ההדרכה כבויה — העבודה הזו כבר שלך.',
    ur: 'رہنمائی بند — یہ کام اب آپ کا ہے۔',
    tl: 'Patay na ang gabay — iyo na ang ganitong gawain.',
  },
  'If a number or a rule feels wrong, ask a question on the task — that is how it gets fixed.': {
    he: 'אם מספר או חוק מרגיש לא נכון, שאל שאלה על המשימה — ככה זה מתוקן.',
    ur: 'اگر کوئی نمبر یا اصول غلط لگے تو کام پر سوال پوچھیں — اسی سے درست ہوتا ہے۔',
    tl: 'Kung parang mali ang numero o tuntunin, magtanong sa gawain — doon ito naitatama.',
  },
  'Write the rule': { he: 'כתוב חוק', ur: 'اصول لکھیں', tl: 'Isulat ang tuntunin' },
  'For {{domain}} tasks': {
    he: 'למשימות מסוג {{domain}}',
    ur: '{{domain}} کے کاموں کے لیے',
    tl: 'Para sa mga gawaing {{domain}}',
  },
  // The coded rejection picker (owner) — optional by design, and every label
  // blames the brief or the circumstances, never the person (the server's
  // RejectionReasonCode taxonomy).
  'Why is it being sent back? (optional)': {
    he: 'למה זה חוזר? (לא חובה)',
    ur: 'یہ کیوں واپس بھیجوایا جا رہا ہے؟ (اختیاری)',
    tl: 'Bakit ito ibinabalik? (opsyonal)',
  },
  'The instruction was unclear': {
    he: 'ההוראה לא הייתה ברורה',
    ur: 'ہدایت واضح نہیں تھی',
    tl: 'Hindi malinaw ang utos',
  },
  'A house preference is missing': {
    he: 'חסרה העדפה של הבית',
    ur: 'گھر کی ترجیح موجود نہیں',
    tl: 'Kulang ang gusto ng bahay',
  },
  'A new kind of task': {
    he: 'סוג חדש של משימה',
    ur: 'نئی قسم کا کام',
    tl: 'Bagong uri ng gawain',
  },
  'Missing access': { he: 'חסרה גישה', ur: 'رسائی نہیں ہے', tl: 'Walang access' },
  'Not delivered': { he: 'לא נמסר', ur: 'پہنچایا نہیں گیا', tl: 'Hindi naihatid' },
  'Language barrier': { he: 'מחסום שפה', ur: 'زبان کی رکاوٹ', tl: 'Hadlang sa wika' },
  'Wrong timing': { he: 'תזמון לא מתאים', ur: 'غلط وقت', tl: 'Maling oras' },
  'Not done right': {
    he: 'לא בוצע כראוי',
    ur: 'صحیح طریقے سے نہیں ہوا',
    tl: 'Hindi tamang pagkakagawa',
  },
  // Domain names, for the worker's own progress rows and the rule sheet's
  // context line (the ids of apps/api src/intelligence/domains.ts).
  Garden: { he: 'גינה', ur: 'باغ', tl: 'Hardin' },
  Linen: { he: 'מצעים', ur: 'چادروں کا کام', tl: 'Mga linen' },
  AC: { he: 'מזגן', ur: 'اے سی', tl: 'Aircon' },
  School: { he: 'בית ספר', ur: 'اسکول', tl: 'Eskwela' },
  Laundry: { he: 'כביסה', ur: 'لانڈری', tl: 'Laba' },
  Cooking: { he: 'בישול', ur: 'کھانا پکانا', tl: 'Pagluluto' },

  // Release readiness (S8): account deletion, server provenance, crash
  // recovery. The 409 LAST_OWNER case renders the server's own message —
  // no key for it by design.
  'Delete account': { he: 'מחיקת החשבון', ur: 'اکاؤنٹ حذف کریں', tl: 'Burahin ang account' },
  'Delete your account?': {
    he: 'למחוק את החשבון?',
    ur: 'اپنا اکاؤنٹ حذف کریں؟',
    tl: 'Burahin ang iyong account?',
  },
  'Your account and sign-in will be removed. The work you did stays with the household. This cannot be undone.':
    {
      he: 'החשבון והכניסה שלך יוסרו. העבודה שעשית נשארת אצל משק הבית. אי אפשר לבטל את זה.',
      ur: 'آپ کا اکاؤنٹ اور سائن ان ہٹا دیا جائے گا۔ آپ کا کام گھرانے کے پاس رہے گا۔ یہ واپس نہیں ہو سکتا۔',
      tl: 'Aalisin ang iyong account at sign-in. Mananatili sa sambahayan ang trabahong ginawa mo. Hindi na ito mababawi.',
    },
  'Account deleted': { he: 'החשבון נמחק', ur: 'اکاؤنٹ حذف ہو گیا', tl: 'Nabura ang account' },
  'Server version': { he: 'גרסת השרת', ur: 'سرور ورژن', tl: 'Bersyon ng server' },
  'Restart the app': {
    he: 'הפעל מחדש את האפליקציה',
    ur: 'ایپ دوبارہ شروع کریں',
    tl: 'I-restart ang app',
  },
  'The app hit an unexpected problem. Anything you saved is safe — restart to continue.': {
    he: 'האפליקציה נתקלה בבעיה לא צפויה. כל מה ששמרת בטוח — הפעל מחדש כדי להמשיך.',
    ur: 'ایپ میں ایک غیر متوقع مسئلہ ہوا۔ آپ کی محفوظ کردہ چیزیں محفوظ ہیں — جاری رکھنے کے لیے دوبارہ شروع کریں۔',
    tl: 'May hindi inaasahang problema ang app. Ligtas ang lahat ng na-save mo — i-restart para magpatuloy.',
  },

  // Money + payroll parity (S9): advances, the payroll-run lifecycle,
  // loans, leave terms, pay terms and expected bills.
  'Request an advance': { he: 'בקשת מקדמה', ur: 'ایڈوانس کی درخواست', tl: 'Humingi ng advance' },
  'What is it for?': { he: 'למה זה מיועד?', ur: 'یہ کس لیے ہے؟', tl: 'Para saan ito?' },
  'Send advance request': {
    he: 'שליחת בקשת מקדמה',
    ur: 'ایڈوانس کی درخواست بھیجیں',
    tl: 'Ipadala ang kahilingan ng advance',
  },
  'Advance requested': {
    he: 'בקשת המקדמה נשלחה',
    ur: 'ایڈوانس کی درخواست بھیج دی گئی',
    tl: 'Naipadala ang kahilingan ng advance',
  },
  'An advance is cash you ask for before payday.': {
    he: 'מקדמה היא כסף מזומן שמבקשים לפני יום המשכורת.',
    ur: 'ایڈوانس وہ نقد رقم ہے جو تنخواہ کے دن سے پہلے مانگی جاتی ہے۔',
    tl: 'Ang advance ay cash na hinihingi bago ang araw ng sweldo.',
  },
  'Your requests': { he: 'הבקשות שלך', ur: 'آپ کی درخواستیں', tl: 'Ang mga kahilingan mo' },
  'No requests yet.': {
    he: 'אין בקשות עדיין.',
    ur: 'ابھی کوئی درخواست نہیں۔',
    tl: 'Wala pang mga kahilingan.',
  },
  'New payroll run': { he: 'ריצת שכר חדשה', ur: 'نیا پے رول رن', tl: 'Bagong payroll run' },
  'Create run': { he: 'יצירת ריצה', ur: 'رن بنائیں', tl: 'Gumawa ng run' },
  Preview: { he: 'תצוגה מקדימה', ur: 'پیش منظر', tl: 'Preview' },
  'Discard this draft run?': {
    he: 'למחוק את טיוטת הריצה?',
    ur: 'کیا یہ ڈرافٹ رن ختم کر دیں؟',
    tl: 'Burahin ang draft run na ito?',
  },
  'Paid via': { he: 'שולם באמצעות', ur: 'ادائیگی کا طریقہ', tl: 'Paraan ng bayad' },
  'e.g. cash, bank transfer': {
    he: 'למשל מזומן, העברה בנקאית',
    ur: 'مثلاً نقد، بینک ٹرانسفر',
    tl: 'hal. cash, bank transfer',
  },
  'Net pay': { he: 'נטו לתשלום', ur: 'خالص تنخواہ', tl: 'Netong sweldo' },
  'No payslips in this run.': {
    he: 'אין תלושי שכר בריצה הזו.',
    ur: 'اس رن میں کوئی تنخواہ سلپ نہیں۔',
    tl: 'Walang payslip sa run na ito.',
  },
  'This locks the period’s hours, loans and reimbursements. It cannot be undone.': {
    he: 'פעולה זו נועלת את השעות, ההלוואות וההחזרים של התקופה. אי אפשר לבטל אותה.',
    ur: 'اس سے اس مدت کے اوقات، قرض اور reimbursements مقفل ہو جاتے ہیں۔ یہ واپس نہیں ہو سکتا۔',
    tl: 'Ila-lock nito ang mga oras, loan, at reimbursement ng panahong ito. Hindi na ito mababawi.',
  },
  'Mark the whole run as paid? This cannot be undone.': {
    he: 'לסמן את כל הריצה כשולמה? אי אפשר לבטל את זה.',
    ur: 'کیا پورے رن کو ادا شدہ نشان زد کریں؟ یہ واپس نہیں ہو سکتا۔',
    tl: 'Markahan ang buong run bilang bayad na? Hindi na ito mababawi.',
  },
  'New loan': { he: 'הלוואה חדשה', ur: 'نیا قرض', tl: 'Bagong loan' },
  'Record repayment': { he: 'רישום החזר', ur: 'ادائیگی درج کریں', tl: 'Itala ang bayad' },
  'Write off': { he: 'מחיקת ההלוואה', ur: 'معاف کریں', tl: 'Patawarin ang loan' },
  'Repayment recorded': { he: 'ההחזר נרשם', ur: 'ادائیگی درج ہو گئی', tl: 'Naitala ang bayad' },
  'Why write it off?': {
    he: 'למה למחוק את ההלוואה?',
    ur: 'معافی کی وجہ؟',
    tl: 'Bakit papatawarin?',
  },
  'Leave saved': { he: 'החופשה נשמרה', ur: 'چھٹی محفوظ ہو گئی', tl: 'Na-save ang leave' },
  'Set pay terms': {
    he: 'הגדרת תנאי שכר',
    ur: 'تنخواہ کی شرائط مقرر کریں',
    tl: 'Itakda ang mga tuntunin ng sweldo',
  },
  'Hours per day': { he: 'שעות ביום', ur: 'گھنٹے فی دن', tl: 'Oras bawat araw' },
  'Days per month': { he: 'ימים בחודש', ur: 'دن فی مہینہ', tl: 'Araw bawat buwan' },
  'Effective from': { he: 'בתוקף מ־', ur: 'لاگو از', tl: 'Simula noong' },
  'Pay terms saved': {
    he: 'תנאי השכר נשמרו',
    ur: 'تنخواہ کی شرائط محفوظ ہو گئیں',
    tl: 'Na-save ang mga tuntunin ng sweldo',
  },
  Bills: { he: 'חשבונות', ur: 'بل', tl: 'Mga bill' },
  'Remove this bill?': {
    he: 'להסיר את החשבון?',
    ur: 'کیا یہ بل ہٹا دیں؟',
    tl: 'Alisin ang bill na ito?',
  },
  'Due soon': { he: 'לתשלום בקרוב', ur: 'جلد واجب الادا', tl: 'Malapit nang bayaran' },
  'Mark as paid': { he: 'סימון כשולם', ur: 'ادا شدہ نشان زد کریں', tl: 'Markahang bayad na' },
  'Amount ({{currency}})': {
    he: 'סכום ({{currency}})',
    ur: 'رقم ({{currency}})',
    tl: 'Halaga ({{currency}})',
  },
  'Staff member': { he: 'איש צוות', ur: 'عملے کا فرد', tl: 'Kasapi ng staff' },
  Pending: { he: 'ממתין', ur: 'زیر التوا', tl: 'Nakabinbin' },
  'Money requests': { he: 'בקשות כסף', ur: 'رقم کی درخواستیں', tl: 'Mga kahilingan ng pera' },
  'Needs your approval': {
    he: 'מחכה לאישורך',
    ur: 'آپ کی منظوری درکار ہے',
    tl: 'Kailangan ang iyong pag-apruba',
  },
  'Help from the team': {
    he: 'בקשות עזרה מהצוות',
    ur: 'ٹیم کی مدد کی درخواستیں',
    tl: 'Mga kahilingan ng tulong mula sa team',
  },
  'Overtime logged': { he: 'שעות נוספות נרשמו', ur: 'اوور ٹائم درج', tl: 'Naitalang overtime' },
  'Things to pay': { he: 'דברים לשלם', ur: 'ادا کی جانے والی چیزیں', tl: 'Mga babayaran' },
  Runs: { he: 'ריצות', ur: 'رن', tl: 'Mga run' },
  Payslips: { he: 'תלושי שכר', ur: 'تنخواہ سلپس', tl: 'Mga payslip' },
  'How often': { he: 'באיזו תדירות', ur: 'کتنی بار', tl: 'Gaano kadalas' },
  Optional: { he: 'אופציונלי', ur: 'اختیاری', tl: 'Opsyonal' },

  // === AI & discoverability (P1) ===
  // The composer magic fill + voice command, the suggestion kinds on the
  // owner home, and the house-rules browser.
  'Magic fill': { he: 'מילוי קסם', ur: 'جادوئی بھرائی', tl: 'Magic na punan' },
  'The assistant is busy — try again in a minute.': {
    he: 'העוזר עסוק — נסה שוב בעוד דקה.',
    ur: 'اسسٹنٹ مصروف ہے — ایک منٹ بعد دوبارہ کوشش کریں۔',
    tl: 'Abala ang assistant — subukan muli pagkalipas ng isang minuto.',
  },
  'The assistant is not configured on this server.': {
    he: 'העוזר לא מוגדר בשרת הזה.',
    ur: 'اسسٹنٹ اس سرور پر ترتیب شدہ نہیں ہے۔',
    tl: 'Hindi naka-configure ang assistant sa server na ito.',
  },
  // The suggestion-kind labels (web OwnerHome SUGGESTION_META + the two
  // learning kinds). The detector sentence below them stays server English.
  // ('Open', 'Receipts to review', 'Unreconciled cash', 'Dormant loan',
  // 'Unused note', 'Delegate work', 'Unusual spend', 'New on the team',
  // 'Done' and 'Delete' already existed in the table.)
  'Good start': { he: 'התחלה טובה', ur: 'اچھا آغاز', tl: 'Magandang simula' },
  'Missing house rule': {
    he: 'חסר חוק בית',
    ur: 'گھر کا اصول غائب ہے',
    tl: 'Kulang na tuntunin ng bahay',
  },
  'Repeating rejection': { he: 'דחייה חוזרת', ur: 'بار بار مسترد', tl: 'Paulit-ulit na pagtanggi' },
  // The rules browser (app/(owner)/rules.tsx) and the create sheet's
  // success state.
  'Who it governs': { he: 'על מי זה חל', ur: 'یہ کس پر لاگو ہے', tl: 'Kanino ito nakakasaklaw' },
  'The whole house': { he: 'כל הבית', ur: 'پورا گھر', tl: 'Buong bahay' },
  'One worker': { he: 'עובד אחד', ur: 'ایک ملازم', tl: 'Isang worker' },
  'Pin to the top': { he: 'נעץ למעלה', ur: 'اوپر پن کریں', tl: 'I-pin sa itaas' },
  'Rule saved': { he: 'החוק נשמר', ur: 'اصول محفوظ ہو گیا', tl: 'Nai-save ang tuntunin' },
  'See all house rules': {
    he: 'כל חוקי הבית',
    ur: 'گھر کے تمام اصول دیکھیں',
    tl: 'Tingnan lahat ng tuntunin ng bahay',
  },
  Deleted: { he: 'נמחק', ur: 'حذف ہو گیا', tl: 'Nabura na' },

  // ------------------------------------------------------------------
  // "Build Your Home" — the post-signup onboarding wizard (mobile
  // app/(owner)/build-home.tsx) and its edit-mode door ('My home' in
  // owner Settings). One section, step by step: welcome → people →
  // children → pets → staff → vehicles → property & rooms → review.
  // ------------------------------------------------------------------
  "Let's build your home": {
    he: 'בואו נבנה את הבית שלך',
    ur: 'آئیے آپ کا گھر بناتے ہیں',
    tl: 'Buuin natin ang inyong tahanan',
  },
  'Who lives here, what you drive, the rooms you run — a few quick steps. Skip anything; you can finish it later.':
    {
      he: 'מי גר כאן, במה אתם נוהגים, החדרים שאתם מנהלים — כמה צעדים קצרים. אפשר לדלג על הכל ולהשלים מאוחר יותר.',
      ur: 'کون یہاں رہتا ہے، آپ کیا چلاتے ہیں، کون سے کمرے ہیں — چند فوری مراحل۔ کچھ بھی چھوڑ دیں؛ بعد میں مکمل کر سکتے ہیں۔',
      tl: 'Kung sino ang nakatira dito, ang mga minamaneho ninyo, ang mga kuwarto — ilang mabilis na hakbang. Puwedeng laktawan ang kahit ano at tapusin mamaya.',
    },
  'Skip this step': {
    he: 'דילוג על הצעד',
    ur: 'یہ مرحلہ چھوڑیں',
    tl: 'Laktawan ang hakbang na ito',
  },
  'The grown-ups': { he: 'המבוגרים', ur: 'بڑے', tl: 'Ang mga nakatatanda' },
  'You are the first parent. Add a co-parent or spouse if there is one.': {
    he: 'אתם ההורה הראשון. הוסיפו הורה נוסף או בן/בת זוג אם יש.',
    ur: 'آپ پہلے والد ہیں۔ اگر کوئی شریکِ حیات یا دوسرا والد/والدہ ہے تو شامل کریں۔',
    tl: 'Kayo ang unang magulang. Idagdag ang kapareha o asawa kung mayroon.',
  },
  'Add a co-parent': {
    he: 'הוספת הורה נוסף',
    ur: 'شریکِ حیات شامل کریں',
    tl: 'Magdagdag ng kapareha',
  },
  "Co-parent's name": {
    he: 'שם ההורה הנוסף',
    ur: 'شریکِ حیات کا نام',
    tl: 'Pangalan ng kaparehang magulang',
  },
  'The children': { he: 'הילדים', ur: 'بچے', tl: 'Ang mga bata' },
  'Add the kids who live at home — a name is enough to start.': {
    he: 'הוסיפו את הילדים שגרים בבית — שם מספיק כדי להתחיל.',
    ur: 'گھر میں رہنے والے بچوں کو شامل کریں — شروعات کے لیے نام کافی ہے۔',
    tl: 'Idagdag ang mga batang nakatira sa bahay — sapat na ang pangalan para magsimula.',
  },
  "Child's name": { he: 'שם הילד/ה', ur: 'بچے کا نام', tl: 'Pangalan ng bata' },
  'Birth date (optional)': {
    he: 'תאריך לידה (אופציונלי)',
    ur: 'تاریخِ پیدائش (اختیاری)',
    tl: 'Petsa ng kapanganakan (opsyonal)',
  },
  'School (optional)': {
    he: 'בית ספר (אופציונלי)',
    ur: 'اسکول (اختیاری)',
    tl: 'Paaralan (opsyonal)',
  },
  Parents: { he: 'הורים', ur: 'والدین', tl: 'Mga magulang' },
  'Add a child': { he: 'הוספת ילד/ה', ur: 'بچہ شامل کریں', tl: 'Magdagdag ng bata' },
  'The pets': { he: 'חיות המחמד', ur: 'پالتو جانور', tl: 'Ang mga alaga' },
  'Every dog, cat, bird and fish counts.': {
    he: 'כל כלב, חתול, ציפור ודג נחשבים.',
    ur: 'ہر کتا، بلی، پرندہ اور مچھلی شمار ہوتی ہے۔',
    tl: 'Mahalaga ang bawat aso, pusa, ibon at isda.',
  },
  "Pet's name": { he: 'שם חיית המחמד', ur: 'پالتو کا نام', tl: 'Pangalan ng alaga' },
  Species: { he: 'סוג', ur: 'قسم', tl: 'Uri' },
  Bird: { he: 'ציפור', ur: 'پرندہ', tl: 'Ibon' },
  'Breed (optional)': { he: 'גזע (אופציונלי)', ur: 'نسل (اختیاری)', tl: 'Lahi (opsyonal)' },
  'Who does it belong to?': { he: 'של מי זה?', ur: 'یہ کس کا ہے؟', tl: 'Kanino ito?' },
  'Add a pet': { he: 'הוספת חיית מחמד', ur: 'پالتو شامل کریں', tl: 'Magdagdag ng alaga' },
  'The help': { he: 'העזרה', ur: 'ملازمین', tl: 'Ang mga katulong' },
  'Staff with the app are added from People. Occasional help — a weekly cleaner, the gardener — lives here without an account.':
    {
      he: 'עובדים עם האפליקציה מתווספים ממסך האנשים. עזרה מזדמנת — מנקה שבועית, הגנן — נשמרת כאן בלי חשבון.',
      ur: 'ایپ والے ملازمین People سے شامل ہوتے ہیں۔ کبھی کبھار کی مدد — ہفتہ وار صفائی والی، مالی — یہاں بغیر اکاؤنٹ رہتی ہے۔',
      tl: 'Ang mga staff na may app ay idinaragdag mula sa People. Ang paminsan-minsang tulong — lingguhang tagapaglinis, ang hardinero — ay nandito nang walang account.',
    },
  'Add staff with the app': {
    he: 'הוספת עובד עם האפליקציה',
    ur: 'ایپ والا ملازم شامل کریں',
    tl: 'Magdagdag ng staff na may app',
  },
  'External or occasional staff': {
    he: 'עזרה חיצונית או מזדמנת',
    ur: 'بیرونی یا کبھی کبھار کا عملہ',
    tl: 'Panlabas o paminsan-minsang katulong',
  },
  'Staff name': { he: 'שם העובד/ת', ur: 'ملازم کا نام', tl: 'Pangalan ng katulong' },
  'Add staff member': { he: 'הוספת עובד/ת', ur: 'ملازم شامل کریں', tl: 'Magdagdag ng katulong' },
  'The vehicles': { he: 'הרכבים', ur: 'گاڑیاں', tl: 'Ang mga sasakyan' },
  'The cars, bikes and vans the household runs.': {
    he: 'המכוניות, האופנועים והוואנים של הבית.',
    ur: 'گھر کی کاریں، موٹرسائیکلیں اور وینیں۔',
    tl: 'Ang mga kotse, bisikleta at van ng sambahayan.',
  },
  'Vehicle name': { he: 'שם הרכב', ur: 'گاڑی کا نام', tl: 'Pangalan ng sasakyan' },
  'Plate (optional)': {
    he: 'לוחית רישוי (אופציונלי)',
    ur: 'نمبر پلیٹ (اختیاری)',
    tl: 'Plaka (opsyonal)',
  },
  'Make (optional)': { he: 'יצרן (אופציונלי)', ur: 'کمپنی (اختیاری)', tl: 'Brand (opsyonal)' },
  'Model (optional)': { he: 'דגם (אופציונלי)', ur: 'ماڈل (اختیاری)', tl: 'Modelo (opsyonal)' },
  'Who usually drives it?': {
    he: 'מי בדרך כלל נוהג?',
    ur: 'عام طور پر کون چلاتا ہے؟',
    tl: 'Sino ang karaniwang nagmamaneho nito?',
  },
  'Add a vehicle': { he: 'הוספת רכב', ur: 'گاڑی شامل کریں', tl: 'Magdagdag ng sasakyan' },
  'Your place': { he: 'הבית שלך', ur: 'آپ کی جگہ', tl: 'Ang inyong lugar' },
  'The property and the rooms in it.': {
    he: 'הנכס והחדרים שבו.',
    ur: 'جائیداد اور اس کے کمرے۔',
    tl: 'Ang ari-arian at ang mga kuwarto nito.',
  },
  'Property name': { he: 'שם הנכס', ur: 'جائیداد کا نام', tl: 'Pangalan ng ari-arian' },
  Apartment: { he: 'דירה', ur: 'فلیٹ', tl: 'Apartment' },
  Villa: { he: 'וילה', ur: 'بنگلہ', tl: 'Villa' },
  Rooms: { he: 'חדרים', ur: 'کمرے', tl: 'Mga kuwarto' },
  'Room name': { he: 'שם החדר', ur: 'کمرے کا نام', tl: 'Pangalan ng kuwarto' },
  'Who keeps it?': {
    he: 'מי אחראי עליו?',
    ur: 'اس کی دیکھ بھال کون کرتا ہے؟',
    tl: 'Sino ang nag-aalaga nito?',
  },
  'Add a room': { he: 'הוספת חדר', ur: 'کمرہ شامل کریں', tl: 'Magdagdag ng kuwarto' },
  'Review your home': {
    he: 'סקירת הבית',
    ur: 'اپنے گھر کا جائزہ لیں',
    tl: 'Suriin ang inyong tahanan',
  },
  'Everything you told me, in one place. Tap a section to change it.': {
    he: 'כל מה שסיפרתם, במקום אחד. הקישו על סעיף כדי לשנות.',
    ur: 'جو کچھ آپ نے بتایا، ایک جگہ۔ تبدیلی کے لیے کسی حصے پر ٹیپ کریں۔',
    tl: 'Lahat ng sinabi ninyo, nasa isang lugar. I-tap ang isang seksyon para baguhin.',
  },
  'Save my home': {
    he: 'שמירת הבית שלי',
    ur: 'میرا گھر محفوظ کریں',
    tl: 'I-save ang aking tahanan',
  },
  Children: { he: 'ילדים', ur: 'بچے', tl: 'Mga bata' },
  Vehicles: { he: 'רכבים', ur: 'گاڑیاں', tl: 'Mga sasakyan' },
  Property: { he: 'נכס', ur: 'جائیداد', tl: 'Ari-arian' },
  'Some entries could not be saved — fix them and try again.': {
    he: 'חלק מהרשומות לא נשמרו — תקנו ונסו שוב.',
    ur: 'کچھ اندراجات محفوظ نہیں ہوئے — درست کر کے دوبارہ کوشش کریں۔',
    tl: 'May ilang entry na hindi na-save — ayusin at subukan muli.',
  },
  'Nothing here yet — every step was skipped.': {
    he: 'עוד אין כלום — כל הצעדים דולגו.',
    ur: 'ابھی کچھ نہیں — ہر مرحلہ چھوڑ دیا گیا۔',
    tl: 'Wala pa rito — nilaktawan ang bawat hakbang.',
  },
  'Your home is built': {
    he: 'הבית שלך מוכן',
    ur: 'آپ کا گھر تیار ہے',
    tl: 'Tapos na ang inyong tahanan',
  },
  'Everyone and everything is in. You can change it all later from Settings.': {
    he: 'כולם והכול בפנים. אפשר לשנות הכל מאוחר יותר מההגדרות.',
    ur: 'سب کچھ شامل ہے۔ بعد میں ترتیبات سے سب بدلا جا سکتا ہے۔',
    tl: 'Nandito na ang lahat. Mababago ninyo ang lahat mamaya mula sa Settings.',
  },

  // Home Brain — the onboarding questions that teach Famanage the owner's
  // decision preferences (apps/mobile/app/(owner)/home-brain.tsx) and the
  // declared-capabilities card on the person page.
  'Home Brain': { he: 'המוח של הבית', ur: 'ہوم برین', tl: 'Home Brain' },
  'Next Question': { he: 'השאלה הבאה', ur: 'اگلا سوال', tl: 'Susunod na tanong' },
  'Finish Setup': { he: 'סיום ההקמה', ur: 'سیٹ اپ مکمل کریں', tl: 'Tapusin ang setup' },
  'Finalize Setup': { he: 'סיימו את ההקמה', ur: 'سیٹ اپ حتمی کریں', tl: 'I-finalize ang setup' },
  Recommended: { he: 'מומלץ', ur: 'تجویز کردہ', tl: 'Inirerekomenda' },
  'What is my daily spending limit?': {
    he: 'מה מגבלת ההוצאה היומית שלי?',
    ur: 'میری روزانہ خرچ کی حد کیا ہے؟',
    tl: 'Ano ang daily spending limit ko?',
  },
  'For routine errands without asking. Medical or unusual costs always require your approval.': {
    he: 'לסידורים שגרתיים בלי לשאול. הוצאות רפואיות או חריגות תמיד דורשות את האישור שלך.',
    ur: 'بغیر پوچھے معمول کی چیزوں کے لیے۔ طبی یا غیر معمولی اخراجات کے لیے ہمیشہ آپ کی منظوری درکار ہے۔',
    tl: 'Para sa mga routine na errand nang hindi na nagtatanong. Ang medical o hindi karaniwang gastos ay laging nangangailangan ng approval ninyo.',
  },
  'For routine errands without asking': {
    he: 'לסידורים שגרתיים בלי לשאול',
    ur: 'بغیر پوچھے معمول کی چیزوں کے لیے',
    tl: 'Para sa mga routine na errand nang hindi na nagtatanong',
  },
  'Medical or unusual costs always require your approval.': {
    he: 'הוצאות רפואיות או חריגות תמיד דורשות את האישור שלך.',
    ur: 'طبی یا غیر معمولی اخراجات کے لیے ہمیشہ آپ کی منظوری درکار ہے۔',
    tl: 'Ang medical o hindi karaniwang gastos ay laging nangangailangan ng approval ninyo.',
  },
  'Custom Limit': { he: 'מגבלה מותאמת', ur: 'حسبِ منشا حد', tl: 'Custom na limit' },
  'Always ask for these': {
    he: 'על אלה שואלים תמיד',
    ur: 'ان کے بارے میں ہمیشہ پوچھیں',
    tl: 'Laging magtanong tungkol sa mga ito',
  },
  'No additional exceptions': {
    he: 'אין חריגים נוספים',
    ur: 'کوئی مزید استثنا نہیں',
    tl: 'Walang karagdagang exception',
  },
  Health: { he: 'בריאות', ur: 'صحت', tl: 'Kalusugan' },
  Safety: { he: 'בטיחות', ur: 'حفاظت', tl: 'Kaligtasan' },
  'Daily spending limit': {
    he: 'מגבלת הוצאה יומית',
    ur: 'روزانہ خرچ کی حد',
    tl: 'Daily spending limit',
  },
  'Product substitutions': {
    he: 'תחליפי מוצרים',
    ur: 'مصنوعات کے متبادل',
    tl: 'Mga pamalit na produkto',
  },
  'If a regular product is unavailable, what should I do?': {
    he: 'אם מוצר רגיל לא זמין, מה לעשות?',
    ur: 'اگر کوئی معمول کی چیز دستیاب نہ ہو تو کیا کروں؟',
    tl: 'Kung hindi available ang karaniwang produkto, ano ang gagawin ko?',
  },
  'Closest alternative within budget': {
    he: 'התחליף הקרוב ביותר בתוך התקציב',
    ur: 'بجٹ میں قریب ترین متبادل',
    tl: 'Pinakamalapit na pamalit na pasok sa budget',
  },
  'Show me 2 options with prices': {
    he: 'להראות לי 2 אפשרויות עם מחירים',
    ur: 'قیمتوں کے ساتھ 2 اختیارات دکھائیں',
    tl: 'Ipakita sa akin ang 2 option na may presyo',
  },
  'Never substitute without my explicit approval': {
    he: 'לא להחליף בלי האישור המפורש שלי',
    ur: 'میری واضح منظوری کے بغیر کبھی متبادل نہ لگائیں',
    tl: 'Huwag kailangang magpalit nang walang tahasan kong approval',
  },
  'When tasks conflict, what comes first?': {
    he: 'כשמשימות מתנגשות, מה קודם?',
    ur: 'جب کام آپس میں ٹکرائیں تو پہلے کیا؟',
    tl: 'Kapag nag-uumpugan ang mga gawain, alin ang nauuna?',
  },
  'When tasks conflict': {
    he: 'כשמשימות מתנגשות',
    ur: 'جب کام آپس میں ٹکرائیں',
    tl: 'Kapag nag-uumpugan ang mga gawain',
  },
  'Protect fixed family schedules first': {
    he: 'לשמור קודם על לוחות הזמנים הקבועים של המשפחה',
    ur: 'پہلے خاندان کے طے شدہ نظام الاوقات کی حفاظت کریں',
    tl: 'Unahing protektahan ang mga nakatakdang schedule ng pamilya',
  },
  'Try to finish both tasks without any delay': {
    he: 'לנסות לסיים את שתי המשימות בלי עיכוב',
    ur: 'دونوں کام بغیر تاخیر ختم کرنے کی کوشش کریں',
    tl: 'Subukang tapusin ang dalawang gawain nang walang pagkaantala',
  },
  'Automatically reassign to another helper': {
    he: 'להעביר אוטומטית לעוזר אחר',
    ur: 'خودکار طور پر کسی دوسرے مددگار کو سونپیں',
    tl: 'Awtomatikong ilipat sa ibang katulong',
  },
  'Always ask me if a conflict cannot be resolved safely': {
    he: 'תמיד לשאול אותי אם אי אפשר ליישב סתירה בבטחה',
    ur: 'اگر ٹکراؤ محفوظ طور پر حل نہ ہو تو ہمیشہ مجھ سے پوچھیں',
    tl: 'Lagi akong tanungin kung hindi malulutas nang ligtas ang alitan',
  },
  'When should I consider a task finished?': {
    he: 'מתי משימה נחשבת לגמורה?',
    ur: 'کب سمجھوں کہ کام مکمل ہو گیا؟',
    tl: 'Kailan ko ituturing na tapos ang isang gawain?',
  },
  'When a task counts as finished': {
    he: 'מתי משימה נחשבת גמורה',
    ur: 'کب کام مکمل شمار ہو',
    tl: 'Kailan ituturing na tapos ang gawain',
  },
  'When the person confirms via WhatsApp': {
    he: 'כשהאדם מאשר בווצאפ',
    ur: 'جب وہ شخص واٹس ایپ پر تصدیق کرے',
    tl: 'Kapag kinumpirma ng tao sa WhatsApp',
  },
  'Only when there is a photo or receipt': {
    he: 'רק כשיש תמונה או קבלה',
    ur: 'صرف جب تصویر یا رسید ہو',
    tl: 'Kapag may litrato o resibo lamang',
  },
  'Choose based on task type': {
    he: 'לבחור לפי סוג המשימה',
    ur: 'کام کی قسم کے مطابق منتخب کریں',
    tl: 'Pumili batay sa uri ng gawain',
  },
  'How often should I update you?': {
    he: 'כמה פעמים לעדכן אותך?',
    ur: 'کتی بار آپ کو اپڈیٹ کروں؟',
    tl: 'Gaano kadalas kita i-update?',
  },
  'How often to update the owner': {
    he: 'כמה פעמים לעדכן את הבעלים',
    ur: 'مالک کو کتی بار اپڈیٹ کریں',
    tl: 'Gaano kadalas i-update ang may-ari',
  },
  'Only when I need to decide': {
    he: 'רק כשאני צריך להחליט',
    ur: 'صرف جب فیصلہ کرنا ہو',
    tl: 'Kapag may kailangan lang akong pagdesisyunan',
  },
  'One quiet daily summary': {
    he: 'סיכום יומי אחד ושקט',
    ur: 'ایک خاموش روزانہ خلاصہ',
    tl: 'Isang tahimik na daily summary',
  },
  'Goal: Under 30 seconds of attention per day.': {
    he: 'המטרה: פחות מ־30 שניות של תשומת לב ביום.',
    ur: 'ہدف: دن میں 30 سیکنڈ سے کم توجہ۔',
    tl: 'Layunin: Mas kaunti sa 30 segundo ng atensyon bawat araw.',
  },
  'Your Home Brain just got smarter.': {
    he: 'המוח של הבית שלך ממש התחכם.',
    ur: 'آپ کا ہوم برین ابھی ہوشیار ہوا۔',
    tl: 'Ang Home Brain ninyo ay mas naging matalino.',
  },
  'Famanage now understands your preferences for decisions, spending, and priorities.': {
    he: 'פמנג׳ עכשיו מבין את ההעדפות שלך להחלטות, הוצאות וסדרי עדיפויות.',
    ur: 'فامینیج اب آپ کی فیصلوں، اخراجات اور ترجیحات کی ترجیحات سمجھتا ہے۔',
    tl: 'Nauunawaan na ng Famanage ang inyong mga preference sa mga desisyon, gastos, at prayoridad.',
  },
  'What they handle': {
    he: 'מה הם יכולים לעשות',
    ur: 'وہ کیا کر سکتے ہیں',
    tl: 'Ang kaya nilang gawin',
  },
  'Tap everything this person handles. Tasks route only to people who can do them.': {
    he: 'סמנו כל דבר שהאדם הזה מטפל בו. משימות מופנות רק לאנשים שיכולים לבצע אותן.',
    ur: 'ہر وہ کام منتخب کریں جو یہ شخص سنبھالتا ہے۔ کام صرف ان لوگوں کو بھیجے جاتے ہیں جو انہیں کر سکتے ہیں۔',
    tl: 'I-tap ang lahat ng kayang asikasuhin ng taong ito. Ang mga gawain ay napupunta lamang sa mga taong kayang gumawa nito.',
  },
  'A name is required.': {
    he: 'חובה להזין שם.',
    ur: 'نام درکار ہے۔',
    tl: 'Kailangan ang pangalan.',
  },
  'Dates are YYYY-MM-DD.': {
    he: 'תאריכים בפורמט YYYY-MM-DD.',
    ur: 'تاریخیں YYYY-MM-DD کی صورت میں۔',
    tl: 'Ang mga petsa ay YYYY-MM-DD.',
  },
  'My home': { he: 'הבית שלי', ur: 'میرا گھر', tl: 'Aking tahanan' },
  'Everyone and everything at home': {
    he: 'כולם והכול בבית',
    ur: 'گھر کے سب لوگ اور سب کچھ',
    tl: 'Lahat ng tao at bagay sa bahay',
  },
  'Remove from your home?': {
    he: 'להסיר מהבית?',
    ur: 'گھر سے ہٹا دیں؟',
    tl: 'Alisin sa inyong tahanan?',
  },
  'Nobody specific': { he: 'אף אחד מסוים', ur: 'کوئی خاص نہیں', tl: 'Walang partikular' },
  'Already at home': { he: 'כבר בבית', ur: 'پہلے سے گھر میں', tl: 'Nasa bahay na' },
  New: { he: 'חדש', ur: 'نیا', tl: 'Bago' },

  // ── Wave 4 (module coherence): wiring + client half — the 'Household
  // members' block in owner Settings. Kind labels reuse the Build Your Home
  // keys above. ──
  'Household members': {
    he: 'חברי משק הבית',
    ur: 'گھر کے افراد',
    tl: 'Mga miyembro ng sambahayan',
  },
  'No household members yet.': {
    he: 'עוד אין חברי משק בית.',
    ur: 'ابھی گھر کا کوئی فرد نہیں۔',
    tl: 'Wala pang miyembro ng sambahayan.',
  },

  // ── Wave 4 (module coherence): screen half — the member detail page
  // (apps/mobile app/(owner)/member/[id].tsx). ──
  Parent: { he: 'הורה', ur: 'والد یا والدہ', tl: 'Magulang' },
  Child: { he: 'ילד', ur: 'بچہ', tl: 'Bata' },
  Guest: { he: 'אורח', ur: 'مہمان', tl: 'Bisita' },
  Vehicle: { he: 'רכב', ur: 'گاڑی', tl: 'Sasakyan' },
  Room: { he: 'חדר', ur: 'کمرہ', tl: 'Kuwarto' },
  Archived: { he: 'בארכיון', ur: 'آرکائیو شدہ', tl: 'Naka-archive' },
  'Unknown member': { he: 'חבר לא ידוע', ur: 'نامعلوم رکن', tl: 'Hindi kilalang miyembro' },
  Relationships: { he: 'קשרים', ur: 'تعلقات', tl: 'Mga ugnayan' },
  'No relationships yet': {
    he: 'עדיין אין קשרים',
    ur: 'ابھی کوئی تعلقات نہیں',
    tl: 'Wala pang mga ugnayan',
  },
  'On the timeline': { he: 'בציר הזמן', ur: 'ٹائم لائن پر', tl: 'Sa timeline' },
  'Nothing on the timeline in the next two weeks': {
    he: 'שום דבר לא מתוכנן בציר הזמן בשבועיים הקרובים',
    ur: 'اگلے دو ہفتوں میں ٹائم لائن پر کچھ نہیں',
    tl: 'Walang nakatakda sa timeline sa susunod na dalawang linggo',
  },
  'Routines & suggestions': {
    he: 'שגרות והצעות',
    ur: 'معمولات اور تجاویز',
    tl: 'Mga routine at mungkahi',
  },
  'No routines or suggestions mention them yet': {
    he: 'אין עדיין שגרות או הצעות שמזכירות אותם',
    ur: 'ابھی کوئی معمول یا تجویز ان کا ذکر نہیں کرتی',
    tl: 'Wala pang routine o mungkahi na nagbanggit sa kanila',
  },
  'Open the person page': {
    he: 'מעבר לעמוד האדם',
    ur: 'شخص کا صفحہ کھولیں',
    tl: 'Buksan ang page ng tao',
  },
  'Pay, access and scheduling live there.': {
    he: 'שכר, גישה וסידור עבודה מנוהלים שם.',
    ur: 'تنخواہ، رسائی اور شیڈول وہیں منظم ہیں۔',
    tl: 'Nandoon ang sahod, access, at iskedyul.',
  },
  Age: { he: 'גיל', ur: 'عمر', tl: 'Edad' },
  months: { he: 'חודשים', ur: 'مہینے', tl: 'buwan' },
  years: { he: 'שנים', ur: 'سال', tl: 'taon' },
  Breed: { he: 'גזע', ur: 'نسل', tl: 'Lahi' },
  Food: { he: 'אוכל', ur: 'کھانا', tl: 'Pagkain' },
  Vet: { he: 'וטרינר', ur: 'ویٹرنری ڈاکٹر', tl: 'Beternaryo' },
  'Next vaccination': { he: 'החיסון הבא', ur: 'اگلا ٹیکہ', tl: 'Susunod na bakuna' },
  Plate: { he: 'לוחית רישוי', ur: 'نمبر پلیٹ', tl: 'Plaka' },
  'Insurance until': { he: 'ביטוח בתוקף עד', ur: 'انشورنس تا', tl: 'Insurance hanggang' },
  'Registration until': { he: 'רישוי רכב בתוקף עד', ur: 'رجسٹریشن تا', tl: 'Rehistro hanggang' },
  Phone: { he: 'טלפון', ur: 'فون', tl: 'Telepono' },
  Birthday: { he: 'יום הולדת', ur: 'سالگرہ', tl: 'Kaarawan' },
  Type: { he: 'סוג', ur: 'قسم', tl: 'Uri' },
  Floor: { he: 'קומה', ur: 'منزل', tl: 'Palapag' },
  'Parent of': { he: 'הורה של', ur: 'کا/کی والد یا والدہ', tl: 'Magulang ni' },
  'Owner of': { he: 'בעלים של', ur: 'کا مالک', tl: 'May-ari ng' },
  Attends: { he: 'משתתף ב', ur: 'میں شریک', tl: 'Dumadalo sa' },
  'Responsible for': { he: 'אחראי על', ur: 'کا ذمہ دار', tl: 'Responsable sa' },
  'Lives in': { he: 'גר ב', ur: 'میں رہتا ہے', tl: 'Nakatira sa' },
  'Related to': { he: 'קשור ל', ur: 'سے متعلق', tl: 'Kaugnay ng' },
  // ---------------------------------------------------------------------
  // Merged from apps/web/src/i18n.ts (2026-08-06): keys that existed only
  // in the web's fork of this table, folded in when apps/web switched to
  // importing @famanage/i18n instead of keeping its own copy.
  // ---------------------------------------------------------------------
  'The house is busy — back in a moment.': {
    he: 'הבית עסוק — חוזרים בעוד רגע.',
    ur: 'گھر مصروف ہے — ایک لمحے میں واپس۔',
    tl: 'Abala ang bahay — babalik sa isang saglit.',
  },
  'Add a job': { he: 'הוסף משימה', ur: 'کام شامل کریں', tl: 'Magdagdag ng gawain' },
  Date: { he: 'תאריך', ur: 'تاریخ', tl: 'Petsa' },
  End: { he: 'סיום', ur: 'اختتام', tl: 'Wakas' },
  Frequency: { he: 'תדירות', ur: 'تعدد', tl: 'Dalas' },
  'Invite link': { he: 'קישור הזמנה', ur: 'دعوتی لنک', tl: 'Link ng imbitasyon' },
  Israel: { he: 'ישראל', ur: 'اسرائیل', tl: 'Israel' },
  UAE: { he: 'איחוד האמירויות', ur: 'متحدہ عرب امارات', tl: 'UAE' },
  'United States': { he: 'ארצות הברית', ur: 'ریاستہائے متحدہ', tl: 'Estados Unidos' },
  '6 · online': { he: '6 · מקוון', ur: '6 · آن لائن', tl: '6 · online' },
  'Microphone unavailable — type instead': {
    he: 'המיקרופון לא זמין — הקלד במקום',
    ur: 'مائیکروفون دستیاب نہیں — لکھیں',
    tl: 'Hindi available ang mikropono — mag-type na lang',
  },
  'Notifications are not available right now': {
    he: 'התראות אינן זמינות כרגע',
    ur: 'اطلاعات ابھی دستیاب نہیں ہیں',
    tl: 'Hindi available ang mga notification ngayon',
  },
  'Which task is it about?': {
    he: 'על איזו משימה מדובר?',
    ur: 'یہ کس کام کے بارے میں ہے؟',
    tl: 'Tungkol sa aling gawain ito?',
  },
  'username (lowercase, dot, underscore)': {
    he: 'שם משתמש (אותיות קטנות, נקודה, קו תחתון)',
    ur: 'صارف نام (چھوٹے حروف، نقطہ، انڈراسکور)',
    tl: 'username (maliliit na letra, tuldok, underscore)',
  },
  'This page does not exist': {
    he: 'הדף הזה לא קיים',
    ur: 'یہ صفحہ موجود نہیں',
    tl: 'Walang ganitong page',
  },
  'The link may be old or mistyped.': {
    he: 'ייתכן שהקישור ישן או שגוי.',
    ur: 'لنک پرانا یا غلط ٹائپ ہوا ہو سکتا ہے۔',
    tl: 'Maaaring luma o mali ang link.',
  },
  'Back to home': { he: 'חזרה לבית', ur: 'ہوم پر واپس', tl: 'Bumalik sa home' },
  'Username or phone': {
    he: 'שם משתמש או טלפון',
    ur: 'صارف نام یا فون نمبر',
    tl: 'Username o numero ng telepono',
  },
  'Improve my wording': {
    he: 'שפר את הניסוח',
    ur: 'میری تحریر بہتر کریں',
    tl: 'Pagandahin ang sinulat ko',
  },
  '{{n}} could not send': {
    he: '{{n}} לא נשלחו',
    ur: '{{n}} بھیجے نہیں جا سکے',
    tl: '{{n}} ang hindi naipadala',
  },
  'The old way': { he: 'הדרך הישנה', ur: 'پرانا طریقہ', tl: 'Ang lumang paraan' },
  'This is how your home runs today.': {
    he: 'ככה הבית שלך מתנהל היום.',
    ur: 'آج آپ کا گھر اسی طرح چلتا ہے۔',
    tl: 'Ganito tumatakbo ang bahay mo ngayon.',
  },
  'House group': { he: 'קבוצת הבית', ur: 'گھر کا گروپ', tl: 'House group' },
  'did you pay the gardener??': {
    he: 'שילמת לגנן??',
    ur: 'کیا مالی کو ادا کیا??',
    tl: 'binayaran mo ba ang gardener??',
  },
  'Please send the receipt photo again. I lost it.': {
    he: 'תשלחו שוב את תמונת הקבלה. איבדתי אותה.',
    ur: 'براہ کرم رسید کی تصویر دوبارہ بھیجیں۔ وہ کھو گئی۔',
    tl: 'Pakipadala ulit ang litrato ng resibo. Nawala ko.',
  },
  'Voice message · 0:47': {
    he: 'הודעה קולית · 0:47',
    ur: 'صوتی پیغام · 0:47',
    tl: 'Voice message · 0:47',
  },
  'The AC guy came but nobody told me': {
    he: 'טכנאי המזגן הגיע אבל אף אחד לא אמר לי',
    ur: 'اے سی والا آیا مگر کسی نے مجھے نہیں بتایا',
    tl: 'Dumating ang AC technician pero walang nagsabi sa akin',
  },
  'what time is pickup tomorrow?': {
    he: 'באיזו שעה האיסוף מחר?',
    ur: 'کل پک اپ کتنے بجے ہے؟',
    tl: 'anong oras ang pickup bukas?',
  },
  'sorry — just seeing this. was in meetings all day': {
    he: 'סליחה — רק עכשיו רואה. הייתי בפגישות כל היום',
    ur: 'معذرت — ابھی دیکھا۔ سارا دن میٹنگز میں تھا',
    tl: 'sorry — ngayon ko lang nakita. nasa meetings buong araw',
  },
  'typing…': { he: 'מקליד/ה…', ur: 'لکھ رہا ہے…', tl: 'nagta-type…' },
  "That's not a home. That's a company running on hope.": {
    he: 'זה לא בית. זו חברה שרצה על תקווה.',
    ur: 'یہ گھر نہیں۔ یہ امید پر چلنے والی کمپنی ہے۔',
    tl: 'Hindi ito tahanan. Ito ay kumpanyang umaasa na lang.',
  },
  'Every day, your household spends money, runs errands and repeats routines — with no record, no memory, and no system.':
    {
      he: 'כל יום, הבית שלך מוציא כסף, עושה סידורים וחוזר על שגרות — בלי תיעוד, בלי זיכרון, בלי מערכת.',
      ur: 'ہر روز آپ کا گھر پیسہ خرچ کرتا ہے، کام چلاتا ہے اور روٹین دہراتا ہے — بغیر ریکارڈ، بغیر یادداشت، بغیر نظام۔',
      tl: 'Araw-araw, gumagastos ang bahay mo, may mga errand, at umuulit ng routines — walang record, walang memorya, walang sistema.',
    },
  'The kids were picked up.': {
    he: 'הילדים נאספו.',
    ur: 'بچے اٹھا لیے گئے۔',
    tl: 'Nakuha ang mga bata.',
  },
  'The driver arrived.': { he: 'הנהג הגיע.', ur: 'ڈرائیور پہنچ گیا۔', tl: 'Dumating ang driver.' },
  'The housekeeper was reimbursed.': {
    he: 'העוזרת קיבלה החזר.',
    ur: 'ملازمہ کو رقم واپس مل گئی۔',
    tl: 'Na-reimburse ang katulong.',
  },
  'You never had to think about any of it.': {
    he: 'ולא היית צריך לחשוב על שום דבר מזה.',
    ur: 'اور آپ کو کسی چیز کے بارے میں سوچنا نہیں پڑا۔',
    tl: 'At hindi mo na kailangang isipin ang alinman diyan.',
  },
  'You pay only when your household goes live.': {
    he: 'משלמים רק כשהבית שלך עולה לאוויר.',
    ur: 'آپ صرف تب ادا کرتے ہیں جب آپ کا گھر لائیو ہو۔',
    tl: 'Babayaran mo lang kapag live na ang bahay mo.',
  },
  'Your home already runs. Let it run itself.': {
    he: 'הבית שלך כבר רץ. תן לו לרוץ לבד.',
    ur: 'آپ کا گھر پہلے سے چل رہا ہے۔ اسے خود چلنے دیں۔',
    tl: 'Tumatakbo na ang bahay mo. Hayaan mo na itong tumakbo mag-isa.',
  },
  'Real product. Real house. Running today.': {
    he: 'מוצר אמיתי. בית אמיתי. רץ היום.',
    ur: 'حقیقی پروڈکٹ۔ حقیقی گھر۔ آج چل رہا ہے۔',
    tl: 'Totoong produkto. Totoong bahay. Tumatakbo ngayon.',
  },
  // Private notes (apps/web/src/components/Notes.tsx,
  // apps/mobile/src/components/NotesScreen.tsx). The screen's whole promise
  // is in one sentence — "only you can see these" — and a housekeeper who
  // reads Urdu has no reason to believe a promise she cannot read.
  'My notes': { he: 'ההערות שלי', ur: 'میرے نوٹس', tl: 'Mga tala ko' },
  'Only you can see these. Nobody else in the household can.': {
    he: 'רק את/ה רואה את אלה. אף אחד אחר בבית לא.',
    ur: 'یہ صرف آپ دیکھ سکتے ہیں۔ گھر میں کوئی اور نہیں۔',
    tl: 'Ikaw lang ang nakakakita nito. Walang iba sa bahay.',
  },
  'Write a note to yourself…': {
    he: 'כתוב/כתבי לעצמך הערה…',
    ur: 'خود کو ایک نوٹ لکھیں…',
    tl: 'Magsulat ng tala para sa sarili mo…',
  },
  'Add note': { he: 'הוסף הערה', ur: 'نوٹ شامل کریں', tl: 'Magdagdag ng tala' },
  'No notes yet.': { he: 'אין עדיין הערות.', ur: 'ابھی کوئی نوٹ نہیں۔', tl: 'Wala pang tala.' },
  'Write down the thing you keep meaning to remember.': {
    he: 'רשום/רשמי את הדבר שאת/ה כל הזמן מנסה לזכור.',
    ur: 'وہ بات لکھ لیں جو آپ ہمیشہ یاد رکھنا چاہتے ہیں۔',
    tl: 'Isulat ang bagay na lagi mong sinusubukang tandaan.',
  },
  'Not done': { he: 'לא בוצע', ur: 'مکمل نہیں', tl: 'Hindi pa tapos' },
  'Nothing left to do.': {
    he: 'לא נשאר מה לעשות.',
    ur: 'کرنے کو کچھ باقی نہیں۔',
    tl: 'Wala nang natitirang gawin.',
  },
  'Delete this note?': {
    he: 'למחוק את ההערה הזו?',
    ur: 'یہ نوٹ حذف کریں؟',
    tl: 'Burahin ang talang ito?',
  },
  'Remind me': { he: 'הזכר לי', ur: 'مجھے یاد دلائیں', tl: 'Paalalahanan ako' },
  'Show more': { he: 'הצג עוד', ur: 'مزید دکھائیں', tl: 'Magpakita pa' },
  'Just for me': { he: 'רק בשבילי', ur: 'صرف میرے لیے', tl: 'Para lang sa akin' },
  'Saving…': { he: 'שומר…', ur: 'محفوظ ہو رہا ہے…', tl: 'Sine-save…' },
  'Couldn’t load your notes.': {
    he: 'לא הצלחנו לטעון את ההערות שלך.',
    ur: 'آپ کے نوٹس لوڈ نہیں ہو سکے۔',
    tl: 'Hindi ma-load ang mga tala mo.',
  },
  'Tap to edit, hold to delete': {
    he: 'הקש/י לעריכה, החזק/י למחיקה',
    ur: 'ترمیم کے لیے ٹیپ کریں، حذف کے لیے دبائے رکھیں',
    tl: 'I-tap para baguhin, pindutin nang matagal para burahin',
  },
  // Forgot password (apps/web/src/ForgotPassword.tsx, ResetPassword.tsx).
  // The one flow in this product where a person is locked out and frightened,
  // read in whichever language they panic in. The delivery sentence has to
  // survive translation intact: there is no email sender here, the link goes
  // over WhatsApp, and an owner watching an inbox is an owner still locked out.
  'Forgot password?': {
    he: 'שכחת סיסמה?',
    ur: 'پاس ورڈ بھول گئے؟',
    tl: 'Nakalimutan ang password?',
  },
  'Forgot your password?': {
    he: 'שכחת את הסיסמה?',
    ur: 'کیا آپ اپنا پاس ورڈ بھول گئے؟',
    tl: 'Nakalimutan mo ang password mo?',
  },
  'Give us the email on the account and we will send a link to set a new one.': {
    he: 'תנו לנו את האימייל של החשבון ונשלח קישור לבחירת סיסמה חדשה.',
    ur: 'اکاؤنٹ کا ای میل دیں، ہم نیا پاس ورڈ بنانے کا لنک بھیج دیں گے۔',
    tl: 'Ibigay ang email ng account at magpapadala kami ng link para gumawa ng bago.',
  },
  'Send the reset link': {
    he: 'שלחו את קישור האיפוס',
    ur: 'ری سیٹ لنک بھیجیں',
    tl: 'Ipadala ang reset link',
  },
  'Staff sign in with a username instead — ask the household owner to reset yours.': {
    he: 'עובדים נכנסים עם שם משתמש — בקשו מבעל הבית לאפס לכם את הסיסמה.',
    ur: 'ملازمین صارف نام سے سائن ان کرتے ہیں — اپنا پاس ورڈ ری سیٹ کرانے کے لیے گھر کے مالک سے کہیں۔',
    tl: 'Naka-sign in ang mga kawani gamit ang username — hilingin sa may-ari ng bahay na i-reset ang sa iyo.',
  },
  'Check WhatsApp': {
    he: 'בדקו בוואטסאפ',
    ur: 'واٹس ایپ دیکھیں',
    tl: 'Tingnan ang WhatsApp',
  },
  'If that email has an account here, a reset link is on its way.': {
    he: 'אם לאימייל הזה יש חשבון כאן, קישור לאיפוס בדרך.',
    ur: 'اگر اس ای میل کا یہاں اکاؤنٹ ہے تو ری سیٹ لنک بھیجا جا رہا ہے۔',
    tl: 'Kung may account dito ang email na iyan, paparating na ang reset link.',
  },
  'The link goes to the WhatsApp number saved on the account, and it works for 30 minutes. If no number is saved there, nothing will arrive — ask whoever set up the household to help you back in.':
    {
      he: 'הקישור נשלח למספר הוואטסאפ השמור בחשבון, והוא תקף ל-30 דקות. אם לא שמור שם מספר, לא יגיע כלום — בקשו ממי שהקים את משק הבית לעזור לכם להיכנס.',
      ur: 'لنک اکاؤنٹ پر محفوظ واٹس ایپ نمبر پر جاتا ہے اور 30 منٹ تک کارآمد رہتا ہے۔ اگر وہاں کوئی نمبر محفوظ نہیں تو کچھ نہیں آئے گا — جس نے گھرانہ بنایا تھا اس سے واپس داخل ہونے میں مدد مانگیں۔',
      tl: 'Napupunta ang link sa WhatsApp number na nakasave sa account, at gumagana ito sa loob ng 30 minuto. Kung walang nakasave na number doon, walang darating — humingi ng tulong sa kung sino ang nag-set up ng sambahayan.',
    },
  'Development only — open the reset link': {
    he: 'לפיתוח בלבד — פתחו את קישור האיפוס',
    ur: 'صرف ڈویلپمنٹ کے لیے — ری سیٹ لنک کھولیں',
    tl: 'Para sa development lang — buksan ang reset link',
  },
  'Send it again': { he: 'שלחו שוב', ur: 'دوبارہ بھیجیں', tl: 'Ipadala ulit' },
  'Back to sign in': {
    he: 'חזרה להתחברות',
    ur: 'سائن ان پر واپس',
    tl: 'Balik sa pag-sign in',
  },
  'Set a new password': {
    he: 'בחרו סיסמה חדשה',
    ur: 'نیا پاس ورڈ مقرر کریں',
    tl: 'Maglagay ng bagong password',
  },
  'Ten characters or more. Only you should know it.': {
    he: 'עשרה תווים לפחות. רק אתם צריכים לדעת אותה.',
    ur: 'کم از کم دس حروف۔ یہ صرف آپ کو معلوم ہونا چاہیے۔',
    tl: 'Sampung karakter o higit pa. Ikaw lang ang dapat nakakaalam nito.',
  },
  'Saving this signs the account out on every device, including this one.': {
    he: 'שמירה תנתק את החשבון מכל המכשירים, כולל זה.',
    ur: 'اسے محفوظ کرنے سے اکاؤنٹ ہر ڈیوائس سے سائن آؤٹ ہو جائے گا، اس سمیت۔',
    tl: 'Kapag na-save ito, masi-sign out ang account sa lahat ng device, pati na rito.',
  },
  'Save the new password': {
    he: 'שמרו את הסיסמה החדשה',
    ur: 'نیا پاس ورڈ محفوظ کریں',
    tl: 'I-save ang bagong password',
  },
  'Password changed': {
    he: 'הסיסמה שונתה',
    ur: 'پاس ورڈ تبدیل ہو گیا',
    tl: 'Napalitan ang password',
  },
  'Every device that was signed in to this account has been signed out.': {
    he: 'כל מכשיר שהיה מחובר לחשבון הזה נותק.',
    ur: 'اس اکاؤنٹ میں سائن ان ہر ڈیوائس سائن آؤٹ کر دی گئی ہے۔',
    tl: 'Na-sign out na ang bawat device na naka-sign in sa account na ito.',
  },
  'This link no longer works': {
    he: 'הקישור הזה כבר לא עובד',
    ur: 'یہ لنک اب کام نہیں کرتا',
    tl: 'Hindi na gumagana ang link na ito',
  },
  'Reset links last 30 minutes and can only be used once.': {
    he: 'קישורי איפוס תקפים ל-30 דקות וניתן להשתמש בהם פעם אחת.',
    ur: 'ری سیٹ لنک 30 منٹ تک چلتے ہیں اور صرف ایک بار استعمال ہو سکتے ہیں۔',
    tl: 'Tumatagal ng 30 minuto ang mga reset link at isang beses lang magagamit.',
  },
  'Send me a new link': {
    he: 'שלחו לי קישור חדש',
    ur: 'مجھے نیا لنک بھیجیں',
    tl: 'Padalhan ako ng bagong link',
  },
  // Account security (apps/web/src/AccountSecurity.tsx). A housekeeper reads
  // this page to answer "is anybody else in my account?", and an answer she
  // cannot read is not an answer. The privacy sentence in particular has to
  // land in her own language: it is the difference between believing the list
  // is hers and assuming her employer is reading it.
  'Account security': {
    he: 'אבטחת החשבון',
    ur: 'اکاؤنٹ کی سیکیورٹی',
    tl: 'Seguridad ng account',
  },
  'Changing it signs out every device, including this one.': {
    he: 'שינוי הסיסמה מנתק את כל המכשירים, כולל זה.',
    ur: 'اسے تبدیل کرنے سے ہر ڈیوائس سائن آؤٹ ہو جائے گی، اس سمیت۔',
    tl: 'Kapag pinalitan ito, masi-sign out ang lahat ng device, pati na rito.',
  },
  'Password changed. Sign in again with the new one.': {
    he: 'הסיסמה שונתה. התחברו שוב עם החדשה.',
    ur: 'پاس ورڈ تبدیل ہو گیا۔ نئے پاس ورڈ سے دوبارہ سائن ان کریں۔',
    tl: 'Napalitan ang password. Mag-sign in ulit gamit ang bago.',
  },
  'Where you are signed in': {
    he: 'איפה אתם מחוברים',
    ur: 'آپ کہاں کہاں سائن ان ہیں',
    tl: 'Kung saan ka naka-sign in',
  },
  'Only you see this list — nobody else in the household can. A device stays signed in until you end it here or it expires.':
    {
      he: 'רק אתם רואים את הרשימה הזו — אף אחד אחר בבית לא. מכשיר נשאר מחובר עד שתנתקו אותו כאן או עד שיפוג.',
      ur: 'یہ فہرست صرف آپ دیکھتے ہیں — گھر میں کوئی اور نہیں۔ ڈیوائس اس وقت تک سائن ان رہتی ہے جب تک آپ اسے یہاں ختم نہ کریں یا اس کی میعاد ختم نہ ہو۔',
      tl: 'Ikaw lang ang nakakakita ng listahang ito — walang iba sa bahay. Nananatiling naka-sign in ang device hanggang sa tapusin mo ito dito o mag-expire ito.',
    },
  'Nothing signed in right now.': {
    he: 'שום דבר לא מחובר כרגע.',
    ur: 'اس وقت کچھ بھی سائن ان نہیں۔',
    tl: 'Walang naka-sign in ngayon.',
  },
  'This device': { he: 'המכשיר הזה', ur: 'یہ ڈیوائس', tl: 'Ang device na ito' },
  'In use': { he: 'בשימוש', ur: 'زیر استعمال', tl: 'Ginagamit' },
  'Unknown device': {
    he: 'מכשיר לא מזוהה',
    ur: 'نامعلوم ڈیوائس',
    tl: 'Hindi kilalang device',
  },
  'Sign out everywhere': {
    he: 'התנתקות מכל המכשירים',
    ur: 'ہر جگہ سے سائن آؤٹ',
    tl: 'Mag-sign out sa lahat',
  },
  'Sign out this device?': {
    he: 'לנתק את המכשיר הזה?',
    ur: 'یہ ڈیوائس سائن آؤٹ کریں؟',
    tl: 'I-sign out ang device na ito?',
  },
  'It will need your password again to get back in.': {
    he: 'הוא יצטרך את הסיסמה שלכם כדי להיכנס שוב.',
    ur: 'واپس داخل ہونے کے لیے اسے دوبارہ آپ کا پاس ورڈ درکار ہوگا۔',
    tl: 'Kakailanganin nito ulit ang password mo para makapasok.',
  },
  'Sign out everywhere?': {
    he: 'להתנתק מכל המכשירים?',
    ur: 'ہر جگہ سے سائن آؤٹ کریں؟',
    tl: 'Mag-sign out sa lahat?',
  },
  'Every phone, tablet and computer signed in to your account is signed out, this one included. Use this if a device was lost or stolen.':
    {
      he: 'כל טלפון, טאבלט ומחשב שמחוברים לחשבון שלכם ינותקו, כולל זה. השתמשו בזה אם מכשיר אבד או נגנב.',
      ur: 'آپ کے اکاؤنٹ میں سائن ان ہر فون، ٹیبلٹ اور کمپیوٹر سائن آؤٹ ہو جائے گا، یہ بھی شامل۔ اگر کوئی ڈیوائس گم یا چوری ہو گئی ہو تو یہ استعمال کریں۔',
      tl: 'Masi-sign out ang bawat telepono, tablet at kompyuter na naka-sign in sa account mo, pati na ito. Gamitin ito kung nawala o nanakaw ang isang device.',
    },
  'That device is signed out.': {
    he: 'המכשיר נותק.',
    ur: 'وہ ڈیوائس سائن آؤٹ ہو گئی۔',
    tl: 'Na-sign out na ang device na iyon.',
  },
  'Two-factor authentication': {
    he: 'אימות דו-שלבי',
    ur: 'دو مرحلوں کی توثیق',
    tl: 'Two-factor authentication',
  },
  "A six-digit code from an app on your phone, on top of your password. This is the account that can see everyone's pay and delete the household, so it is worth the extra step.":
    {
      he: 'קוד בן שש ספרות מאפליקציה בטלפון, בנוסף לסיסמה. זה החשבון שרואה את המשכורות של כולם ויכול למחוק את הבית, אז השלב הנוסף שווה את זה.',
      ur: 'آپ کے پاس ورڈ کے علاوہ فون کی ایپ سے چھ ہندسوں کا کوڈ۔ یہ وہ اکاؤنٹ ہے جو سب کی تنخواہ دیکھ سکتا ہے اور گھرانہ حذف کر سکتا ہے، اس لیے یہ اضافی قدم فائدہ مند ہے۔',
      tl: 'Isang anim na digit na code mula sa app sa telepono mo, bukod pa sa password. Ito ang account na nakakakita ng sahod ng lahat at kayang burahin ang sambahayan, kaya sulit ang dagdag na hakbang.',
    },
  'Turn on two-factor': {
    he: 'הפעילו אימות דו-שלבי',
    ur: 'دو مرحلوں کی توثیق آن کریں',
    tl: 'I-on ang two-factor',
  },
  'Turn it off instead': {
    he: 'לכבות אותו במקום',
    ur: 'اس کے بجائے اسے بند کریں',
    tl: 'I-off na lang ito',
  },
  'Two-factor is already on for this account.': {
    he: 'האימות הדו-שלבי כבר פעיל בחשבון הזה.',
    ur: 'اس اکاؤنٹ پر دو مرحلوں کی توثیق پہلے سے آن ہے۔',
    tl: 'Naka-on na ang two-factor sa account na ito.',
  },
  'Turn it off': { he: 'כבו אותו', ur: 'اسے بند کریں', tl: 'I-off ito' },
  '1. Open your authenticator app and add an account.': {
    he: '1. פתחו את אפליקציית האימות והוסיפו חשבון.',
    ur: '1. اپنی توثیق ایپ کھولیں اور اکاؤنٹ شامل کریں۔',
    tl: '1. Buksan ang authenticator app mo at magdagdag ng account.',
  },
  '2. Type in the key below, or tap it to open the app for you.': {
    he: '2. הקלידו את המפתח שלמטה, או הקישו עליו כדי לפתוח את האפליקציה.',
    ur: '2. نیچے دی گئی کلید ٹائپ کریں، یا ایپ کھولنے کے لیے اس پر ٹیپ کریں۔',
    tl: '2. I-type ang key sa ibaba, o i-tap ito para buksan ang app.',
  },
  '3. Enter the six digits it shows, to prove it worked.': {
    he: '3. הזינו את שש הספרות שהיא מציגה, כדי לוודא שזה עבד.',
    ur: '3. جو چھ ہندسے دکھائے وہ درج کریں، تاکہ ثابت ہو کہ یہ کام کر گیا۔',
    tl: '3. Ilagay ang anim na digit na ipinapakita nito, para patunayang gumana.',
  },
  'Six-digit code': {
    he: 'קוד בן שש ספרות',
    ur: 'چھ ہندسوں کا کوڈ',
    tl: 'Anim na digit na code',
  },
  Confirm: { he: 'אישור', ur: 'تصدیق کریں', tl: 'Kumpirmahin' },
  'Two-factor is on.': {
    he: 'האימות הדו-שלבי פעיל.',
    ur: 'دو مرحلوں کی توثیق آن ہے۔',
    tl: 'Naka-on na ang two-factor.',
  },
  'Write these down now and keep them somewhere safe. They are shown once and never again — each one gets you in if you lose your phone.':
    {
      he: 'רשמו אותם עכשיו ושמרו במקום בטוח. הם מוצגים פעם אחת בלבד — כל אחד מהם יכניס אתכם אם תאבדו את הטלפון.',
      ur: 'انہیں ابھی لکھ لیں اور محفوظ جگہ رکھیں۔ یہ صرف ایک بار دکھائے جاتے ہیں — فون گم ہونے پر ان میں سے ہر ایک آپ کو داخل کرا دے گا۔',
      tl: 'Isulat mo na ang mga ito ngayon at itago sa ligtas na lugar. Isang beses lang ipinapakita ang mga ito — bawat isa ay makakapagpapasok sa iyo kung mawala ang telepono mo.',
    },
  'I have written them down': {
    he: 'רשמתי אותם',
    ur: 'میں نے انہیں لکھ لیا ہے',
    tl: 'Naisulat ko na sila',
  },
  'Turning it off needs a live code from your authenticator, or one of your recovery codes.': {
    he: 'כדי לכבות צריך קוד חי מאפליקציית האימות, או אחד מקודי השחזור.',
    ur: 'اسے بند کرنے کے لیے آپ کی توثیق ایپ کا موجودہ کوڈ، یا کوئی ایک ریکوری کوڈ درکار ہے۔',
    tl: 'Para i-off ito, kailangan ng bagong code mula sa authenticator mo, o isa sa mga recovery code mo.',
  },
  'Code or recovery code': {
    he: 'קוד או קוד שחזור',
    ur: 'کوڈ یا ریکوری کوڈ',
    tl: 'Code o recovery code',
  },
  'Two-factor authentication is off.': {
    he: 'האימות הדו-שלבי כבוי.',
    ur: 'دو مرحلوں کی توثیق بند ہے۔',
    tl: 'Naka-off na ang two-factor authentication.',
  },

  // ── The owner closing a task for a worker who never opened the app.
  // Deliberately NOT the word "approve": nothing was submitted to approve,
  // and the owner is the one making the claim, so the copy has to say that
  // it is her word going into the record. ──
  'Record as done': { he: 'רשום כבוצע', ur: 'مکمل کے طور پر درج کریں', tl: 'Itala bilang tapos' },
  'Nobody marked this done in the app. It closes as done on your word, and the history will show that you recorded it.':
    {
      he: 'אף אחד לא סימן את המשימה כבוצעה באפליקציה. היא נסגרת כבוצעה על סמך דבריך, ובהיסטוריה יופיע שאתה רשמת אותה.',
      ur: 'ایپ میں کسی نے اسے مکمل نشان زد نہیں کیا۔ یہ آپ کے کہنے پر مکمل کے طور پر بند ہو رہا ہے، اور تاریخ میں درج ہوگا کہ آپ نے اسے ریکارڈ کیا۔',
      tl: 'Walang nagmarka nito bilang tapos sa app. Isasara ito bilang tapos base sa salita mo, at ipapakita ng history na ikaw ang nagtala nito.',
    },
  'Nobody marked this done in the app, and the proof it asked for never arrived. It closes as done on your word, and the history will show you closed it without the proof.':
    {
      he: 'אף אחד לא סימן את המשימה כבוצעה באפליקציה, וההוכחה שנדרשה מעולם לא הגיעה. היא נסגרת כבוצעה על סמך דבריך, ובהיסטוריה יופיע שסגרת אותה בלי ההוכחה.',
      ur: 'ایپ میں کسی نے اسے مکمل نشان زد نہیں کیا، اور جو ثبوت مانگا گیا تھا وہ کبھی نہیں آیا۔ یہ آپ کے کہنے پر مکمل کے طور پر بند ہو رہا ہے، اور تاریخ میں درج ہوگا کہ آپ نے اسے ثبوت کے بغیر بند کیا۔',
      tl: 'Walang nagmarka nito bilang tapos sa app, at hindi dumating ang patunay na hiningi. Isasara ito bilang tapos base sa salita mo, at ipapakita ng history na isinara mo ito nang walang patunay.',
    },

  // Acting on a row without opening it — the swipe tray and the ⋯ sheet that
  // every task list now carries (SwipeRow, web and mobile). The ⋯ button is
  // the only name a screen reader ever hears for these actions, so it says
  // WHICH row it belongs to; a list of forty identical "Actions" buttons is
  // no better than no buttons at all.
  'Actions for {{name}}': {
    he: 'פעולות עבור {{name}}',
    ur: '{{name}} کے لیے کارروائیاں',
    tl: 'Mga aksyon para sa {{name}}',
  },
  'Cancel this task?': {
    he: 'לבטל את המשימה?',
    ur: 'یہ کام منسوخ کریں؟',
    tl: 'Kanselahin ang gawaing ito?',
  },
  'It stops here, and the record will say nobody did it.': {
    he: 'היא נעצרת כאן, וברישום ייכתב שאיש לא ביצע אותה.',
    ur: 'یہ یہیں رک جاتا ہے، اور ریکارڈ میں لکھا جائے گا کہ کسی نے یہ نہیں کیا۔',
    tl: 'Dito ito hihinto, at sasabihin ng talaan na walang gumawa nito.',
  },
  'Archive this task?': {
    he: 'להעביר את המשימה לארכיון?',
    ur: 'یہ کام محفوظ خانے میں بھیجیں؟',
    tl: 'Iarkibo ang gawaing ito?',
  },
  'It leaves the list. What happened to it stays on the record.': {
    he: 'היא יורדת מהרשימה. מה שקרה בה נשאר ברישום.',
    ur: 'یہ فہرست سے نکل جائے گا۔ اس کے ساتھ جو ہوا وہ ریکارڈ میں رہے گا۔',
    tl: 'Aalis ito sa listahan. Mananatili sa talaan ang nangyari rito.',
  },

  // The row status strip, the conflict banner, the teaching line, the inline
  // card editor and Clear draft — the operational-UX block. Web and mobile use
  // the identical source text, so one entry covers both boards. Five strings
  // the request listed as new were already here (Review, "You are not allowed
  // to do that.", Edit, Normal, "Saving…") and are reused rather than
  // duplicated: a duplicate key is a lint error in this file.
  Retry: { he: 'נסה שוב', ur: 'دوبارہ کوشش کریں', tl: 'Subukan ulit' },
  Reload: { he: 'רענן', ur: 'دوبارہ لوڈ کریں', tl: 'I-reload' },
  'Task changed elsewhere.': {
    he: 'המשימה השתנתה במקום אחר.',
    ur: 'کام کہیں اور تبدیل ہو گیا۔',
    tl: 'Nagbago ang gawain sa ibang lugar.',
  },
  'Waiting for signal': { he: 'ממתין לקליטה', ur: 'سگنل کا انتظار', tl: 'Naghihintay ng signal' },
  'This task is no longer available.': {
    he: 'המשימה כבר לא קיימת.',
    ur: 'یہ کام اب دستیاب نہیں ہے۔',
    tl: 'Wala na ang gawaing ito.',
  },
  'Clear draft': { he: 'נקה טיוטה', ur: 'مسودہ صاف کریں', tl: 'Burahin ang draft' },
  'Edit task': { he: 'עריכת משימה', ur: 'کام میں ترمیم', tl: 'I-edit ang gawain' },
  'Edit title': { he: 'עריכת כותרת', ur: 'عنوان میں ترمیم', tl: 'I-edit ang pamagat' },
  Where: { he: 'איפה', ur: 'کہاں', tl: 'Saan' },
  'Open full details': {
    he: 'לפרטים המלאים',
    ur: 'مکمل تفصیلات کھولیں',
    tl: 'Buksan ang buong detalye',
  },
  'Changes save themselves.': {
    he: 'השינויים נשמרים לבד.',
    ur: 'تبدیلیاں خود محفوظ ہو جاتی ہیں۔',
    tl: 'Kusang nase-save ang mga pagbabago.',
  },
  'Drag a row all the way to run its main action.': {
    he: 'גרור שורה עד הסוף כדי להפעיל את הפעולה הראשית שלה.',
    ur: 'قطار کو پورا کھینچیں تاکہ اس کا بنیادی عمل چلے۔',
    tl: 'Hilahin ang hanay hanggang dulo para patakbuhin ang pangunahing aksyon nito.',
  },
  'Nothing was sent just now. The same action is in the row’s menu, always.': {
    he: 'שום דבר לא נשלח כרגע. אותה פעולה נמצאת תמיד בתפריט של השורה.',
    ur: 'ابھی کچھ نہیں بھیجا گیا۔ وہی عمل ہمیشہ قطار کے مینو میں ہے۔',
    tl: 'Walang naipadala ngayon. Nasa menu ng hanay ang parehong aksyon, palagi.',
  },
  'Show shortcuts again': {
    he: 'הצג שוב את הקיצורים',
    ur: 'شارٹ کٹ دوبارہ دکھائیں',
    tl: 'Ipakita ulit ang mga shortcut',
  },
  Plans: { he: 'מסלולים', ur: 'پلانز', tl: 'Mga plano' },
  Workers: { he: 'עובדים', ur: 'ملازمین', tl: 'Mga manggagawa' },
  from: { he: 'החל מ', ur: 'سے', tl: 'mula' },
  'Priced by the size of your household.': {
    he: 'המחיר נקבע לפי גודל משק הבית שלך.',
    ur: 'قیمت آپ کے گھرانے کے حجم کے مطابق۔',
    tl: 'Presyong batay sa laki ng sambahayan mo.',
  },
  'Most households': { he: 'רוב משקי הבית', ur: 'زیادہ تر گھرانے', tl: 'Karamihan ng sambahayan' },
  'per month, USD': { he: 'לחודש, דולר', ur: 'فی ماہ، ڈالر', tl: 'kada buwan, USD' },
  forever: { he: 'לתמיד', ur: 'ہمیشہ کے لیے', tl: 'habambuhay' },
  'at base': { he: 'בבסיס', ur: 'بنیادی پیکج میں', tl: 'sa base' },
  Storage: { he: 'אחסון', ur: 'اسٹوریج', tl: 'Storage' },
  'Simple household': { he: 'משק בית פשוט', ur: 'سادہ گھرانہ', tl: 'Simpleng sambahayan' },
  'Small family, one domestic worker': {
    he: 'משפחה קטנה, עובד בית אחד',
    ur: 'چھوٹا خاندان، ایک گھریلو ملازم',
    tl: 'Maliit na pamilya, isang kasambahay',
  },
  'Established household, small staff': {
    he: 'משק בית מבוסס, צוות קטן',
    ur: 'قائم شدہ گھرانہ، چھوٹا عملہ',
    tl: 'Establisadong sambahayan, maliit na staff',
  },
  'Larger staffed household': {
    he: 'משק בית גדול עם צוות',
    ur: 'بڑا گھرانہ جس میں عملہ ہو',
    tl: 'Mas malaking sambahayang may staff',
  },
  'Complex or multi-property': {
    he: 'מורכב או כמה נכסים',
    ur: 'پیچیدہ یا کئی جائیدادیں',
    tl: 'Komplikado o maraming ari-arian',
  },
  'Manual task management, in English, forever free.': {
    he: 'ניהול משימות ידני, באנגלית, חינם לתמיד.',
    ur: 'دستی کام کا انتظام، انگریزی میں، ہمیشہ مفت۔',
    tl: 'Manwal na pamamahala ng gawain, sa Ingles, libre habambuhay.',
  },
  'No AI actions': { he: 'בלי פעולות AI', ur: 'اے آئی کارروائیاں نہیں', tl: 'Walang AI actions' },
  'No automatic translation': {
    he: 'בלי תרגום אוטומטי',
    ur: 'خودکار ترجمہ نہیں',
    tl: 'Walang awtomatikong pagsasalin',
  },
  'No receipt scanning': {
    he: 'בלי סריקת קבלות',
    ur: 'رسید اسکین نہیں',
    tl: 'Walang pag-scan ng resibo',
  },
  'No image uploads, so no photo proof': {
    he: 'בלי העלאת תמונות, ולכן בלי הוכחה בתמונה',
    ur: 'تصویریں اپ لوڈ نہیں، اس لیے تصویری ثبوت بھی نہیں',
    tl: 'Walang pag-upload ng larawan, kaya walang patunay na litrato',
  },
  'No voice messages': {
    he: 'בלי הודעות קוליות',
    ur: 'صوتی پیغامات نہیں',
    tl: 'Walang voice message',
  },
  'No WhatsApp': { he: 'בלי וואטסאפ', ur: 'واٹس ایپ نہیں', tl: 'Walang WhatsApp' },
  'AI actions': { he: 'פעולות AI', ur: 'اے آئی کارروائیاں', tl: 'AI actions' },
  'Automatic translations': {
    he: 'תרגומים אוטומטיים',
    ur: 'خودکار ترجمے',
    tl: 'Awtomatikong pagsasalin',
  },
  'Receipt scans': { he: 'סריקות קבלות', ur: 'رسید اسکین', tl: 'Mga scan ng resibo' },
  'Voice minutes': { he: 'דקות קוליות', ur: 'صوتی منٹ', tl: 'Mga minuto ng boses' },
  'WhatsApp rescues': {
    he: 'תזכורות וואטסאפ',
    ur: 'واٹس ایپ یاد دہانیاں',
    tl: 'Mga paalala sa WhatsApp',
  },
  'Per month. Everything in the list below is included and unmetered.': {
    he: 'לחודש. כל מה שברשימה למטה כלול ובלי מדידה.',
    ur: 'فی ماہ۔ نیچے دی گئی فہرست کی ہر چیز شامل ہے اور اس کی گنتی نہیں ہوتی۔',
    tl: 'Kada buwan. Kasama ang lahat sa listahan sa ibaba at hindi binibilang.',
  },
  'Start free': { he: 'להתחיל בחינם', ur: 'مفت شروع کریں', tl: 'Magsimula nang libre' },
  'Start with this plan': {
    he: 'להתחיל במסלול הזה',
    ur: 'اس پلان سے شروع کریں',
    tl: 'Simulan ang planong ito',
  },
  'Request a quote': {
    he: 'לבקש הצעת מחיר',
    ur: 'قیمت کا تخمینہ منگوائیں',
    tl: 'Humiling ng quote',
  },
  'Included in every paid plan': {
    he: 'כלול בכל מסלול בתשלום',
    ur: 'ہر ادائیگی والے پلان میں شامل',
    tl: 'Kasama sa bawat bayad na plano',
  },
  'Unlimited tasks, routines and recurring work': {
    he: 'משימות, שגרות ועבודה חוזרת ללא הגבלה',
    ur: 'لامحدود کام، روٹین اور بار بار ہونے والا کام',
    tl: 'Walang limitasyong gawain, routine at paulit-ulit na trabaho',
  },
  'Unlimited staff accounts up to your plan’s worker seats': {
    he: 'חשבונות צוות ללא הגבלה, עד מספר העובדים במסלול שלך',
    ur: 'آپ کے پلان کی ملازم نشستوں تک لامحدود عملہ اکاؤنٹس',
    tl: 'Walang limitasyong staff account hanggang sa bilang ng manggagawa sa plano mo',
  },
  'Owner approvals and proof-of-work sign-off': {
    he: 'אישורים של בעל הבית וחתימה על הוכחת ביצוע',
    ur: 'مالک کی منظوریاں اور کام کے ثبوت کی تصدیق',
    tl: 'Mga approval ng may-ari at pag-sign-off sa patunay ng trabaho',
  },
  'Payroll, payslips and end-of-service handover': {
    he: 'שכר, תלושים והעברת תפקיד בסיום העסקה',
    ur: 'تنخواہ، پے سلپ اور ملازمت کے اختتام پر حوالگی',
    tl: 'Payroll, payslip at turnover sa pagtatapos ng serbisyo',
  },
  'Expenses, budgets and recurring household bills': {
    he: 'הוצאות, תקציבים וחשבונות קבועים של הבית',
    ur: 'اخراجات، بجٹ اور گھر کے باقاعدہ بل',
    tl: 'Gastos, badyet at paulit-ulit na bayarin sa bahay',
  },
  'Household memory — every appliance, supplier and decision, kept': {
    he: 'זיכרון הבית — כל מכשיר, כל ספק וכל החלטה, נשמרים',
    ur: 'گھر کی یادداشت — ہر آلہ، ہر سپلائر اور ہر فیصلہ محفوظ',
    tl: 'Memorya ng bahay — bawat kagamitan, supplier at desisyon, nakatago',
  },
  'Shift planning, task chains and prepared task packs': {
    he: 'תכנון משמרות, שרשראות משימות וחבילות משימות מוכנות',
    ur: 'شفٹ پلاننگ، کاموں کی زنجیریں اور تیار شدہ ٹاسک پیک',
    tl: 'Pagpaplano ng shift, task chain at handang task pack',
  },
  'Full audit trail and household timeline': {
    he: 'תיעוד מלא וציר זמן של הבית',
    ur: 'مکمل آڈٹ ریکارڈ اور گھر کی ٹائم لائن',
    tl: 'Buong audit trail at timeline ng sambahayan',
  },
  'Works offline, syncs when the house is back online': {
    he: 'עובד גם בלי אינטרנט, ומסתנכרן כשהבית חוזר לרשת',
    ur: 'آف لائن بھی کام کرتا ہے، گھر دوبارہ آن لائن ہوتے ہی ہم آہنگ ہو جاتا ہے',
    tl: 'Gumagana offline, nagsi-sync kapag bumalik ang bahay online',
  },
  'Owner and staff apps, iOS, Android and web': {
    he: 'אפליקציה לבעל הבית ולצוות, ל-iOS, אנדרואיד ובדפדפן',
    ur: 'مالک اور عملے کی ایپس، iOS، اینڈرائیڈ اور ویب',
    tl: 'Mga app para sa may-ari at staff, iOS, Android at web',
  },
  'Places in the first cohort': {
    he: 'מקומות במחזור הראשון',
    ur: 'پہلے گروپ میں جگہیں',
    tl: 'Mga puwesto sa unang cohort',
  },
  'households onboarded personally, by the founder': {
    he: 'משקי בית שהמייסד מקים אישית',
    ur: 'گھرانے جنہیں بانی خود ذاتی طور پر شامل کرتا ہے',
    tl: 'mga sambahayang personal na ino-onboard ng founder',
  },
  'What does it cost?': { he: 'כמה זה עולה?', ur: 'اس کی قیمت کیا ہے؟', tl: 'Magkano ang halaga?' },
  'Free plan available · no card today': {
    he: 'יש מסלול חינם · בלי כרטיס היום',
    ur: 'مفت پلان دستیاب · آج کارڈ نہیں',
    tl: 'May libreng plano · walang card ngayon',
  },
  'In testing': { he: 'בבדיקה', ur: 'زیرِ آزمائش', tl: 'Sinusubukan' },
  'New and not yet proven. Turn one on to try it, and off again to stop it — nothing here is permanent.':
    {
      he: 'חדש ועדיין לא מוכח. אפשר להדליק כדי לנסות ולכבות כדי להפסיק — שום דבר כאן אינו קבוע.',
      ur: 'نیا اور ابھی ثابت نہیں۔ آزمانے کے لیے آن کریں اور روکنے کے لیے آف — یہاں کچھ بھی مستقل نہیں۔',
      tl: 'Bago at hindi pa napatunayan. I-on para subukan, i-off para itigil — walang permanente rito.',
    },
};

export function t(key: string): string {
  const lang = getLang();
  if (lang === 'en') return key;
  return T[key]?.[lang] ?? OVERLAYS[lang]?.[key] ?? key;
}

/**
 * Notification titles from the server are one of two shapes: a short fixed
 * string (translated via `t` like anything else), or a fixed template
 * carrying `{{name}}`/`{{hours}}`/`{{kind}}`/`{{label}}` slots for the parts
 * that can't live in a dictionary — a person's name, an hour count, a
 * household-chosen payroll run label. Filled in from `payload` after
 * translation, the same `.replace('{{name}}', …)` pattern already used for
 * `{{name}}` strings throughout this file.
 */
export function notificationTitle(n: {
  title: string;
  payload?: Record<string, unknown> | null;
}): string {
  const translated = t(n.title);
  if (!translated.includes('{{')) return translated;
  const payload = n.payload ?? {};
  const name = typeof payload.staff_name === 'string' ? payload.staff_name : '';
  const hours = typeof payload.hours === 'string' ? payload.hours : '';
  const label = typeof payload.run_label === 'string' ? payload.run_label : '';
  const kind =
    payload.kind === 'overtime'
      ? getLang() === 'en'
        ? 'overtime'
        : t('Overtime')
      : payload.kind === 'holiday'
        ? getLang() === 'en'
          ? 'holiday'
          : t('Holiday')
        : typeof payload.kind === 'string'
          ? payload.kind
          : '';
  return translated
    .replace('{{name}}', name)
    .replace('{{hours}}', hours)
    .replace('{{kind}}', kind)
    .replace('{{label}}', label);
}

// Format helpers that respect the active language.
/**
 * The Intl locale for the active language — never the browser/OS locale.
 * "No browser-language sniffing — a Hebrew-locale browser still gets
 * English by default" is this file's own stated design; any call site
 * that passes `undefined` to `toLocaleDateString`/`toLocaleString` instead
 * of this quietly breaks that promise the moment the viewer's device
 * locale differs from their account language.
 */
export function dateLocale(): string {
  const lang = getLang();
  return lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const locale = dateLocale();
  try {
    return new Date(iso).toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

// On boot, re-apply the platform's lang/dir effect for the hydrated
// language. The web version wrote <html lang/dir>; here the injected
// onLangChange owns the effect (RN: I18nManager + reload, ADR-0001 §12).
export function bootstrapLang(): void {
  const l = getLang();
  config?.onLangChange?.(l, isRtl(l) ? 'rtl' : 'ltr');
}

/**
 * Pick the viewer's preferred translation from a per-language map, falling
 * back to the canonical source string when the slot is missing or empty.
 *
 * Used for owner-typed content (operation title / description) where the
 * server-side Haiku translator populates op.titleTranslations / op.descriptionTranslations
 * fire-and-forget on create. During the ~1-2s window between create and the
 * translator finishing, the slot may be empty for non-source langs — this
 * helper returns the source string so the staff member sees *something*
 * meaningful instead of a blank label. After the next refresh, the slot is
 * populated and they see their preferred lang.
 */
export function pickTranslation(
  translations: Record<string, string | null | undefined> | null | undefined,
  fallback: string,
  lang: Lang = getLang(),
): string {
  if (!translations) return fallback;
  const slot = translations[lang];
  if (slot && slot.trim().length > 0) return slot;
  return fallback;
}
